/**
 * AR Billing — Billing Run tool (ported from examples/billing_run.html).
 *
 * Faithful 1-to-1 port of the Softr custom-code page. Render/checklist logic is preserved; only the
 * seams changed: all Airtable access goes through services/billing.js (no PAT in the browser), and
 * every BQE Core call goes through the Clerk-gated api/bqe-* endpoints, which mint the access token
 * SERVER-SIDE — the entire client-side OAuth refresh (and the hardcoded client secret) is gone.
 * All DOM is scoped to `root` so React unmount tears it down.
 *
 * Percent complete is read-only here: BQE Core's project-update API is unreliable for writing it
 * (PATCH null-references, PUT re-validates the contract and fails), so the tool shows Core's % for
 * comparison and the bookkeeper updates % directly in Core, then hits "Refresh Core" to re-pull.
 */

import * as B from '../services/billing.js'
import { savePhasePercentComplete } from '../services/phases.js'
import { searchContacts } from '../services/contacts.js'
import { alertDialog, promptDialog, confirmDialog } from '../components/Dialog.jsx'
import { hasModule } from '../services/tenancy.js'
import { startDiscussion, replyToThread } from '../services/slack.js'

const CSS = `
#aj-br *{box-sizing:border-box}
#aj-br,.aj-modal-overlay,.arb-core-load{
  --aj:#07272D;--aj-mid:#0a3540;--bg:#f4f7f8;--surf:#edf2f3;--wh:#ffffff;--wh2:#f8fafb;
  --t1:#07272D;--t2:#3a5a60;--t3:#6b8f95;--t4:#9ab8bd;
  --b1:rgba(7,39,45,0.08);--b2:rgba(7,39,45,0.14);--b3:rgba(7,39,45,0.22);
  --red:#C0392B;--red-bg:#fdf0ef;--amb:#B7651A;--amb-bg:#fdf4e7;--grn:#1a7a4a;--grn-bg:#eaf4ee;
  --blu:#1a4f8a;--blu-bg:#edf3fb;--r4:4px;--r8:8px;--r10:10px}
/* Dark mode: remap the local palette (values mirror the global --aj-* tokens in outline.css).
   .arb-core-load is appended as a sibling of #aj-br, so it carries the tokens too. Brand-dark
   bands (--aj), solid brand/semantic action buttons, and danger red stay constant. --wh2 is the
   subtle active-row surface (between --wh and --bg). */
body.dark #aj-br,body.dark .aj-modal-overlay,body.dark .arb-core-load{
  --bg:#0d1e24;--surf:#1c3840;--wh:#1b3a44;--wh2:#214049;
  --t1:#cfe2e8;--t2:#8ab0bc;--t3:#5c8494;--t4:#4a6d7c;
  --b1:rgba(255,255,255,0.07);--b2:rgba(255,255,255,0.10);--b3:rgba(255,255,255,0.16);
  --red-bg:rgba(192,57,43,0.16);--amb:#d08a3e;--amb-bg:rgba(183,101,26,0.16);
  --grn:#3fae7e;--grn-bg:rgba(26,122,74,0.16);--blu:#5b96d6;--blu-bg:rgba(26,79,138,0.18)}
/* The modal overlays are appended to the mount root as a SIBLING of #aj-br, so the design tokens
   declared on #aj-br don't cascade into them. Without the tokens above, aj-btn-p reads
   background:var(--aj) as invalid → a white-text button on no background (invisible). The shared
   declaration block fixes every modal (the "Confirm progress current" button was vanishing here). */
#aj-br{font-family:'DM Sans',sans-serif;font-size:13px;color:var(--t1);padding:1.25rem;max-width:1200px;margin:0 auto}
.aj-run-header{background:var(--aj);border-radius:var(--r10);padding:0.875rem 1.25rem;margin-bottom:1.25rem;display:flex;align-items:center;gap:1rem;flex-wrap:wrap}
.aj-run-title{font-family:'Montserrat',sans-serif;font-weight:300;font-size:14px;letter-spacing:0.1em;text-transform:uppercase;color:white}
.aj-run-meta{font-family:'DM Mono',monospace;font-size:10px;color:rgba(255,255,255,0.4);margin-top:2px}
.aj-month-nav{display:flex;align-items:center;gap:6px;margin-left:auto}
.aj-nav-btn{background:rgba(255,255,255,0.08);border:0.5px solid rgba(255,255,255,0.15);color:rgba(255,255,255,0.6);border-radius:var(--r4);padding:3px 8px;cursor:pointer;font-size:12px;font-family:'DM Sans',sans-serif}
.aj-nav-btn:hover{background:rgba(255,255,255,0.15)}
.aj-nav-label{font-family:'DM Mono',monospace;font-size:11px;color:rgba(255,255,255,0.7);min-width:100px;text-align:center}
.aj-two{display:grid;grid-template-columns:1fr 400px;gap:1rem;align-items:start}
.aj-panel-right{position:sticky;top:1rem;max-height:calc(100vh - 120px);overflow-y:auto}
.aj-btn{font-size:11px;padding:6px 13px;border-radius:var(--r4);border:0.5px solid var(--b3);background:transparent;color:var(--t1);cursor:pointer;font-family:'DM Sans',sans-serif;white-space:nowrap;transition:background 0.12s}
.aj-btn:hover{background:var(--surf)}
.aj-btn-p{background:var(--aj);border-color:var(--aj);color:white}
.aj-btn-p:hover{background:var(--aj-mid)}
.aj-btn-warn{border-color:var(--amb);color:var(--amb)}
.aj-btn-warn:hover{background:var(--amb-bg)}
.aj-btn-sm{font-size:10px;padding:4px 10px}
.aj-btn-full{width:100%}
.aj-eyebrow{font-family:'Montserrat',sans-serif;font-weight:500;font-size:9px;letter-spacing:0.1em;text-transform:uppercase;color:var(--t3);margin-bottom:8px}
.aj-card{background:var(--wh);border:0.5px solid var(--b1);border-radius:var(--r10);overflow:hidden;margin-bottom:1rem}
.aj-pill{font-size:10px;font-weight:500;padding:2px 8px;border-radius:20px;white-space:nowrap;display:inline-block}
.p-grn{background:var(--grn-bg);color:var(--grn)}
.p-amb{background:var(--amb-bg);color:var(--amb)}
.p-red{background:var(--red-bg);color:var(--red)}
.p-blu{background:var(--blu-bg);color:var(--blu)}
.p-muted{background:var(--surf);color:var(--t3)}
.aj-list-row{display:flex;align-items:center;gap:8px;padding:0.625rem 0.875rem;border-bottom:0.5px solid var(--b1);cursor:pointer;transition:background 0.1s}
.aj-list-row:last-child{border-bottom:none}
.aj-list-row:hover{background:var(--surf)}
.aj-list-row.sel{background:var(--blu-bg);border-left:2px solid var(--blu)}
.aj-dot-row{display:flex;gap:3px}
.aj-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0}
.d-grn{background:var(--grn)}
.d-amb{background:var(--amb)}
.d-empty{background:var(--surf)}
.aj-prog-track{height:3px;background:var(--surf);border-radius:2px;overflow:hidden}
.aj-prog-fill{height:100%;border-radius:2px}
.aj-detail-head{background:var(--aj);padding:0.875rem 1rem}
.aj-detail-name{font-family:'Montserrat',sans-serif;font-weight:300;font-size:14px;letter-spacing:0.08em;text-transform:uppercase;color:white;line-height:1.2}
.aj-detail-sub{display:flex;gap:6px;margin-top:5px;align-items:center;flex-wrap:wrap}
.aj-dnum{font-family:'DM Mono',monospace;font-size:10px;color:rgba(255,255,255,0.4)}
.aj-wf-step{display:flex;gap:10px;padding:0.75rem 1rem;border-bottom:0.5px solid var(--b1);background:var(--wh)}
.aj-wf-step.wf-active{background:var(--wh2)}
.aj-wf-step.wf-dim{opacity:0.4;pointer-events:none}
.aj-wf-num{width:20px;height:20px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-family:'DM Mono',monospace;font-size:9px;font-weight:500;margin-top:1px}
.wfn-done{background:var(--grn-bg);color:var(--grn)}
.wfn-now{background:var(--aj);color:white}
.wfn-pend{background:var(--surf);color:var(--t4)}
.aj-wf-label{font-size:12px;font-weight:500;line-height:1.3}
.aj-wf-desc{font-size:11px;color:var(--t3);margin-top:2px;line-height:1.5}
.aj-wf-act{margin-top:8px}
.aj-sel{font-size:11px;padding:5px 8px;border-radius:var(--r4);border:0.5px solid var(--b3);background:var(--wh);color:var(--t1);width:100%;font-family:'DM Sans',sans-serif;cursor:pointer}
.aj-pct-box{background:var(--bg);border-radius:var(--r8);padding:8px 10px;margin-top:6px;font-size:11px}
.aj-pct-row{display:flex;justify-content:space-between;align-items:center;margin-bottom:5px}
.aj-info-strip{padding:0.5rem 1rem;background:var(--bg);border-top:0.5px solid var(--b1);display:flex;gap:16px;flex-wrap:wrap}
.aj-info-item{font-size:11px;color:var(--t3)}
.aj-info-item span{font-family:'DM Mono',monospace;font-weight:500;color:var(--t1);margin-left:3px}
.aj-empty{padding:2rem;text-align:center;color:var(--t3);font-size:12px}
.aj-loading{padding:2rem;text-align:center;color:var(--t3);font-size:12px}
.aj-mono{font-family:'DM Mono',monospace}
.aj-saving{font-size:10px;color:var(--t3);margin-left:8px;font-style:italic}
@media(max-width:780px){.aj-desktop-only{display:none !important}.aj-two{grid-template-columns:1fr}.aj-panel-right{display:none}.aj-panel-right.mobile-show{display:block}}
.aj-modal-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;z-index:9998}
.aj-modal{font-family:'DM Sans',sans-serif;color:var(--t1);background:var(--wh);border-radius:10px;width:520px;max-width:94vw;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,0.25);display:flex;flex-direction:column;max-height:90vh}
.aj-modal-head{background:#07272D;padding:0.875rem 1.1rem;display:flex;align-items:center;justify-content:space-between}
.aj-modal-title{font-family:'Montserrat',sans-serif;font-weight:300;font-size:12px;letter-spacing:0.08em;text-transform:uppercase;color:white}
.aj-modal-close{color:rgba(255,255,255,0.5);cursor:pointer;font-size:18px;line-height:1;background:none;border:none;padding:0 4px}
.aj-modal-body{padding:1rem 1.1rem}
.aj-modal-footer{padding:0.75rem 1.1rem;border-top:0.5px solid var(--b1);display:flex;gap:8px;justify-content:flex-end;align-items:center}
.aj-modal-status{font-size:11px;color:var(--t3);margin-right:auto}
.aj-dot-wrap{position:relative;display:inline-block;cursor:default}
#aj-dot-tooltip{position:fixed;background:#07272D;color:white;font-size:10px;padding:4px 8px;border-radius:4px;white-space:nowrap;pointer-events:none;z-index:9999;font-family:'DM Sans',sans-serif;display:none}
/* right:56px (not 18px) — clears the global notification bell (fixed, right:12px, 30px wide,
   z-index:500 — see NotificationBell.jsx), which otherwise sits directly on top of this pill's
   right edge and covers part of its text now that the bell renders above every workspace. */
.arb-core-load{position:fixed;top:11px;right:56px;z-index:50;display:flex;align-items:center;gap:7px;font-size:11px;color:var(--t2);font-family:'DM Sans',sans-serif;background:var(--wh);border:0.5px solid var(--b2);border-radius:20px;padding:5px 12px;box-shadow:0 1px 4px rgba(0,0,0,0.12);transition:opacity 0.4s}
.arb-spin{width:11px;height:11px;border:1.5px solid var(--surf);border-top-color:var(--t1);border-radius:50%;animation:arb-spin 0.7s linear infinite;display:inline-block;flex-shrink:0}
.arb-core-refresh{background:none;border:0.5px solid var(--b3);color:var(--t2);border-radius:12px;cursor:pointer;font-size:11px;line-height:1;padding:2px 7px;margin-left:2px;font-family:'DM Sans',sans-serif}
.arb-core-refresh:hover{background:var(--aj);color:#fff;border-color:var(--aj)}
@keyframes arb-spin{to{transform:rotate(360deg)}}
`

export function mountRun(root, opts) {
  opts = opts || {}
  const styleEl = document.createElement('style')
  styleEl.textContent = CSS
  root.appendChild(styleEl)
  root.insertAdjacentHTML('beforeend', '<div id="aj-br"><div class="aj-loading">Loading billing run...</div></div>')

  const byId = (id) => root.querySelector('#' + id)
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']
  // Debug-only "Data" button on the invoice list (Step 4) - hidden for now, not needed day-to-day.
  // Flip to true to bring it back; the action handler + B.openInvoiceData are untouched either way.
  const SHOW_INVOICE_DATA_DEBUG_BUTTON = false
  const state = { run: null, lineItems: [], selectedIdx: -1, monthOffset: 0, saving: false, loading: false, newBillingInsight: null, openBillingInsight: null, hiddenFilters: {}, searchQuery: '', renumberFollowups: [], renumberAutoShown: false, pendingReimbCount: null, openHoldsCount: null, followupStatus: {}, followupStatusLoaded: false, urlIdConsumed: false, approvedTotal: 0, approvedTotalLoaded: false, preflight: {}, preflightLoaded: false, preflightError: false }

  function getMonthYear(offset) {
    var now = new Date()
    var d = new Date(now.getFullYear(), now.getMonth() + offset, 1)
    return { month: MONTHS[d.getMonth()], year: String(d.getFullYear()) }
  }
  async function load() {
    byId('aj-br').innerHTML = '<div class="aj-loading">Loading billing run...</div>'
    // opts.runId is the ONLY legitimate source here — it's BillingWorkspace's own explicit
    // one-shot deep-link hint. A `recordId=` URL param was previously accepted as a fallback, but
    // `recordId` is this app's generic "current project" param (set/restored by the outline on
    // almost every page), never a billing-run id — a stray project recordId left in (or restored
    // into) the URL on a cold reload would get GET'd against the Billing Runs table, and Airtable
    // returns that record's fields regardless of which table id was queried, so a Project record
    // (with its own unrelated `Year` field, e.g. "26") would get silently treated as "the run,"
    // producing a mislabeled, empty-line-item run. mountRun is only ever called from
    // BillingWorkspace.jsx, which always passes runId explicitly, so there is no legitimate case
    // for a URL fallback at all.
    var urlId = opts.runId
    // Only ever consumed once, on the true initial mount — NOT re-checked via monthOffset===0,
    // which arrow navigation can legitimately return to after moving away (that previously caused
    // "→" back to the starting offset to silently re-load this fixed run under whatever month the
    // calendar-relative offset happened to compute, mislabeling it).
    if (urlId && !state.urlIdConsumed) {
      state.urlIdConsumed = true
      var rec = await B.getRunById(urlId)
      if (rec && rec.id) { state.run = rec; fetchLineItems(rec.id); return }
    }
    loadByDate()
  }

  async function loadByDate() {
    var my = getMonthYear(state.monthOffset)
    var run = await B.getRunForMonth(my.month, my.year)
    if (!run) { state.run = null; state.lineItems = []; render(); return }
    state.run = run
    fetchLineItems(run.id)
  }

  async function fetchLineItems(rid) {
    state.loading = true; state.loadError = false
    // Reset per-run follow-up state so switching months never shows stale data from the last one.
    state.followupStatus = {}; state.followupStatusLoaded = false; state.followupInvoiceFallback = {}
    state.approvedTotal = 0; state.approvedTotalLoaded = false
    state.paymentActivity = {}
    state.preflight = {}; state.preflightLoaded = false; state.preflightError = false
    render()
    try {
      var lis = await B.getLineItemsForRun(rid, { sortByName: true })
      state.lineItems = lis || []
    } catch (e) {
      // A failed load (e.g. Airtable rate-limit) must NOT look like an empty run — flag it so the
      // list shows a retry, not a misleading "0 projects" (dangerous in billing).
      state.loadError = true
    }
    state.loading = false
    render()
    loadRenumberFollowups()        // open Core-renumber follow-ups for the header button + checklist
    loadCloseoutContext()          // reimbursables pending + open billing holds, for the closeout panel
    loadPaymentFollowupStatus()    // per-invoice paid/unpaid + days-since-issue, for the follow-up steps
    loadApprovedNewBillingTotal()  // header summary: this run's approved new-billing $ (near the progress bar)
    loadPaymentActivity()          // what Stripe has seen each client do on their pay link
    loadPreflight()                // the system's new billing / open billing / aging answers, every project
  }

  /**
   * What has each client actually DONE on their pay page? One query for the whole run — affordable
   * only because the Stripe webhook records each step into our own table as it happens
   * (database/245_stripe_payment_activity.sql). Asking Stripe directly would be a call per project.
   *
   * Silent on failure, deliberately: a badge that fails to load should show nothing, never an
   * implied "no client has paid".
   */
  async function loadPaymentActivity() {
    try {
      var ids = (state.lineItems || []).map(function (it) { return (it.fields['Project'] || [])[0] }).filter(Boolean)
      if (!ids.length) return
      var resp = await B.getPaymentActivity(ids)
      if (!resp || resp.error || !resp.byProject) return
      state.paymentActivity = resp.byProject
      render()
    } catch (e) { /* the list is useful without it */ }
  }

  /**
   * The payment badge on a list row. Says STRIPE explicitly on the paid state, because Core is a
   * separate system and a payment collected by Stripe still has to be applied there by a person —
   * a bare "Paid" would read as "the books are done" and it is not what this knows.
   */
  var PAY_BADGE = {
    requires_action: { label: 'Client action needed', color: 'var(--amb)', title: 'The client started a bank payment but has not finished verifying their account. It expires after 10 days.' },
    failed: { label: 'Payment failed', color: 'var(--red)', title: 'Stripe could not collect this payment.' },
    processing: { label: 'Paying', color: 'var(--blu)', title: 'The client has paid and the money is moving. Nothing needed from them.' },
    paid: { label: 'Paid in Stripe', color: 'var(--grn)', title: 'Stripe collected this. It still needs applying in BQE Core.' },
  }
  function payActivityBadge(f) {
    var pid = (f['Project'] || [])[0]
    var a = pid && state.paymentActivity ? state.paymentActivity[pid] : null
    if (!a) return ''
    var b = PAY_BADGE[a.status]
    if (!b) return ''
    var rail = a.method === 'us_bank_account' ? ' (bank)' : a.method === 'card' ? ' (card)' : ''
    return '<span title="' + escHtml(b.title) + '" style="flex-shrink:0;font-size:9px;letter-spacing:0.04em;text-transform:uppercase;padding:1px 5px;border-radius:3px;border:0.5px solid ' + b.color + ';color:' + b.color + '">'
      + escHtml(b.label + rail) + '</span>'
  }

  // Precompute which posted line items still need a payment follow-up. Cheap: every lookup is an
  // in-memory match against the one firm-wide invoice cache (no per-item Core calls). Sets
  // followupStatusLoaded=true once done, even if some items came back unmatched — the closeout gate
  // treats "still loading" and "unmatched" differently (unmatched blocks; loading just waits).
  async function loadPaymentFollowupStatus() {
    var candidates = state.lineItems.filter(function (li) { return li.fields['Mark Posted'] && li.fields['Invoice Number'] })
    var results = await Promise.all(candidates.map(function (li) {
      return B.getInvoicePaymentStatus(li.fields['Invoice Number']).catch(function () { return { found: false } })
    }))
    candidates.forEach(function (li, i) { state.followupStatus[li.id] = results[i] })
    state.followupStatusLoaded = true
    render()
  }

  // Header summary near the progress bar: the sum of this run's APPROVED, NEW-billing invoice
  // amounts. "Approved" = Draft Review Disposition is 'Approved' — that field is set once and
  // never cleared by finalize/issue/post, so an item stays counted as it moves further along, not
  // just while it sits in the 'Approved' bucket specifically. "New billing only" is enforced two
  // ways: (1) the line item itself must have New Billing? = Yes (excludes pure statement-chase
  // items with no new charges this cycle), and (2) the dollar figure is always a fresh invoice's
  // own invoiceAmount, never its balance — so a prior-period carried balance (what a statement
  // bundles in) can never leak into this total. Interest/late-fee invoices are filtered out of the
  // candidates entirely, so they can never be picked as "the" invoice either.
  async function loadApprovedNewBillingTotal() {
    var approved = state.lineItems.filter(function (li) {
      return li.fields['Draft Review Disposition'] === 'Approved' && li.fields['New Billing?'] === 'Yes'
    })
    if (!approved.length) { state.approvedTotal = 0; state.approvedTotalLoaded = true; render(); return }
    var projectIds = []
    approved.forEach(function (li) {
      var pid = (li.fields['Project'] || [])[0]
      if (pid && projectIds.indexOf(pid) === -1) projectIds.push(pid)
    })
    try {
      var uidMap = await B.getProjectCoreUids(projectIds)
      var invoices = await B.getCoreInvoicesCached()
      var isLateFee = function (iv) { return iv.isLateFeeInvoice === true || /-INT\b/i.test(iv.invoiceNumber || '') }
      var total = 0
      approved.forEach(function (li) {
        var pid = (li.fields['Project'] || [])[0]
        var uid = pid ? uidMap[pid] : ''
        if (!uid) return
        // The relevant invoice for this line item is whichever non-void, non-late-fee Core invoice
        // for that project is newest — the one actively being drafted/approved this cycle. Drafts
        // are deliberately included (an 'Approved'-but-not-yet-finalized item's invoice is still a
        // Core draft at this point; excluding drafts would silently drop it from the total).
        var candidates = invoices.filter(function (iv) {
          if (iv.isVoid || isLateFee(iv)) return false
          return (iv.invoiceDetails || []).some(function (d) { return d.rootProjectId === uid })
        })
        candidates.sort(function (a, b) { return (new Date(b.date)) - (new Date(a.date)) })
        var newest = candidates[0]
        if (newest) total += Number(newest.invoiceAmount) || 0
      })
      state.approvedTotal = total
    } catch (e) {
      state.approvedTotal = 0
    }
    state.approvedTotalLoaded = true
    render()
  }

  // Targeted sibling of loadPaymentFollowupStatus() for a single item that just became a candidate
  // mid-session (posted just now, so it missed the run-load bulk pass) — same cheap in-memory
  // lookup, scoped to one item instead of re-running the whole list. Called right after "Mark
  // Posted" is saved true so the next step's "Checking with BQE Core…" placeholder actually
  // resolves instead of hanging until the user switches months and back.
  async function refreshFollowupStatusForIdx(idx) {
    var li = state.lineItems[idx]
    var invNum = li && li.fields['Invoice Number']
    if (!li || !invNum) return
    try { state.followupStatus[li.id] = await B.getInvoicePaymentStatus(invNum) }
    catch (e) { state.followupStatus[li.id] = { found: false } }
    if (state.selectedIdx === idx) {
      var panel = byId('aj-detail-panel')
      if (panel) panel.innerHTML = renderDetail(idx)
      updateListRow(idx)
    }
  }

  // Stamps `Mark Posted At` (a plain Airtable date field on Billing Run Line Items — added by hand,
  // same as Payment Manually Marked Unpaid etc., since the connected token has no schema-write scope)
  // the moment Mark Posted is confirmed true. Deliberately a SEPARATE, best-effort write from the
  // primary Mark Posted save above, not bundled into the same `fields` object — if this field doesn't
  // exist yet in Airtable, the whole PATCH would be rejected, and bundling it would break "Mark
  // Posted" itself until the field is added. On failure, followupDays() just falls back to the
  // Core-invoice-date signal for this item, same as any legacy pre-fix record — never blocks.
  async function stampMarkPostedAt(idx) {
    var item = state.lineItems[idx]
    if (!item || item.fields['Mark Posted At']) return
    var iso = new Date().toISOString()
    try {
      var updated = await B.updateLineItem(item.id, { 'Mark Posted At': iso })
      if (updated && !updated.error) {
        item.fields['Mark Posted At'] = iso
        if (state.selectedIdx === idx) { var p2 = byId('aj-detail-panel'); if (p2) p2.innerHTML = renderDetail(idx) }
      }
    } catch (e) {}
  }

  // The payment-followup reminder's pacing clock. Anchored to `Mark Posted At` — a plain Airtable
  // timestamp stamped the moment "Mark Posted" is confirmed true in Cortex (see saveField below) —
  // rather than Core's own invoice date. Matt's call: Core's invoice date can be much older than
  // when this item actually entered Cortex's own follow-up tracking (a statement bundles older open
  // invoices; even a single fresh invoice can lag between being drafted in Core and being marked
  // posted here), so anchoring to OUR OWN action is the one signal guaranteed to mean "this is when
  // we started the clock," for every line item, not just statement cycles. Falls back to the
  // invoice-date signal only for line items marked posted before `Mark Posted At` existed (fails
  // open, not stuck).
  function followupDays(li, fs) {
    var markedAt = li.fields['Mark Posted At']
    if (markedAt) return Math.max(0, Math.round((Date.now() - new Date(markedAt).getTime()) / 86400000))
    return (fs && fs.daysSinceIssue != null) ? fs.daysSinceIssue : null
  }

  // Explicit small state machine (not independent booleans) so the closeout gate can't be cleared
  // by "confirm checked" alone when the invoice is actually still unpaid — only a real, recorded
  // reminder send (or a genuinely resolved paid/unknown state) exits the blocking states.
  function computeFollowupState(li) {
    var f = li.fields
    if (!f['Mark Posted']) return null
    var invNum = f['Invoice Number']
    var fs = invNum ? state.followupStatus[li.id] : { found: false }
    if (invNum && !fs) return 'loading'
    var corePaid = (fs && fs.found && !fs.unknown) ? fs.paid : null
    // Live Core "paid" wins immediately, at ANY stage — checked BEFORE the reminder-resolved
    // branch below, so a payment that posts in Core after the reminder was sent (or skipped)
    // still flips this to 'paid' instead of getting stuck showing stale "Checked" text forever.
    if (corePaid === true) return 'paid'
    // A human's manual "paid" call (made when Core couldn't confirm status automatically) resolves
    // to the SAME clean terminal state a Core-confirmed payment reaches — previously this only set
    // "Payment Status Checked" and fell through to the vaguer "checked-uncertain" bucket below,
    // which never advanced to a real Paid status even once a human had actually confirmed it.
    if (f['Payment Manually Marked Paid']) return 'paid'
    var days = followupDays(li, fs)
    if (days != null && days < B.PAYMENT_FOLLOWUP_DAYS) return 'not-due'
    // The reminder decision (sent, or a deliberate "don't send it, just move on" skip) is NOT
    // terminal — status can still change between the reminder and the actual run close, so it
    // moves into a required SECOND checkpoint (Final payment check) instead of closing the loop.
    // "closed-unpaid" is the only truly terminal unpaid state, reached once that second check has
    // actually happened — it still surfaces in the Readiness "Billing holds" panel (reads Core
    // directly, not scoped to any one run), so closing this out here never loses track of it.
    if (f['Reminder Sent'] || f['Reminder Skipped']) {
      return f['Final Payment Confirmed'] ? 'closed-unpaid' : 'final-check'
    }
    // A human's manual "still unpaid" call (made when Core couldn't confirm status automatically)
    // overrides everything below — same terminal state a Core-confirmed unpaid invoice reaches.
    if (f['Payment Manually Marked Unpaid']) return 'needs-reminder'
    if (!f['Payment Status Checked']) return (corePaid === null ? 'unknown' : 'needs-check')
    return corePaid === false ? 'needs-reminder' : 'checked-uncertain'
  }
  function needsFollowup(li) {
    var s = computeFollowupState(li)
    return s === 'needs-check' || s === 'needs-reminder' || s === 'unknown' || s === 'final-check'
  }

  // Live context for the closeout panel's two precondition checkboxes — informs the attestation
  // instead of leaving it a blind toggle. Each refreshes its own small host span, not a full
  // re-render (the panel itself may not even be in the DOM yet if the run isn't fully processed).
  function loadCloseoutContext() {
    B.getReimbursables().then(function (exp) {
      state.pendingReimbCount = (exp || []).filter(function (e) {
        var s = e.fields['Workflow Status'] || ''
        // 'Logged into Core' is a formula field name, not a status choice — this never matched, so
        // every expense already recorded in Core was counted as pending here. See services/billing.js.
        return s !== '' && s !== B.EXPENSE_IN_CORE_STATUS
      }).length
      var el = byId('aj-closeout-reimb-ctx'); if (el) el.outerHTML = closeoutReimbCtxHtml()
    }).catch(function () {})
    B.getBillingHolds().then(function (res) {
      state.openHoldsCount = ((res && res.holds) || []).filter(function (h) { return h.tier === 'hold' }).length
      var el2 = byId('aj-closeout-holds-ctx'); if (el2) el2.outerHTML = closeoutHoldsCtxHtml()
    }).catch(function () {})
  }
  function closeoutReimbCtxHtml() {
    var reimb = state.pendingReimbCount
    var text = reimb == null ? 'Loading…' : (reimb > 0 ? reimb + ' pending' : 'None pending')
    return '<div id="aj-closeout-reimb-ctx" style="font-size:10px;color:' + (reimb ? 'var(--amb)' : 'var(--t3)') + ';margin-top:2px">' + text + '</div>'
  }
  function closeoutHoldsCtxHtml() {
    var holds = state.openHoldsCount
    var text = holds == null ? 'Loading…' : (holds > 0 ? holds + ' client' + (holds > 1 ? 's' : '') + ' 60+ days overdue' : 'No open holds')
    return '<div id="aj-closeout-holds-ctx" style="font-size:10px;color:' + (holds ? 'var(--amb)' : 'var(--t3)') + ';margin-top:2px">' + text + '</div>'
  }

  // Load open "needs Core renumber recalibration" follow-ups; refresh the header without a full reload.
  function loadRenumberFollowups() {
    B.listRenumberFollowups().then(function (d) {
      state.renumberFollowups = (d && d.followups) || []
      var host = byId('aj-renumber-host'); if (host) host.innerHTML = renumberButtonHtml()
      maybeAutoShowRenumber()
    }).catch(function () {})
  }
  function renumberButtonHtml() {
    var n = (state.renumberFollowups || []).length
    if (!n) return ''
    return '<button class="aj-btn aj-btn-sm" style="border-color:rgba(192,57,43,0.3);background:var(--red-bg);color:var(--red);font-size:11px" data-action="open-renumber-checklist">&#9888; Core renumbering to fix (' + n + ')</button>'
  }
  // Auto-pop the checklist once per session when the run is fully worked (all bills sent/posted/skipped)
  // and there are still projects to re-calibrate — the user can close it and return via the button.
  function maybeAutoShowRenumber() {
    if (state.renumberAutoShown || !(state.renumberFollowups || []).length) return
    var li = state.lineItems || []
    if (!li.length) return
    var allDone = li.every(function (x) { var s = getStatusLabel(x); return s === 'posted' || s === 'paid' || s === 'closed-unpaid' || s === 'followup' || s === 'sent' || s === 'skip' })
    if (!allDone) return
    state.renumberAutoShown = true
    openRenumberChecklist()
  }
  function openRenumberChecklist() {
    var existing = byId('aj-renumber-modal'); if (existing) existing.parentNode.removeChild(existing)
    var el = document.createElement('div'); el.id = 'aj-renumber-modal'; el.className = 'aj-modal-overlay'
    el.innerHTML = '<div class="aj-modal" style="width:560px;max-width:95vw">'
      + '<div class="aj-modal-head"><div class="aj-modal-title">Core invoice numbering to re-calibrate</div><button class="aj-modal-close" data-action="close-renumber">x</button></div>'
      + '<div id="aj-renumber-body" style="padding:0.875rem 1rem;overflow-y:auto;flex:1"></div>'
      + '<div class="aj-modal-footer"><span style="margin-right:auto;font-size:11px;color:var(--t4)">Re-calibrate each project\'s automatic invoice numbering in Core, then mark it done. Close and return anytime.</span><button class="aj-btn" data-action="close-renumber">Close</button></div>'
      + '</div>'
    root.appendChild(el); document.body.style.overflow = 'hidden'
    renderRenumberBody()
  }
  function renderRenumberBody() {
    var wrap = byId('aj-renumber-body'); if (!wrap) return
    var list = state.renumberFollowups || []
    if (!list.length) { wrap.innerHTML = '<div style="font-size:12px;color:var(--grn);padding:8px 0">All caught up — nothing to re-calibrate.</div>'; return }
    var h = '<div style="font-size:11px;color:var(--t2);line-height:1.6;margin-bottom:8px">These projects had an invoice number changed to fix sequencing. In BQE Core, open each and re-calibrate its automatic invoice numbering so the next invoice numbers correctly.</div>'
    list.forEach(function (r) {
      h += '<div style="display:flex;align-items:center;gap:8px;padding:8px 0;border-bottom:0.5px solid var(--b1)">'
      h += '<div style="flex:1;font-size:13px;color:var(--t1)">' + escHtml(r.project_label || r.project_airtable_id) + '</div>'
      if (r.core_url) h += '<a href="' + escHtml(r.core_url) + '" target="_blank" rel="noopener noreferrer" style="font-size:11px;color:var(--blu);text-decoration:none">Open in Core &#8599;</a>'
      h += '<button class="aj-btn aj-btn-sm" style="font-size:10px" data-action="resolve-renumber" data-proj="' + escHtml(r.project_airtable_id) + '">Mark recalibrated</button>'
      h += '</div>'
    })
    wrap.innerHTML = h
  }

  function getStatusLabel(li) {
    var f = li.fields
    if (f['Skip Invoicing?'] === 'Yes') return 'skip'
    if (f['New Billing?'] === 'No' && f['Open Billing?'] === 'No' && f['PM Confirm Submit']) return 'skip'
    if (f['Mark Posted']) {
      var fs = computeFollowupState(li)
      if (fs === 'paid') return 'paid'
      if (fs === 'closed-unpaid') return 'closed-unpaid'
      return (fs === 'needs-check' || fs === 'needs-reminder' || fs === 'unknown' || fs === 'final-check') ? 'followup' : 'posted'
    }
    if (f['Send Invoice or Statement']) return 'sent'
    if (f['Draft Review Disposition'] === 'Approved') {
      // Approved is two distinct sub-states, not one — awaiting Core finalization (the invoice-
      // number sequence check lives here, buildSteps' step8Input) vs. actually finalized and ready
      // to issue. Split so "confirm the invoice number in Core" has a status/filter of its own
      // instead of being invisible inside a single "Approved" pill. Mirrors the two dots (`Approved`,
      // `Finalized`) rowDots() already tracks separately off these same two fields.
      return f['Review Draft Preview'] ? 'finalized' : 'approved'
    }
    // Sent back for corrections (and not yet re-prepared) — needs the bookkeeper to redo the draft.
    if (f['Draft Review Disposition'] === 'Revise' && !f['Create Draft']) return 'revise'
    // Create Draft is set by "Mark draft ready for review" — by the time this fires, the draft is
    // DONE and sitting in the billing manager's queue. "Drafting" read as if someone were still
    // actively preparing it; the real wait here is on an approval decision.
    if (f['Create Draft']) return 'awaiting-approval'
    if (f['Ready for Batch Invoice?'] === 'Yes') return 'batch'
    if (f['CORE Project Progress Current?']) return 'progress'
    return 'pending'
  }
  function pillHtml(s) {
    var map = { posted:['p-grn','Posted'], paid:['p-grn','Paid'], 'closed-unpaid':['p-muted','Closed — unpaid'], followup:['p-amb','Needs follow-up'], sent:['p-grn','Issued'], finalized:['p-blu','Finalized'], approved:['p-blu','Approved'], revise:['p-amb','Sent back'], 'awaiting-approval':['p-blu','Awaiting approval'], batch:['p-blu','Ready for batch'], progress:['p-muted','In progress'], skip:['p-muted','Skipping'], pending:['p-muted','Not started'] }
    var v = map[s] || map['pending']
    return '<span class="aj-pill ' + v[0] + '">' + v[1] + '</span>'
  }

  function rowDots(f, s) {
    var aging2 = f['Past Due Billing Aging'] || ''
    var openBilling2 = f['Open Billing?'] || ''
    var interestDone = !!(f['Charge Interest on Open Invoices 60 Days Plus']) || (openBilling2 === 'No') || (openBilling2 === 'Yes' && aging2 === '30 days')
    // Mirror the real step progression in buildSteps through posting, so the dots actually fill
    // across as a line item is worked. Field-based (no per-row insight needed); the conditional
    // Statement pay-all step isn't a line-item field, so it isn't a dot — nor are the post-posting
    // payment-followup steps (those need the per-item Core lookup this row-level pass doesn't do;
    // the "Needs follow-up" pill above is the row-level signal for that instead).
    var steps = [
      !!f['CORE Project Progress Current?'], !!f['Time and Expense Double Checked?'], !!f['New Billing?'], !!openBilling2,
      !!(f['Past Due Billing Aging'] || openBilling2 === 'No'), interestDone, !!f['PM Confirm Submit'], !!f['Create Draft'],
      f['Draft Review Disposition'] === 'Approved', !!f['Review Draft Preview'], !!f['Send Invoice or Statement'], !!f['Mark Posted']
    ]
    var dotLabels = ['Phase progress','T+E review','New billing','Open billing','Aging','Interest fee','Confirm','Prepare draft','Approved','Finalized','Issued','Posted']
    var firstIncomplete = -1
    for (var di = 0; di < steps.length; di++) { if (!steps[di]) { firstIncomplete = di; break } }
    var dots = ''
    steps.forEach(function(done, i) {
      var dc = s === 'skip' ? 'd-empty' : done ? 'd-grn' : i === firstIncomplete ? 'd-amb' : 'd-empty'
      var tipStatus = s === 'skip' ? 'skipped' : done ? 'complete' : i === firstIncomplete ? 'in progress' : 'not started'
      dots += '<div class="aj-dot-wrap" data-tip="' + dotLabels[i] + ': ' + tipStatus + '"><div class="aj-dot ' + dc + '" style="pointer-events:none"></div></div>'
    })
    return dots
  }

  function listRowHtml(item, idx) {
    var s = getStatusLabel(item)
    var f = item.fields
    var projName = (f['Project Name (from Project)'] || ['Unknown'])[0]
    var projNum = (f['Project Number'] || [''])[0]
    var isSel = state.selectedIdx === idx
    var h = '<div class="aj-list-row' + (isSel ? ' sel' : '') + '" id="lirow-' + idx + '" data-action="select-li" data-idx="' + idx + '">'
    h += '<div style="flex:1;min-width:0">'
    h += '<div style="display:flex;align-items:center;gap:6px"><div class="aj-mono" style="font-size:10px;color:var(--t4)">' + projNum + '</div>'
    h += '<div style="font-size:12px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' + projName + '</div></div>'
    h += '<div style="display:flex;align-items:center;gap:6px;margin-top:4px"><div class="aj-dot-row">' + rowDots(f, s) + '</div>'
    h += payActivityBadge(f)
    h += preflightTagHtml(item)
    h += '</div></div>'
    h += pillHtml(s)
    h += '<button data-action="delete-li" data-idx="' + idx + '" style="flex-shrink:0;background:none;border:none;cursor:pointer;padding:4px 6px;color:var(--t4);line-height:1;border-radius:4px;pointer-events:all" title="Remove from billing run">&#x2715;</button>'
    h += '</div>'
    return h
  }

  // One source of truth mapping a line-item status → a filter pill bucket, so the counts and the
  // filter can never drift and every status is covered. Every status is now its own bucket — this
  // used to lump progress/awaiting-approval/approved/revise/sent into one catch-all "active" bucket,
  // which meant "Approved" (and several other real states) had no filter of their own. Kept as its
  // own function (not just using the label directly) so a future re-grouping has one place to change.
  function statusBucket(s) { return s }
  function filterLi() {
    return state.lineItems.filter(function(x) {
      var s = getStatusLabel(x)
      if (state.hiddenFilters[statusBucket(s)]) return false
      if (state.searchQuery) {
        var q = state.searchQuery.toLowerCase()
        var name = ((x.fields['Project Name (from Project)'] || [''])[0]).toLowerCase()
        var num = ((x.fields['Project Number'] || [''])[0]).toLowerCase()
        if (name.indexOf(q) === -1 && num.indexOf(q) === -1) return false
      }
      return true
    })
  }

  function render() {
    // `my` (offset-derived) is only a query/navigation aid — never trust it for what's ON SCREEN.
    // The displayed month/year must come from the run record actually loaded (state.run.fields),
    // so the header can never show a different month than the data underneath it (e.g. after the
    // urlId-shortcut load, or any other path where the fetched run doesn't match the offset).
    var my = getMonthYear(state.monthOffset)
    var dispMonth = (state.run && state.run.fields['Month']) || my.month
    var dispYear = (state.run && state.run.fields['Year']) || my.year
    var h = ''
    var li = state.lineItems
    // Counts 'followup' as posted for the progress bar — it IS posted, just still needs a
    // payment check-in; otherwise the bar would visibly regress days after nothing changed.
    var postedCount = li.filter(function(x) { var s = getStatusLabel(x); return s === 'posted' || s === 'paid' || s === 'closed-unpaid' || s === 'followup' }).length
    // Skipped items are a DONE terminal state too (nothing left to do on them) — a run made up
    // partly of legitimately-skipped projects must still be able to reach 100%, not get stuck below
    // it forever just because "posted" alone was in the denominator's numerator.
    var skipCount = li.filter(function(x) { return getStatusLabel(x) === 'skip' }).length
    var pct = li.length > 0 ? Math.round((postedCount + skipCount) / li.length * 100) : 0

    h += '<div class="aj-run-header">'
    h += '<div><div class="aj-run-title">' + dispMonth + ' ' + dispYear + ' billing run</div>'
    h += '<div class="aj-run-meta">' + (state.run ? (state.run.fields['Status'] || '') : 'No run found') + ' &middot; ' + li.length + ' projects</div></div>'
    h += '<div class="aj-month-nav">'
    h += '<button class="aj-nav-btn" data-action="nav" data-dir="-1">&#8592;</button>'
    h += '<div class="aj-nav-label">' + dispMonth.substring(0,3) + ' ' + dispYear + '</div>'
    h += '<button class="aj-nav-btn" data-action="nav" data-dir="1">&#8594;</button>'
    h += '</div></div>'

    if (!state.run) {
      h += '<div class="aj-card"><div class="aj-empty">No billing run found for ' + my.month + ' ' + my.year + '. <span data-action="go-readiness" style="color:var(--blu);cursor:pointer;text-decoration:underline">Go to Readiness</span> to create one.</div></div>'
      byId('aj-br').innerHTML = h
      return
    }

    h += '<div style="background:var(--wh);border:0.5px solid var(--b1);border-radius:10px;padding:0.75rem 1rem;margin-bottom:1rem">'
    h += '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">'
    h += '<div style="font-size:12px;color:var(--t2)">'
    // The payments / reimbursables pills went with the two closeout ticks (2026-11-03): the monthly
    // books close checks both.
    h += '</div>'
    h += '<div style="display:flex;align-items:center;gap:10px"><span id="aj-renumber-host">' + renumberButtonHtml() + '</span><span id="aj-saving-msg"></span></div>'
    h += '</div>'
    h += '<div class="aj-prog-track"><div class="aj-prog-fill" style="width:' + pct + '%;background:var(--grn)"></div></div>'
    h += '<div style="display:flex;justify-content:space-between;margin-top:4px;font-family:\'DM Mono\',monospace;font-size:10px;color:var(--t3)">'
    h += '<span>' + postedCount + ' of ' + li.length + ' posted' + (skipCount ? ' &middot; ' + skipCount + ' skipped' : '') + '</span><span>' + pct + '%</span></div>'
    // Sum of this run's approved, new-billing invoice amounts (never statements, never interest —
    // see loadApprovedNewBillingTotal()'s own comment for exactly what's included).
    h += '<div style="margin-top:6px;font-size:11px;color:var(--t2)">'
    h += state.approvedTotalLoaded
      ? 'Approved new billing: <b style="font-family:\'DM Mono\',monospace;color:var(--t1)">' + fmtUsd(state.approvedTotal) + '</b>'
      : '<span style="color:var(--t4);font-style:italic">Calculating approved total&hellip;</span>'
    h += '</div></div>'

    var batchRunDone = !!(state.run.fields['Batch Run Complete'])
    var allItemsReady = state.run.fields['All Items Ready for Batch'] === 'Yes, all projects are ready.'
    var gateActive = allItemsReady && !batchRunDone

    if (gateActive) {
      var batchProjects = li.filter(function(x) {
        var bi = x.fields['Billing Instruction'] || ''
        return bi === 'Send Invoice Only' || bi === 'Send Invoice with Statement'
      })
      h += '<div style="background:var(--wh);border:0.5px solid var(--b1);border-radius:10px;overflow:hidden;margin-bottom:1rem">'
      h += '<div style="background:#07272D;padding:1rem 1.25rem">'
      h += '<div style="font-family:Montserrat,sans-serif;font-weight:300;font-size:14px;letter-spacing:0.08em;text-transform:uppercase;color:white;margin-bottom:4px">Batch invoice run</div>'
      h += '<div style="font-size:11px;color:rgba(255,255,255,0.5)">All ' + li.length + ' projects have completed pre-flight. Run the batch in Core before proceeding.</div>'
      h += '</div>'
      h += '<div style="padding:1.25rem">'
      h += '<div style="font-size:12px;color:var(--t2);line-height:1.7;margin-bottom:1.25rem">'
      h += 'Go to the batch invoice screen in Core and select the projects below. Check each invoice for phase overages. Apply any write-downs before creating drafts. Once complete, mark the batch done here to unlock per-project draft preparation.'
      h += '</div>'
      if (batchProjects.length > 0) {
        h += '<div style="background:var(--bg);border-radius:8px;padding:10px 14px;margin-bottom:1.25rem">'
        h += '<div style="font-family:Montserrat,sans-serif;font-weight:500;font-size:9px;letter-spacing:0.1em;text-transform:uppercase;color:var(--t4);margin-bottom:10px">Projects for batch (' + batchProjects.length + ')</div>'
        for (var bi2 = 0; bi2 < batchProjects.length; bi2++) {
          var bpf = batchProjects[bi2].fields
          var bpNum = (bpf['Project Number'] || [''])[0]
          var bpName = (bpf['Project Name (from Project)'] || ['Unknown'])[0]
          var bpInstr = bpf['Billing Instruction'] || ''
          var bpTag = bpInstr === 'Send Invoice with Statement' ? '<span style="font-size:9px;color:var(--amb);font-family:DM Mono,monospace;margin-left:4px">+ stmt</span>' : ''
          h += '<div style="display:flex;align-items:baseline;gap:10px;padding:5px 0;border-bottom:0.5px solid var(--b1)">'
          h += '<div style="font-family:DM Mono,monospace;font-size:11px;font-weight:500;color:var(--t1);min-width:70px">' + bpNum + '</div>'
          h += '<div style="font-size:12px;color:var(--t2);flex:1">' + bpName + bpTag + '</div>'
          h += '</div>'
        }
        h += '</div>'
      }
      h += '<div style="display:flex;align-items:center;gap:10px">'
      h += '<button class="aj-btn aj-btn-p" data-action="confirm-batch-run" style="font-size:12px;padding:8px 20px">Mark batch run complete</button>'
      h += '<span id="aj-batch-saving" style="font-size:11px;color:var(--t4);font-style:italic"></span>'
      h += '</div>'
      h += '</div></div>'
      byId('aj-br').innerHTML = h
      return
    }

    var allProcessed = state.run.fields['All Items Processed'] === 'True'
    var runStatus = state.run.fields['Status'] || ''
    var runComplete = runStatus === 'Complete'
    if (allProcessed || runComplete) {
      h += '<div class="aj-card" style="padding:1rem 1.25rem">'
      h += '<div class="aj-eyebrow" style="margin-bottom:8px">Close out this run</div>'
      if (runComplete) {
        h += '<div style="display:flex;align-items:center;gap:10px">'
        h += '<span class="aj-pill p-grn">Complete</span>'
        h += '<span style="font-size:11px;color:var(--t3)">' + dispMonth + ' is closed out.</span>'
        h += '<button class="aj-btn aj-btn-sm" style="margin-left:auto" data-action="reopen-run">Reopen</button>'
        h += '</div>'
      } else {
        // The two closeout ticks ("reimbursables recorded and approved", "prior payments processed and
        // recorded") are gone (Matt, 2026-11-03: "superfluous now"). The monthly books close applies
        // every payment before a month closes, and expense entries are reviewed daily in the lockout.
        h += '<div style="font-size:11px;color:var(--t2);margin-bottom:10px">All projects are posted or skipped. Payments and reimbursables are checked in the monthly books close, so nothing else is needed here.</div>'
        // Frontend-only gate, NOT a change to the opaque "All Items Processed" formula. A run can't
        // close while any posted line item still needs its payment follow-up resolved (paid-confirmed
        // or reminder actually sent).
        var followupBlockers = state.followupStatusLoaded ? li.filter(needsFollowup) : []
        var closeoutDisabled = !state.followupStatusLoaded || followupBlockers.length > 0
        if (!state.followupStatusLoaded) {
          h += '<div style="font-size:11px;color:var(--t3);margin-top:10px;font-style:italic">Still checking payment follow-ups…</div>'
        } else if (followupBlockers.length) {
          h += '<div style="font-size:11px;color:var(--amb);margin-top:10px;padding:8px 10px;background:var(--amb-bg);border-radius:6px">'
            + '<b>' + followupBlockers.length + '</b> project' + (followupBlockers.length > 1 ? 's' : '') + ' still need' + (followupBlockers.length > 1 ? '' : 's') + ' a payment follow-up before this run can close: '
            + followupBlockers.map(function (x) { return escHtml((x.fields['Project Number'] || [''])[0] || '') }).join(', ')
            + '</div>'
        }
        h += '<button class="aj-btn aj-btn-p aj-btn-sm" style="margin-top:10px" data-action="close-out-run" ' + (closeoutDisabled ? 'disabled' : '') + '>Close out ' + dispMonth + ' run</button>'
        h += '<span id="aj-closeout-saving" style="font-size:11px;color:var(--t4);font-style:italic;margin-left:8px"></span>'
      }
      h += '</div>'
    }

    h += '<div class="aj-two">'
    h += '<div>'
    // Counts per filter bucket — same statusBucket() mapping the filter uses, so they always agree.
    var counts = { pending: 0, progress: 0, batch: 0, 'awaiting-approval': 0, revise: 0, approved: 0, finalized: 0, sent: 0, posted: 0, followup: 0, paid: 0, 'closed-unpaid': 0, skip: 0 }
    li.forEach(function (x) { var b = statusBucket(getStatusLabel(x)); if (counts[b] !== undefined) counts[b]++ })

    h += '<div style="position:relative;margin-bottom:8px">'
    h += '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" style="position:absolute;left:10px;top:50%;transform:translateY(-50%);pointer-events:none;color:var(--t4)"><circle cx="5.5" cy="5.5" r="4" stroke="currentColor" stroke-width="1.3"/><path d="M9 9l2.5 2.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>'
    h += '<input id="aj-br-search" type="text" placeholder="Search projects..." value="' + (state.searchQuery || '').replace(/"/g, '&quot;') + '" style="width:100%;padding:6px 28px 6px 30px;font-size:12px;font-family:DM Sans,sans-serif;border:0.5px solid var(--b2);border-radius:6px;background:var(--wh);color:var(--t1);outline:none" data-action="search-input">'
    if (state.searchQuery) {
      h += '<button data-action="search-clear" style="position:absolute;right:8px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:var(--t4);font-size:14px;line-height:1;padding:2px 4px">&#x2715;</button>'
    }
    h += '</div>'

    h += '<div style="display:flex;gap:4px;margin-bottom:8px;flex-wrap:wrap;align-items:center">'
    h += '<span style="font-size:9px;color:var(--t4);font-family:Montserrat,sans-serif;font-weight:500;letter-spacing:0.08em;text-transform:uppercase;margin-right:2px">Show:</span>'
    var filterDefs = [
      {key:'pending', label:'Not started', count:counts.pending},
      {key:'progress', label:'In progress', count:counts.progress},
      {key:'batch', label:'Ready for batch', count:counts.batch},
      {key:'awaiting-approval', label:'Awaiting approval', count:counts['awaiting-approval']},
      {key:'revise', label:'Sent back', count:counts.revise},
      {key:'approved', label:'Approved', count:counts.approved},
      {key:'finalized', label:'Finalized', count:counts.finalized},
      {key:'sent', label:'Issued', count:counts.sent},
      {key:'posted', label:'Posted', count:counts.posted},
      {key:'followup', label:'Needs follow-up', count:counts.followup},
      {key:'paid', label:'Paid', count:counts.paid},
      {key:'closed-unpaid', label:'Closed — unpaid', count:counts['closed-unpaid']},
      {key:'skip', label:'Skipping', count:counts.skip}
    ]
    filterDefs.forEach(function(fi) {
      var isHidden = state.hiddenFilters[fi.key]
      h += '<button style="font-size:10px;padding:3px 9px;border-radius:4px;border:0.5px solid;display:inline-flex;align-items:center;gap:4px;pointer-events:all;cursor:pointer;font-family:DM Sans,sans-serif;'
      h += isHidden ? 'border-color:var(--b2);background:transparent;color:var(--t4);text-decoration:line-through' : 'border-color:var(--b3);background:#07272D;color:white'
      h += '" data-action="toggle-filter" data-filter="' + fi.key + '">'
      h += '<span style="pointer-events:none">' + fi.label + '</span>'
      h += '<span style="font-family:DM Mono,monospace;pointer-events:none;opacity:0.7"> ' + fi.count + '</span>'
      h += '</button>'
    })
    h += '</div>'

    h += preflightBannerHtml()
    h += '<div class="aj-card" id="aj-li-list">'
    if (state.loading) {
      h += '<div style="padding:1.5rem;text-align:center;color:var(--t3);font-size:12px">Loading projects...</div>'
    } else if (state.loadError) {
      // Load failed (e.g. Airtable rate-limit) — show a retry, never a false "0 projects".
      h += '<div style="padding:1.5rem;text-align:center;color:var(--t2);font-size:13px">'
        + '<div style="color:var(--red);font-weight:500;margin-bottom:4px">Couldn’t load this run’s invoices</div>'
        + '<div style="font-size:11px;color:var(--t3);margin-bottom:12px">The list failed to load (often a brief BQE/Airtable rate limit). Your invoices are safe.</div>'
        + '<button class="aj-btn aj-btn-p aj-btn-sm" data-action="retry-line-items">Retry</button>'
        + '</div>'
    } else {
      var filteredLi = filterLi()
      if (filteredLi.length === 0) h += '<div class="aj-empty">No projects match the current filters.</div>'
      filteredLi.forEach(function(item) { h += listRowHtml(item, li.indexOf(item)) })
    }
    h += '</div></div>'

    h += '<div id="aj-detail-panel" class="aj-panel-right">'
    if (state.selectedIdx >= 0 && state.lineItems[state.selectedIdx]) h += renderDetail(state.selectedIdx)
    else h += '<div class="aj-card"><div class="aj-empty">Select a project to work through its billing checklist</div></div>'
    h += '</div>'
    h += '</div>'

    byId('aj-br').innerHTML = h
  }

  function renderDetail(idx) {
    var item = state.lineItems[idx]
    var f = item.fields
    var projName = (f['Project Name (from Project)'] || ['Unknown'])[0]
    var projNum = (f['Project Number'] || [''])[0]
    var contractType = (f['Main Contract Type (from Project)'] || [''])[0]
    var isFixed = contractType === 'Fixed Fee'
    var s = getStatusLabel(item)
    var retainer = f['Current Project Retainer'] || 0
    var aging = f['Past Due Billing Aging'] || ''
    var isSkip = s === 'skip'

    var steps = buildSteps(f, isFixed)
    var activeStep = -1
    for (var si = 0; si < steps.length; si++) { if (!steps[si].done && !isSkip) { activeStep = si; break } }

    var h = '<div class="aj-card">'
    h += '<div class="aj-detail-head">'
    h += '<div class="aj-detail-name">' + projName + '</div>'
    h += '<div class="aj-detail-sub" style="justify-content:space-between;width:100%">'
    var slackTs = f['Slack Thread Timestamp'] || ''
    var slackBtn = '<button style="display:inline-flex;align-items:center;justify-content:center;padding:3px 5px;border-radius:4px;border:0.5px solid rgba(255,255,255,' + (slackTs ? '0.5' : '0.3') + ');background:rgba(255,255,255,' + (slackTs ? '0.18' : '0.08') + ');color:rgba(255,255,255,' + (slackTs ? '0.95' : '0.6') + ');cursor:pointer;pointer-events:all" data-action="open-billing-slack" data-idx="' + idx + '" title="' + (slackTs ? 'View discussion' : 'Start a discussion in Slack') + '">'
      + '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>'
      + '</button>'
    h += '<div style="display:flex;gap:6px;align-items:center"><span class="aj-dnum">' + projNum + '</span>' + pillHtml(s) + slackBtn + '</div>'
    // Undo the LAST completed step. Resolve the field from the live `steps` array (built above) — the
    // step list is dynamic (aging/interest/batch steps appear conditionally), so a fixed positional
    // map is wrong: it would clear the wrong field and appear to skip a step. Carry the field directly.
    var lastDoneStep = -1
    for (var si2 = steps.length - 1; si2 >= 0; si2--) { if (steps[si2].done) { lastDoneStep = si2; break } }
    if (lastDoneStep >= 0 && steps[lastDoneStep].field) {
      h += '<button style="font-size:10px;padding:3px 10px;border-radius:4px;border:0.5px solid rgba(255,255,255,0.4);background:rgba(255,255,255,0.12);color:rgba(255,255,255,0.85);cursor:pointer;font-family:DM Sans,sans-serif;pointer-events:all" data-action="step-back" data-idx="' + idx + '" data-field="' + steps[lastDoneStep].field + '">&#8592; Undo last step</button>'
    }
    // Temporary (Matt, 2026-11-03): skip any project for this run in one click, until the one-tab
    // Billing redesign decides this per project. Writes what "Close the quiet projects" writes.
    if (!isSkip && needsPreflight(item)) {
      h += '<button style="font-size:10px;padding:3px 10px;border-radius:4px;border:0.5px solid rgba(255,255,255,0.4);background:rgba(255,255,255,0.12);color:rgba(255,255,255,0.85);cursor:pointer;font-family:DM Sans,sans-serif;pointer-events:all" data-action="skip-this-month" data-idx="' + idx + '">Skip this month</button>'
    }
    h += '</div></div>'

    if (retainer > 0 || aging) {
      h += '<div style="display:flex;gap:12px;padding:0.5rem 1rem;background:var(--bg);border-bottom:0.5px solid var(--b1);flex-wrap:wrap">'
      if (retainer > 0) h += '<div style="font-size:11px;color:var(--t3)">Retainer on file <span style="font-family:DM Mono,monospace;font-weight:500;color:var(--t1)">$' + retainer.toLocaleString() + '</span></div>'
      if (aging) {
        var agc = aging === '90+ days' ? 'var(--red)' : aging === '60 days' ? 'var(--amb)' : 'var(--t3)'
        h += '<div style="font-size:11px;color:var(--t3)">Open aging <span style="font-family:DM Mono,monospace;font-weight:500;color:' + agc + '">' + aging + '</span></div>'
      }
      h += '</div>'
    }

    if (isSkip) {
      h += '<div style="padding:1.5rem;text-align:center;color:var(--t3);font-size:12px">This project is skipped for this billing run.</div></div>'
      return h
    }

    h += '<div>'
    for (var j = 0; j < steps.length; j++) {
      var step = steps[j]
      var isDone = step.done
      var isActive = j === activeStep
      var isPending = !isDone && !isActive
      var rowBg = isActive ? 'var(--wh2)' : 'var(--wh)'
      var rowOp = isPending ? '0.45' : '1'
      h += '<div class="aj-wf-step" style="background:' + rowBg + ';opacity:' + rowOp + '">'
      h += '<div class="aj-wf-num ' + (isDone ? 'wfn-done' : isActive ? 'wfn-now' : 'wfn-pend') + '">' + (isDone ? '&#10003;' : (j+1)) + '</div>'
      h += '<div class="aj-wf-body">'
      h += '<div class="aj-wf-label">' + step.label + '</div>'
      if (isDone && step.value) h += '<div style="font-size:11px;color:var(--t2);margin-top:2px">' + step.value + '</div>'
      else if (isActive) { h += '<div class="aj-wf-desc">' + step.desc + '</div><div class="aj-wf-act">' + step.input + '</div>' }
      h += '</div></div>'
    }
    h += '</div></div>'
    return h
  }

  function buildSteps(f, isFixed) {
    var steps = []
    var newBilling = f['New Billing?']
    var openBilling = f['Open Billing?']
    var aging = f['Past Due Billing Aging'] || ''
    var pmDone = f['PM Confirm Submit']
    var noActivity = newBilling === 'No' && openBilling === 'No'

    steps.push({
      field: 'CORE Project Progress Current?',
      label: 'Phase progress',
      desc: 'Review current phase completion. Update percent complete if advancement has occurred since last update.',
      done: !!f['CORE Project Progress Current?'],
      value: f['CORE Project Progress Current?'] ? 'Confirmed current' : '',
      input: isFixed
        ? '<button class="aj-btn aj-btn-p aj-btn-sm" data-action="open-phase-modal" data-idx="' + state.selectedIdx + '">Review and update phases</button>'
        : '<button class="aj-btn aj-btn-p aj-btn-sm" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="CORE Project Progress Current?" data-val="Yes">Confirm progress current</button>'
    })

    steps.push({
      field: 'Time and Expense Double Checked?',
      label: 'Time and expense review',
      desc: 'Confirm all T+E entries are accurate, under the correct phase, and have readable memos. Open the T+E Review screen in Core before confirming.',
      done: !!f['Time and Expense Double Checked?'],
      value: f['Time and Expense Double Checked?'] === 'Yes' ? 'Verified' : '',
      input: '<div style="margin-bottom:8px">'
        + '<a href="https://wap1-01.bqecore.com/webapp/Reviewer/ReviewTimeAndExpenses" target="_blank" class="aj-btn aj-btn-sm" data-action="open-te-reviewer" data-projnum="' + ((f['Project Number'] || [''])[0]) + '" style="display:inline-flex;align-items:center;gap:5px;text-decoration:none;pointer-events:all">'
        + '<svg width="11" height="11" viewBox="0 0 11 11" fill="none" style="pointer-events:none"><path d="M1 10L10 1M10 1H4M10 1V7" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        + '<span style="pointer-events:none">Open T+E Reviewer in Core</span></a>'
        + '<span id="aj-copy-confirm" style="font-size:10px;color:var(--grn);margin-left:6px;opacity:0;transition:opacity 0.3s"></span>'
        + '</div>'
        + '<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:2px">'
        + '<button class="aj-btn aj-btn-p aj-btn-sm" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="Time and Expense Double Checked?" data-val="Yes">Confirm T+E verified</button>'
        + '<button class="aj-btn aj-btn-sm" style="display:flex;align-items:center;gap:5px;color:var(--blu);border-color:var(--blu)" data-action="show-te-help">'
        + '<svg width="13" height="13" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.2"/><path d="M7 6v4M7 4.5v.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>'
        + 'How to review in Core</button>'
        + '</div>'
    })

    var nbSel = state.newBillingInsight && state.newBillingInsight.idx === state.selectedIdx ? state.newBillingInsight : null
    var nbInsight = nbSel ? nbSel.html : '<div style="background:var(--bg);border-radius:8px;padding:8px 10px;margin-bottom:6px;font-size:11px;color:var(--t3);font-style:italic">Loading billing signals...</div>'
    // Recommend "Yes" whenever the signals show any billable hours or phase advancement this cycle
    // (nbSel.recommendYes, set in fetchNewBillingInsight) — same "dark button = the system's own
    // finding" rule as Open billing? below, not a hardcoded default.
    var nbRecommendYes = !!(nbSel && nbSel.recommendYes === true)
    var nbRecommendNo = !!(nbSel && nbSel.recommendYes === false)

    steps.push({
      field: 'New Billing?',
      label: 'New billing this cycle?',
      desc: 'Review the signals below then confirm whether there is new work to invoice.',
      done: !!newBilling,
      value: newBilling || '',
      input: preflightAcceptHtml(state.selectedIdx) + nbInsight
        + '<div style="display:flex;gap:6px;margin-top:6px">'
        + '<button class="aj-btn aj-btn-sm' + (nbRecommendYes ? ' aj-btn-p' : '') + '" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="New Billing?" data-val="Yes">Yes, invoice this cycle</button>'
        + '<button class="aj-btn aj-btn-sm' + (nbRecommendNo ? ' aj-btn-p' : '') + '" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="New Billing?" data-val="No">No new billing</button>'
        + '</div>'
    })

    // The dark/primary button should match whatever Core itself found — not a hardcoded default —
    // so the recommended answer is visually obvious instead of always favoring "Yes" regardless of
    // what the insight above actually says. No recommendation (still loading, or Core couldn't
    // determine it) means neither button is emphasized.
    var obIns = state.openBillingInsight && state.openBillingInsight.idx === state.selectedIdx ? state.openBillingInsight.status : undefined
    var obRecommendYes = !!(obIns && !obIns.unknown && obIns.openCount > 0)
    var obRecommendNo = !!(obIns && !obIns.unknown && obIns.openCount === 0)
    steps.push({
      field: 'Open Billing?',
      label: 'Open billing?',
      desc: 'Are there any outstanding unpaid invoices for this project?',
      done: !!openBilling,
      value: openBilling || '',
      input: openBillingInsightHtml()
        + '<div style="margin-bottom:8px">'
        + '<a href="https://wap1-01.bqecore.com/webapp/InvoiceReview/InvoiceReview" target="_blank" class="aj-btn aj-btn-sm" data-action="open-invoice-reviewer" data-projnum="' + ((f['Project Number'] || [''])[0]) + '" style="display:inline-flex;align-items:center;gap:5px;text-decoration:none;pointer-events:all">'
        + '<svg width="11" height="11" viewBox="0 0 12 12" fill="none" style="pointer-events:none"><path d="M2 10h8M2 2h5l3 3v5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        + '<span style="pointer-events:none">Verify in Core</span></a>'
        + '<span id="aj-inv-copy-confirm" style="font-size:10px;color:var(--grn);margin-left:6px;opacity:0;transition:opacity 0.3s"></span>'
        + '</div>'
        + '<div style="display:flex;gap:6px">'
        + '<button class="aj-btn aj-btn-sm' + (obRecommendYes ? ' aj-btn-p' : '') + '" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="Open Billing?" data-val="Yes">Yes, open invoices</button>'
        + '<button class="aj-btn aj-btn-sm' + (obRecommendNo ? ' aj-btn-p' : '') + '" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="Open Billing?" data-val="No">No open invoices</button>'
        + '</div>'
    })

    if (noActivity) {
      steps.push({
        field: 'PM Confirm Submit',
        label: 'Nothing to send',
        desc: 'No new billing and no open invoices. This project is complete for this run.',
        done: !!pmDone,
        value: pmDone ? 'Marked complete' : '',
        input: '<button class="aj-btn aj-btn-p aj-btn-sm" data-action="skip-no-activity" data-idx="' + state.selectedIdx + '">Mark complete and skip</button>'
      })
      return steps
    }

    if (openBilling === 'Yes') {
      var agingSug = state.openBillingInsight && state.openBillingInsight.idx === state.selectedIdx && state.openBillingInsight.status ? state.openBillingInsight.status.suggestion : null
      var sugBadge = function(val) { return val === agingSug ? ' <span style="font-size:9px;font-weight:600;color:var(--blu);background:var(--blu-bg);border-radius:10px;padding:1px 6px;margin-left:4px;pointer-events:none">Suggested</span>' : '' }
      steps.push({
        field: 'Past Due Billing Aging',
        label: 'Open invoice aging',
        desc: 'How long has the oldest unpaid invoice been outstanding?',
        done: !!f['Past Due Billing Aging'],
        value: aging,
        input: '<div style="display:flex;flex-direction:column;gap:6px;margin-top:4px">'
          + '<button class="aj-btn aj-btn-sm" style="text-align:left;height:auto;padding:7px 10px;display:block;white-space:normal" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="Past Due Billing Aging" data-val="30 days">'
          + '<div style="font-weight:500;color:var(--t1);pointer-events:none">One cycle late' + sugBadge('30 days') + '</div>'
          + '<div style="font-size:10px;color:var(--t3);margin-top:1px;pointer-events:none">Grace period applies, no interest charged</div>'
          + '</button>'
          + '<button class="aj-btn aj-btn-sm" style="text-align:left;height:auto;padding:7px 10px;display:block;white-space:normal;border-color:var(--amb);color:var(--t1)" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="Past Due Billing Aging" data-val="60 days">'
          + '<div style="font-weight:500;color:var(--t1);pointer-events:none">Two cycles late' + sugBadge('60 days') + '</div>'
          + '<div style="font-size:10px;color:var(--t3);margin-top:1px;pointer-events:none">Interest applies, pause work notice required</div>'
          + '</button>'
          + '<button class="aj-btn aj-btn-sm" style="text-align:left;height:auto;padding:7px 10px;display:block;white-space:normal;border-color:var(--red);color:var(--t1)" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="Past Due Billing Aging" data-val="90+ days">'
          + '<div style="font-weight:500;color:var(--t1);pointer-events:none">Three or more cycles late' + sugBadge('90+ days') + '</div>'
          + '<div style="font-size:10px;color:var(--t3);margin-top:1px;pointer-events:none">Additional interest, strong pause notice required</div>'
          + '</button>'
          + '</div>'
      })

      var needsInterest = aging === '60 days' || aging === '90+ days'
      if (needsInterest) {
        steps.push({
          field: 'Charge Interest on Open Invoices 60 Days Plus',
          label: 'Charge interest in Core',
          desc: aging === '90+ days'
            ? '90+ days: charge additional interest in Core and prepare a strong notice that work will be paused until the account is current.'
            : '60 days: go to Core, select the open invoice, and apply the late fee. This will roll up on the next invoice automatically.',
          done: !!f['Charge Interest on Open Invoices 60 Days Plus'],
          value: f['Charge Interest on Open Invoices 60 Days Plus'] ? 'Interest charged in Core' : '',
          input: '<button class="aj-btn aj-btn-warn aj-btn-sm" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="Charge Interest on Open Invoices 60 Days Plus" data-val="true">Confirm interest charged in Core</button>'
        })
      }
    }

    steps.push({
      field: 'PM Confirm Submit',
      label: 'Confirm and proceed',
      desc: 'Pre-flight complete. Confirm to unlock batch invoice preparation.',
      done: !!pmDone,
      value: pmDone ? 'Pre-flight confirmed' : '',
      input: '<button class="aj-btn aj-btn-p aj-btn-sm" data-action="pm-confirm" data-idx="' + state.selectedIdx + '">Confirm and proceed</button>'
    })

    var batchRunComplete = !!(state.run && state.run.fields['Batch Run Complete'])
    if (pmDone && batchRunComplete) {
      // ── Prepare draft (bookkeeper) ── batch + write-downs in Core, then mark ready for review.
      // If it was sent back for corrections, surface that + the note so they know what to fix.
      var wasSentBack = f['Draft Review Disposition'] === 'Revise' && !f['Create Draft']
      var reviseNote = (f['Draft Review Comments'] || '').trim()
      var prepBanner = wasSentBack
        ? '<div style="background:var(--amb-bg);border:0.5px solid rgba(183,101,26,0.3);border-radius:6px;padding:7px 9px;margin-bottom:8px;font-size:11px;color:var(--amb);line-height:1.5"><b>Sent back for corrections.</b> Delete the draft in Core, make the changes, and generate a new draft, then mark it ready for review again.' + (reviseNote ? '<div style="margin-top:4px;color:var(--amb)"><b>Note:</b> ' + escHtml(reviseNote) + '</div>' : '') + '</div>'
        : ''
      steps.push({
        field: 'Create Draft',
        label: wasSentBack ? 'Re-prepare draft (corrections)' : 'Prepare draft',
        desc: 'Run the batch in Core, apply any write-downs for phase overages, and prepare the draft. Then mark it ready for the billing manager to review.',
        done: !!f['Create Draft'],
        value: f['Create Draft'] ? 'Ready for review' : '',
        input: prepBanner + invoicePreviewHtml() + '<button class="aj-btn aj-btn-p aj-btn-sm" data-action="mark-draft-ready" data-idx="' + state.selectedIdx + '">Mark draft ready for review</button>'
      })
      // ── Review & approve (billing manager = admin), THEN finalize in Core — two separate steps,
      //    not one. Previously both actions lived inside a single "Review & approve" step object, so
      //    the invoice-number finalization (and its out-of-sequence check below) only ever appeared
      //    as a second phase buried inside an already-checkmarked-looking step, easy to miss entirely
      //    until you'd actually clicked Approve. Splitting them also makes "Undo last step" correctly
      //    target JUST the finalize confirm (Review Draft Preview) instead of rolling back the whole
      //    approval decision when all you meant to undo was confirming too early. ──
      var isApprover = (function () { try { return hasModule('admin') } catch (e) { return false } })()
      var disp = f['Draft Review Disposition'] || ''
      var finalized = !!f['Review Draft Preview']   // "finalized in Core" confirm (real number + GL posted)
      var approveInput = invoicePreviewHtml()
      if (isApprover) {
        approveInput += '<div style="display:flex;gap:6px;margin-top:4px;flex-wrap:wrap">'
          + '<button class="aj-btn aj-btn-p aj-btn-sm" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="Draft Review Disposition" data-val="Approved">Approve</button>'
          + '<button class="aj-btn aj-btn-sm aj-btn-warn" data-action="send-back-draft" data-idx="' + state.selectedIdx + '">Send back for corrections</button>'
          + '</div>'
      } else {
        approveInput += '<div style="font-size:11px;color:var(--t3);font-style:italic;margin-top:6px">Awaiting billing manager approval.</div>'
      }
      steps.push({
        field: 'Draft Review Disposition',
        label: 'Approve draft',
        desc: 'Billing manager reviews the draft, then approves to finalize — or sends it back for corrections.',
        done: disp === 'Approved',
        value: disp === 'Approved' ? 'Approved' : '',
        input: approveInput
      })
      // Only appears once approved — Core assigns the real invoice number on finalize. This is the
      // step Matt was looking for: computeOutOfSeq()/selectedInvoicesOutOfSeq() (below, feeding the
      // invoice list above) auto-detect and HARD-BLOCK this confirm if the project's open invoices
      // aren't in numeric sequence — Core's auto-numbering can occasionally skip a number or land on
      // a malformed one — same check the old manual "filter Core's invoice list by project" process
      // existed to catch, just automated instead of relying on someone remembering to look.
      if (disp === 'Approved') {
        var finInput = invoicePreviewHtml()
          + '<div style="font-size:11px;color:var(--t2);margin-top:4px;line-height:1.6">Finalize the approved draft in Core (this assigns the real invoice number and posts the ledger). Check the invoice list above for a sequence warning before confirming.</div>'
          + '<div style="display:flex;gap:6px;margin-top:6px;flex-wrap:wrap">'
          + '<button class="aj-btn aj-btn-p aj-btn-sm" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="Review Draft Preview" data-val="true">Confirm finalized in Core</button>'
          + (isApprover ? '<button class="aj-btn aj-btn-sm aj-btn-warn" data-action="send-back-draft" data-idx="' + state.selectedIdx + '">Send back for corrections</button>' : '')
          + '</div>'
        steps.push({
          field: 'Review Draft Preview',
          label: 'Confirm invoice number in Core',
          desc: 'Core assigns the real invoice number when you finalize. Confirm it came through correctly and in sequence — Core\'s auto-numbering can occasionally skip a number or apply the wrong format.',
          done: finalized,
          value: finalized ? 'Finalized' : '',
          input: finInput
        })
      }
      // ── Statement pay-all link (only when the WORKFLOW calls for a statement: Open Billing? = Yes).
      //    Its own step because it can't exist until the invoice is approved + finalized in Core above
      //    (the statement isn't generated until the invoice has a real number). Done = link captured. ──
      if (f['Open Billing?'] === 'Yes') {
        var slSel = state.openBillingInsight && state.openBillingInsight.idx === state.selectedIdx ? state.openBillingInsight : null
        var slUrl = (slSel && slSel.statementPayUrl) || ''
        steps.push({
          field: '__statement-link__',   // sentinel — not an Airtable field; Undo clears the generated link
          label: 'Statement pay-all link',
          desc: 'Generate the pay link so the client settles the whole balance in one payment.',
          done: !!slUrl,
          value: slUrl ? 'Captured' : '',
          input: statementLinkHtml()
        })
      }
      steps.push({
        field: 'Send Invoice or Statement',
        label: 'Issue to client',
        desc: 'Email the ' + (f['Open Billing?'] === 'Yes' ? 'statement' : 'invoice') + ' to the client with the PDF attached and a pay link.',
        done: !!f['Send Invoice or Statement'],
        value: f['Send Invoice or Statement'] ? 'Issued' : '',
        input: issueStepHtml(f)
      })
      steps.push({
        field: 'Mark Posted',
        label: 'Post in BQE Core',
        desc: 'Open this invoice in BQE Core and post it (this commits it to the ledger). Once it shows posted in Core, confirm here to finish.',
        done: !!f['Mark Posted'],
        value: f['Mark Posted'] ? 'Posted in Core' : '',
        input: '<button class="aj-btn aj-btn-p aj-btn-sm" data-action="save-check-field" data-idx="' + state.selectedIdx + '" data-field="Mark Posted" data-val="true">I\'ve posted it in Core</button>'
      })
      if (f['Mark Posted'] && f['Send Invoice or Statement']) {
        var fuItem = state.lineItems[state.selectedIdx]
        var fuState = fuItem ? computeFollowupState(fuItem) : null
        var fuData = fuItem ? (state.followupStatus[fuItem.id] || {}) : {}
        // followupDays() anchors to OUR OWN Mark Posted At timestamp when we have one, not Core's
        // invoice date — copy below reflects whichever anchor is actually driving the state, so the
        // bookkeeper isn't told "Core shows X issued N days ago" when N actually came from our own
        // Mark Posted moment (fuHasAnchor false only for legacy line items posted before this field
        // existed, where the Core-date fallback is really what's being shown).
        var fuHasAnchor = !!(fuItem && fuItem.fields['Mark Posted At'])
        var fuDays = fuItem ? followupDays(fuItem, fuData) : null
        if (fuState === 'loading') {
          steps.push({ field: '__payment-check__', label: 'Confirm payment received', desc: 'Checking with BQE Core…', done: false, value: '', input: '' })
        } else if (fuState) {
          var multi = (fuData.openCount || 1) > 1
          var invoiceWord = multi ? (fuData.openCount + ' open invoices') : 'this invoice'
          // Every check state now asks explicitly — paid or still unpaid — never a bare ambiguous
          // "Confirm". This is a human sign-off, not just an echo of what Core already believes:
          // Core's cached balance can be stale (a check received but not yet entered there), so the
          // bookkeeper's own finding is what gets recorded. If the payment IS entered into Core
          // afterward, the next load/Refresh picks it up automatically — computeFollowupState checks
          // Core's live corePaid===true before ever looking at this manual flag.
          var paidBtn = '<button class="aj-btn aj-btn-sm" data-action="mark-payment-checked-paid" data-idx="' + state.selectedIdx + '">Confirm &mdash; paid</button>'
          var unpaidBtn = '<button class="aj-btn aj-btn-warn aj-btn-sm" data-action="mark-payment-checked-unpaid" data-idx="' + state.selectedIdx + '">Confirm &mdash; still unpaid</button>'
          // Core's invoice search is an in-page filter, not URL-drivable — copy a value to the
          // clipboard and open Invoice Review, same pattern as the T+E/Open-billing "verify in Core"
          // links above (reuses the existing generic open-core-invoice action, no new handler needed).
          // Copies just the leading project-number digits (e.g. "2602" from "2602.04"), not the full
          // invoice number, so pasting into Core's search surfaces every invoice for this client
          // instead of filtering down to the one tracked invoice.
          var fuInvNum = firstInvoiceNumber(f)
          var fuInvPrefix = fuInvNum ? (fuInvNum.match(/^\d+/) || [fuInvNum])[0].slice(0, 4) : ''
          var verifyInCoreHtml = fuInvPrefix
            ? ('<div style="margin-bottom:8px">'
              + '<a href="#" data-action="open-core-invoice" data-num="' + escHtml(fuInvPrefix) + '" data-url="https://wap1-01.bqecore.com/webapp/InvoiceReview/InvoiceReview" title="Open Core Invoice Review and copy this client\'s invoice prefix to paste into search" class="aj-btn aj-btn-sm" style="display:inline-flex;align-items:center;gap:5px;text-decoration:none;pointer-events:all">'
              + '<svg width="11" height="11" viewBox="0 0 12 12" fill="none" style="pointer-events:none"><path d="M2 10h8M2 2h5l3 3v5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
              + '<span style="pointer-events:none">Verify in Core (all invoices)</span></a>'
              + '</div>')
            : ''
          var checkDesc, checkValue, checkInput = ''
          if (fuState === 'not-due') {
            var fuTimingDesc = fuHasAnchor
              ? ('marked posted ' + (fuDays != null ? fuDays + ' day' + (fuDays === 1 ? '' : 's') + ' ago' : 'recently'))
              : ('Core shows ' + invoiceWord + ' issued ' + (fuDays != null ? fuDays + ' day' + (fuDays === 1 ? '' : 's') + ' ago' : 'recently'))
            checkDesc = 'Not due yet — ' + fuTimingDesc + '. Check back after ' + B.PAYMENT_FOLLOWUP_DAYS + ' days.'
            checkValue = ''
          } else if (fuState === 'unknown') {
            checkDesc = 'Couldn\'t confirm with Core (no invoice number on file for this line item, or Core has no balance on record for it). Check manually, then confirm either way.'
            checkValue = ''
            checkInput = paidBtn + ' ' + unpaidBtn
          } else if (fuState === 'needs-check') {
            var fuSinceLabel = fuHasAnchor ? 'since it was marked posted' : 'since the most recent was issued'
            checkDesc = 'Core shows an open balance of $' + (fuData.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + (multi ? (' across ' + invoiceWord) : ' on this invoice') + ', ' + fuDays + ' days ' + fuSinceLabel + ' — review the open invoice(s) above, then mark what you find.'
            checkValue = ''
            checkInput = paidBtn + ' ' + unpaidBtn
          } else if (fuState === 'paid') {
            checkDesc = f['Payment Manually Marked Paid'] ? 'Manually confirmed paid.' : ('Core shows ' + invoiceWord + ' as paid.')
            checkValue = 'Paid'
          } else if (fuState === 'needs-reminder' || fuState === 'checked-uncertain') {
            checkDesc = 'Payment status checked.'
            checkValue = fuState === 'needs-reminder' ? 'Still unpaid' : 'Checked — Core status unclear'
          } else if (fuState === 'final-check' || fuState === 'closed-unpaid') {
            checkDesc = 'Payment status checked.'
            checkValue = f['Reminder Sent'] ? 'Reminder sent' : 'Reminder skipped'
          }
          steps.push({
            field: '__payment-check__', label: 'Confirm payment received', desc: checkDesc,
            done: fuState !== 'not-due' && fuState !== 'unknown' && fuState !== 'needs-check',
            value: checkValue, input: openInvoiceListHtml() + verifyInCoreHtml + checkInput,
          })
        }
        if (fuState === 'needs-reminder') {
          var fuMulti = (fuData.openCount || 1) > 1
          var fuAfterLabel = fuHasAnchor ? 'after being marked posted' : (fuMulti ? 'after the most recent was issued' : 'after issue')
          steps.push({
            field: '__payment-reminder__', label: 'Send follow-up reminder', required: true,
            desc: 'Still unpaid ' + (fuDays != null ? fuDays + ' days ' + fuAfterLabel : 'after the follow-up window') + ' — send a reminder before closing this out, or skip it for now if this cycle is intentionally running late.',
            done: false, value: '',
            input: '<button class="aj-btn aj-btn-warn aj-btn-sm" data-action="open-followup-reminder-modal" data-idx="' + state.selectedIdx + '">Preview &amp; send reminder</button> '
              + '<button class="aj-btn aj-btn-sm" data-action="skip-followup-reminder" data-idx="' + state.selectedIdx + '">Skip for now</button>',
          })
        }
        // Second, later checkpoint — the reminder decision (sent or skipped) doesn't close the loop
        // by itself; status can still change before the run actually closes. Required until a human
        // confirms it one more time, right here at the close-out boundary.
        if (fuState === 'final-check' || fuState === 'closed-unpaid') {
          var fcDone = fuState === 'closed-unpaid'
          var fcSource = f['Reminder Sent'] ? 'the reminder was sent' : 'the reminder was skipped'
          steps.push({
            field: '__final-payment-check__', label: 'Final payment check', required: !fcDone,
            desc: fcDone
              ? 'Confirmed still unpaid at close — carries forward as open AR, tracked in Readiness > Billing holds.'
              : ('Before closing this run, confirm whether payment has come in since ' + fcSource + '.'),
            done: fcDone, value: fcDone ? 'Closed — still unpaid' : '',
            input: fcDone ? '' : (openInvoiceListHtml() + verifyInCoreHtml
              + '<button class="aj-btn aj-btn-sm" data-action="mark-payment-checked-paid" data-idx="' + state.selectedIdx + '">Confirm &mdash; paid</button> '
              + '<button class="aj-btn aj-btn-warn aj-btn-sm" data-action="mark-final-check-unpaid" data-idx="' + state.selectedIdx + '">Confirm &mdash; still unpaid, close it out</button>'),
          })
        }
      }
    }
    return steps
  }

  function selectLI(idx) {
    state.selectedIdx = idx
    state.newBillingInsight = null
    state.openBillingInsight = null
    var panel = byId('aj-detail-panel')
    if (panel) panel.innerHTML = renderDetail(idx)
    var rows = root.querySelectorAll('.aj-list-row')
    for (var i = 0; i < rows.length; i++) rows[i].classList.remove('sel')
    var row = byId('lirow-' + idx)
    if (row) row.classList.add('sel')
    fetchNewBillingInsight(idx)
  }

  function mergeUpdated(idx, updated) {
    var merged = state.lineItems[idx]
    var uf = (updated && updated.fields) || {}
    for (var key in uf) { if (uf.hasOwnProperty(key)) merged.fields[key] = uf[key] }
    state.lineItems[idx] = merged
  }

  async function saveField(idx, fieldName, value) {
    var fields = {}
    fields[fieldName] = value
    if (fieldName === 'CORE Project Progress Current?' && value === true) fields['CORE Project Progress Current?'] = 'Yes'
    return saveFields(idx, fields)
  }

  async function saveFields(idx, fields) {
    var item = state.lineItems[idx]
    showSaving(true)
    var updated = null
    try { updated = await B.updateLineItem(item.id, fields) }
    catch (e) { updated = { error: (e && e.message) || 'update failed' } }
    showSaving(false)
    if (updated && !updated.error) {
      mergeUpdated(idx, updated)
      var panel = byId('aj-detail-panel')
      if (panel) panel.innerHTML = renderDetail(idx)
      updateListRow(idx)
      // loadPaymentFollowupStatus() only ever bulk-computes state.followupStatus ONCE, for items
      // already posted when the run loaded — an item posted just now during this session was never
      // a candidate for that pass, so without this it shows "Checking with BQE Core…" forever (the
      // 'loading' branch of computeFollowupState) with nothing left to resolve it.
      if (fields['Mark Posted'] === true) { refreshFollowupStatusForIdx(idx); stampMarkPostedAt(idx) }
      // Approving/reversing a draft, or flipping New Billing?, changes whether this item counts
      // toward the header's approved-total — recompute rather than let it go stale until reload.
      if (fields['Draft Review Disposition'] !== undefined || fields['New Billing?'] !== undefined) loadApprovedNewBillingTotal()
    } else {
      // A silent no-op here reads as "nothing happened" — surface it so a failed write (e.g. a
      // field that doesn't exist yet in Airtable) isn't mistaken for a click that did nothing.
      var errMsg = updated && updated.error ? (updated.error.message || updated.error) : 'Unknown error'
      try { alertDialog({ title: 'Save failed', message: 'Couldn\'t save this change: ' + errMsg + '. Nothing was recorded — try again.' }) } catch (e) {}
    }
  }

  function updateListRow(idx) {
    var item = state.lineItems[idx]
    var f = item.fields
    var s = getStatusLabel(item)
    var row = byId('lirow-' + idx)
    if (!row) return
    var pillEl = row.querySelector('.aj-pill')
    if (pillEl) { var tmp = document.createElement('div'); tmp.innerHTML = pillHtml(s); row.replaceChild(tmp.firstChild, pillEl) }
    var dotRow = row.querySelector('.aj-dot-row')
    if (dotRow) dotRow.innerHTML = rowDots(f, s)
  }

  function showSaving(on) {
    var el = byId('aj-saving-msg')
    if (el) el.innerHTML = on ? '<span class="aj-saving">Saving...</span>' : ''
  }

  // ── Phase modal ───────────────────────────────────────────────────────────────
  async function openPhaseModal(idx) {
    var item = state.lineItems[idx]
    var f = item.fields
    var projName = (f['Project Name (from Project)'] || ['Unknown'])[0]
    var projId = (f['Project'] || [])[0]

    var existing = byId('aj-phase-modal')
    if (existing) existing.parentNode.removeChild(existing)

    var el = document.createElement('div')
    el.id = 'aj-phase-modal'; el.className = 'aj-modal-overlay'
    el.innerHTML = '<div class="aj-modal" style="width:620px;max-width:95vw">'
      + '<div class="aj-modal-head"><div class="aj-modal-title">Phase progress: ' + projName + '</div>'
      + '<button class="aj-modal-close" data-action="close-phase-modal">x</button></div>'
      + '<div id="aj-phase-table-wrap" style="padding:0.875rem 1rem;overflow-y:auto;flex:1"><div style="font-size:12px;color:var(--t3)">Loading phases...</div></div>'
      + '<div id="aj-core-mismatch-warn" style="display:none;background:var(--amb-bg);border-top:1px solid rgba(183,101,26,0.3);padding:8px 16px;font-size:11px;color:var(--amb);align-items:center;gap:8px">'
      + '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" style="flex-shrink:0"><path d="M6.5 1L12 12H1L6.5 1Z" stroke="#B7651A" stroke-width="1.2" stroke-linejoin="round"/><path d="M6.5 5v3M6.5 9.5v.5" stroke="#B7651A" stroke-width="1.2" stroke-linecap="round"/></svg>'
      + '<span id="aj-core-mismatch-msg">Resolve Core mismatches before confirming.</span>'
      + '</div>'
      + '<div class="aj-modal-footer">'
      + '<span class="aj-modal-status" id="aj-phase-status"></span>'
      + '<button class="aj-btn aj-btn-sm" style="margin-right:auto;color:var(--t3);border-color:var(--b2)" data-action="refresh-core-pct"><span style="pointer-events:none">Refresh Core</span></button>'
      + '<button class="aj-btn" data-action="close-phase-modal">Close</button>'
      + '<button class="aj-btn aj-btn-p" id="aj-confirm-phases-btn" data-action="confirm-phases-done" data-idx="' + idx + '" title="Marks CORE Project Progress Current? = Yes and advances this invoice step.">Confirm progress current</button>'
      + '</div></div>'
    root.appendChild(el)
    document.body.style.overflow = 'hidden'

    if (!projId) {
      byId('aj-phase-table-wrap').innerHTML = '<div style="font-size:12px;color:var(--t3)">No project linked to this line item.</div>'
      return
    }

    var phases = await B.getPhasesForProject(projId)
    if (!phases || !phases.length) {
      byId('aj-phase-table-wrap').innerHTML = '<div style="font-size:12px;color:var(--t3)">No phases found for this project.</div>'
      return
    }
    var h = '<table style="width:100%;border-collapse:collapse;font-size:12px">'
    h += '<tr style="border-bottom:0.5px solid var(--b2)">'
    h += '<th style="text-align:left;padding:5px 8px;font-family:Montserrat,sans-serif;font-size:9px;letter-spacing:0.08em;text-transform:uppercase;color:var(--t3);font-weight:500">Phase</th>'
    h += '<th style="text-align:center;padding:5px 8px;font-family:Montserrat,sans-serif;font-size:9px;letter-spacing:0.08em;text-transform:uppercase;color:var(--t3);font-weight:500;width:70px">Cortex</th>'
    h += '<th style="text-align:center;padding:5px 8px;font-family:Montserrat,sans-serif;font-size:9px;letter-spacing:0.08em;text-transform:uppercase;color:var(--t3);font-weight:500;width:60px">Core</th>'
    h += '<th style="text-align:center;padding:5px 8px;font-family:Montserrat,sans-serif;font-size:9px;letter-spacing:0.08em;text-transform:uppercase;color:var(--t3);font-weight:500;width:100px">Update %</th>'
    h += '<th style="text-align:center;padding:5px 8px;font-family:Montserrat,sans-serif;font-size:9px;letter-spacing:0.08em;text-transform:uppercase;color:var(--t3);font-weight:500;width:40px"></th>'
    h += '<th style="text-align:right;padding:5px 8px;font-family:Montserrat,sans-serif;font-size:9px;letter-spacing:0.08em;text-transform:uppercase;color:var(--t3);font-weight:500;width:80px">Billed</th>'
    h += '<th style="text-align:left;padding:5px 8px;font-family:Montserrat,sans-serif;font-size:9px;letter-spacing:0.08em;text-transform:uppercase;color:var(--t3);font-weight:500;width:90px">Last updated</th>'
    h += '<th class="aj-desktop-only" style="text-align:left;padding:5px 8px;font-family:Montserrat,sans-serif;font-size:9px;letter-spacing:0.08em;text-transform:uppercase;color:var(--t3);font-weight:500;width:110px">Updated by</th>'
    h += '<th style="width:28px;padding:5px 4px"></th>'
    h += '</tr>'
    for (var i = 0; i < phases.length; i++) {
      var p = phases[i]
      var pf2 = p.fields
      var phaseName = pf2['Name'] || ''
      var pctRaw = pf2['Percent Complete Override'] || 0
      var pctDisplay = Math.round(pctRaw * 100)
      var lastUpdated = pf2['Percent Complete Updated'] ? new Date(pf2['Percent Complete Updated']).toLocaleDateString('en-US', {month:'short',day:'numeric'}) : ''
      var pctColor = pctDisplay >= 100 ? 'var(--grn)' : pctDisplay >= 50 ? 'var(--t1)' : 'var(--amb)'
      h += '<tr style="border-bottom:0.5px solid var(--b1)">'
      h += '<td style="padding:6px 8px;font-weight:500">' + phaseName + '</td>'
      h += '<td style="padding:6px 8px;text-align:center;font-family:DM Mono,monospace;font-size:12px;font-weight:500;color:' + pctColor + '" id="pct-display-' + p.id + '">' + pctDisplay + '%</td>'
      var isHourly = (pf2['Contract Type'] || '') === 'Hourly'
      h += '<td style="padding:6px 8px;text-align:center;font-family:DM Mono,monospace;font-size:11px;color:var(--t4)" id="core-pct-' + p.id + '" data-core-id="' + (pf2['BQE Core Record ID'] || '') + '" data-hourly="' + (isHourly ? '1' : '0') + '">...</td>'
      if (isHourly) {
        h += '<td style="padding:6px 8px;text-align:center" colspan="2"><span style="font-size:10px;color:var(--t4);font-style:italic">Hourly phase</span></td>'
      } else {
        h += '<td style="padding:6px 8px;text-align:center"><input id="pct-input-' + p.id + '" type="number" min="0" max="100" value="' + pctDisplay + '" style="width:65px;font-size:12px;padding:3px 6px;border-radius:4px;border:0.5px solid var(--b3);background:var(--wh);color:var(--t1);text-align:center;font-family:DM Mono,monospace"></td>'
        h += '<td style="padding:6px 4px;text-align:center">'
        h += '<button class="aj-btn aj-btn-sm" style="font-size:10px;padding:3px 8px" data-action="save-phase-pct" data-phase-id="' + p.id + '" data-idx="' + idx + '" title="Save records this % in Cortex. Update Core directly in BQE, then click Refresh Core.">Save</button>'
        h += '</td>'
      }
      var atBilled = pf2['Billed Fee'] || 0
      h += '<td style="padding:6px 8px;text-align:right;font-family:DM Mono,monospace;font-size:11px;color:var(--t4)" id="billed-fee-' + p.id + '" data-at-billed="' + atBilled + '">...</td>'
      var updatedBy = (pf2['Member Name (from Last Update By)'] || [])[0] || ''
      var firstName = updatedBy.split(' ')[0] || ''
      h += '<td style="padding:6px 8px;font-size:10px;color:var(--t3)" id="last-date-' + p.id + '">' + lastUpdated + '</td>'
      h += '<td class="aj-desktop-only" style="padding:6px 8px;font-size:10px;color:var(--t3)" id="last-by-' + p.id + '">' + firstName + '</td>'
      var openUrl = pf2['Open in Core'] || ''
      h += '<td style="padding:6px 4px;text-align:center">'
      if (openUrl) {
        h += '<a href="' + openUrl + '" target="_blank" title="Open in Core" style="color:var(--t3);text-decoration:none;display:inline-flex;align-items:center">'
        h += '<svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M1 10L10 1M10 1H4M10 1V7" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg></a>'
      }
      h += '</td></tr>'
    }
    h += '</table>'
    // Legend — explain the Save-here / update-in-Core / Refresh workflow.
    h += '<div style="margin-top:10px;padding:8px 10px;background:var(--bg);border-radius:8px;font-size:11px;color:var(--t2);line-height:1.6">'
      + '<b style="color:var(--t1)">Save</b> records the % in Cortex. To change what Core bills on, update the phase’s percent complete '
      + 'directly in BQE Core (the ↗ link opens it), then click <b style="color:var(--t1)">Refresh Core</b> to pull the latest. '
      + 'Once a phase matches (Cortex = Core), use <b style="color:var(--t1)">Confirm progress current</b> to advance this invoice step.'
      + '</div>'
    byId('aj-phase-table-wrap').innerHTML = h
    var phaseModal = byId('aj-phase-modal')
    if (phaseModal && phases.length > 0) {
      var rci = phases[0].fields['Core Project UID (from Project)'] || ''
      phaseModal.setAttribute('data-root-project-id', Array.isArray(rci) ? rci[0] || '' : rci)
    }
    setTimeout(function() {
      var bcells = root.querySelectorAll('[id^="billed-fee-"]')
      for (var bi = 0; bi < bcells.length; bi++) {
        if (bcells[bi].textContent === '...') {
          // Core didn't load — show the last-synced Cortex value, clearly muted + italic so it reads
          // as "unverified cached", distinct from a green (matches Core) or amber (differs) figure.
          var atVal = parseFloat(bcells[bi].getAttribute('data-at-billed') || '0')
          bcells[bi].textContent = '$' + atVal.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})
          bcells[bi].style.color = 'var(--t4)'
          bcells[bi].style.fontStyle = 'italic'
          bcells[bi].title = 'Cortex cached value — Core not loaded yet'
        }
      }
    }, 8000)
    setTimeout(fetchCorePercents, 100)
  }

  function showTeHelpPopover() {
    var existing = byId('aj-te-help')
    if (existing) { existing.parentNode.removeChild(existing); return }
    var el = document.createElement('div')
    el.id = 'aj-te-help'; el.className = 'aj-modal-overlay'; el.style.zIndex = '10000'
    el.innerHTML = '<div class="aj-modal" style="width:420px;max-width:94vw">'
      + '<div class="aj-modal-head"><div class="aj-modal-title">Reviewing T+E in Core</div><button class="aj-modal-close" data-action="close-te-help">x</button></div>'
      + '<div style="padding:1.1rem 1.25rem">'
      + '<div style="font-size:12px;color:var(--t2);line-height:1.75;margin-bottom:1rem">The T+E Review screen in BQE Core lets you see all time and expense entries for a project grouped by phase. Before confirming, check that each entry is under the correct phase, hours are accurate, and memos are readable and professional.</div>'
      + '<div style="font-size:11px;color:var(--t3);margin-bottom:12px;font-family:Montserrat,sans-serif;font-weight:500;letter-spacing:0.06em;text-transform:uppercase">Steps</div>'
      + '<div style="font-size:12px;color:var(--t2);line-height:2">1. Open the project in Core<br>2. Go to Time and Expenses tab<br>3. Review each entry for phase accuracy and memo quality<br>4. Correct any errors before returning here to confirm</div>'
      + '<a href="https://www.loom.com/share/a376d8e9626642768031e0f3bebac204" target="_blank" style="display:flex;align-items:center;gap:8px;margin-top:1rem;padding:10px 12px;background:var(--blu-bg);border-radius:8px;border:0.5px solid rgba(26,79,138,0.2);text-decoration:none">'
      + '<svg width="28" height="20" viewBox="0 0 28 20" fill="none"><rect width="28" height="20" rx="4" fill="#625DF5"/><path d="M11 6l8 4-8 4V6z" fill="white"/></svg>'
      + '<div><div style="font-size:12px;font-weight:500;color:var(--blu)">Watch the walkthrough</div><div style="font-size:10px;color:var(--t3);margin-top:1px">How to review T+E entries in BQE Core</div></div></a>'
      + '</div>'
      + '<div class="aj-modal-footer"><button class="aj-btn aj-btn-p" data-action="close-te-help">Got it</button></div>'
      + '</div>'
    root.appendChild(el)
  }

  function showPctWarning(liIdx, phaseId, newPct, currentPct) {
    var existing = byId('aj-pct-warn')
    if (existing) existing.parentNode.removeChild(existing)
    var el = document.createElement('div')
    el.id = 'aj-pct-warn'; el.className = 'aj-modal-overlay'; el.style.zIndex = '10000'
    el.innerHTML = '<div class="aj-modal" style="width:440px;max-width:94vw">'
      + '<div class="aj-modal-head" style="background:#B7651A"><div class="aj-modal-title">Reduce phase progress?</div></div>'
      + '<div style="padding:1.1rem 1.25rem">'
      + '<div style="font-size:13px;font-weight:500;color:var(--t1);margin-bottom:8px">You are setting progress from ' + currentPct + '% down to ' + newPct + '%.</div>'
      + '<div style="font-size:12px;color:var(--t2);line-height:1.7">This may set the billing basis below what has already been invoiced for this phase. Before proceeding, confirm that you have reviewed the posted invoices for this project and that this reduction is intentional and accurate.</div>'
      + '<div style="margin-top:10px;padding:8px 10px;background:var(--amb-bg);border-left:3px solid var(--amb);border-radius:4px;font-size:11px;color:var(--amb)">Reducing percent complete below a previously billed amount may cause overbilling discrepancies on future invoices.</div>'
      + '</div>'
      + '<div class="aj-modal-footer">'
      + '<button class="aj-btn" data-action="cancel-pct-warn">Cancel</button>'
      + '<button class="aj-btn" style="border-color:var(--amb);color:var(--amb)" data-action="confirm-pct-reduce" data-phase-id="' + phaseId + '" data-idx="' + liIdx + '" data-val="' + (newPct / 100) + '">I have reviewed - proceed</button>'
      + '</div></div>'
    root.appendChild(el)
  }

  // ── BQE Core reads (server-minted token via services/billing.js) ────────────────
  // Paint one Core % cell from a resolved percentComplete value (0–100). Green when it matches the
  // Cortex %, amber when it differs (update Core directly, then Refresh Core to re-pull).
  function paintCoreCell(cell, percentComplete) {
    if (percentComplete === undefined || percentComplete === null) { cell.textContent = 'n/a'; cell.style.color = 'var(--t4)'; return }
    var corePct = Math.round(percentComplete || 0)
    var atCell = byId(cell.id.replace('core-pct-', 'pct-display-'))
    var atPct = atCell ? parseInt(atCell.textContent) : null
    var color = 'var(--t3)'
    if (atPct !== null && corePct !== atPct) color = 'var(--amb)'
    if (atPct !== null && corePct === atPct) color = 'var(--grn)'
    cell.textContent = corePct + '%'
    cell.style.color = color
  }

  // Fill the modal's Core % + Billed columns. Both come from the firm-wide caches prefetched on
  // Run open, so this is instant and fires no per-modal Core calls (which used to race the token).
  // Any phase missing from the cache falls back to a single direct read.
  async function fetchCorePercents() {
    var cells = root.querySelectorAll('[id^="core-pct-"]')
    if (!cells.length) return
    fetchBilledFromCore()   // Billed = firm invoice cache, matched by phase Core UID

    var phaseMap = await B.getCorePhasesCached()
    var fallbacks = []
    for (var ci = 0; ci < cells.length; ci++) {
      var cell = cells[ci]
      var coreId = cell.getAttribute('data-core-id')
      if (!coreId) { cell.textContent = '-'; cell.style.color = 'var(--t4)'; continue }
      if (cell.getAttribute('data-hourly') === '1') { cell.textContent = '—'; cell.style.color = 'var(--t4)'; continue }
      if (phaseMap && Object.prototype.hasOwnProperty.call(phaseMap, coreId)) {
        paintCoreCell(cell, phaseMap[coreId])
      } else {
        fallbacks.push(cell)   // not in the cache — resolve individually
      }
    }
    checkCoreMatch()

    // Rare path: a phase Core ID wasn't in the firm prefetch — read it directly (still token-safe
    // now that the access token is cached server-side).
    fallbacks.forEach(function (cell) {
      var coreId = cell.getAttribute('data-core-id')
      B.getCorePhase(coreId).then(function (phase) {
        if (!phase || phase.error) { cell.textContent = 'err'; cell.style.color = 'var(--red)'; return }
        paintCoreCell(cell, phase.percentComplete)
        checkCoreMatch()
      }).catch(function () { cell.textContent = '-'; cell.style.color = 'var(--t4)' })
    })
  }

  function fmtMoney(n) { return (Math.round((n || 0) * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
  function setBilledCell(billedCell, coreBilled) {
    var atBilled = parseFloat(billedCell.getAttribute('data-at-billed') || '0')
    var cb = Math.round((coreBilled || 0) * 100) / 100
    var differs = Math.abs(cb - atBilled) > 0.01
    var phaseId = billedCell.id.replace('billed-fee-', '')
    billedCell.style.color = differs ? 'var(--amb)' : (cb > 0 ? 'var(--grn)' : 'var(--t4)')   // green = verified match to Core
    billedCell.style.fontWeight = differs ? '600' : '500'
    billedCell.style.fontStyle = 'normal'   // clears the muted/italic "cached" state
    billedCell.title = differs
      ? ('Core: $' + fmtMoney(cb) + ' vs Cortex: $' + fmtMoney(atBilled) + ' — Sync to update Cortex to match')
      : ('✓ matches Core: $' + fmtMoney(cb))
    if (differs) {
      billedCell.innerHTML = '<span style="pointer-events:none">$' + fmtMoney(cb) + '</span>'
        + ' <button class="aj-btn aj-btn-sm" style="font-size:9px;padding:1px 5px;color:var(--amb);border-color:var(--amb);vertical-align:middle" data-action="sync-billed-fee" data-phase-record-id="' + phaseId + '" data-core-billed="' + cb + '">Sync</button>'
    } else {
      billedCell.textContent = '$' + fmtMoney(cb)
    }
  }
  // Billed-to-date per phase = sum of the firm's Core invoice lines (serviceAmount + fixedFee), keyed
  // by phase Core UID. Invoices come from the prefetched firm-wide cache (loaded once on Run open), so
  // this is instant — no per-modal re-scan. Interest (Core's "Billed Extra") is excluded.
  async function fetchBilledFromCore() {
    var invoices = await B.getCoreInvoicesCached()
    if (!Array.isArray(invoices)) return
    var billedByPhase = {}
    for (var i = 0; i < invoices.length; i++) {
      var inv = invoices[i]
      var isInterest = inv.isLateFeeInvoice === true || String(inv.invoiceNumber || '').toUpperCase().indexOf('-INT') !== -1
      if (inv.isVoid || inv.isDraft || isInterest) continue
      var details = inv.invoiceDetails || []
      for (var j = 0; j < details.length; j++) {
        var d = details[j]
        if (!d.projectId) continue
        billedByPhase[d.projectId] = (billedByPhase[d.projectId] || 0) + (d.serviceAmount || 0) + (d.fixedFee || 0)
      }
    }
    var coreCells = root.querySelectorAll('[id^="core-pct-"]')
    for (var ci = 0; ci < coreCells.length; ci++) {
      var coreId = coreCells[ci].getAttribute('data-core-id')
      if (!coreId) continue
      var billedCell = byId('billed-fee-' + coreCells[ci].id.replace('core-pct-', ''))
      if (billedCell) setBilledCell(billedCell, billedByPhase[coreId] || 0)
    }
  }

  function checkCoreMatch() {
    var cells = root.querySelectorAll('[id^="core-pct-"]')
    var mismatches = []
    for (var i = 0; i < cells.length; i++) {
      var cell = cells[i]
      var coreVal = cell.textContent.trim()
      var phaseId = cell.id.replace('core-pct-', '')
      var row = cell.closest('tr')
      if (coreVal === '...' || coreVal === '-' || coreVal === 'err' || coreVal === 'n/a' || coreVal === '—') { if (row) row.style.borderLeft = ''; continue }
      var atCell = byId('pct-display-' + phaseId)
      if (!atCell) continue
      var atVal = atCell.textContent.trim()
      if (coreVal !== atVal) { mismatches.push(coreVal + ' vs ' + atVal); if (row) { row.style.borderLeft = '3px solid var(--amb)'; row.style.paddingLeft = '4px' } }
      else { if (row) row.style.borderLeft = '' }
    }
    var btn = byId('aj-confirm-phases-btn')
    var warn = byId('aj-core-mismatch-warn')
    var msg = byId('aj-core-mismatch-msg')
    // The button always stays clickable so you can advance the invoice step. A mismatch isn't a hard
    // block — it's a flagged warning (it can be intentional, e.g. you're billing behind Core), so the
    // button just shifts to an amber "Confirm anyway" with the warning shown above it.
    if (mismatches.length > 0) {
      if (warn) warn.style.display = 'flex'
      if (msg) msg.textContent = mismatches.length + ' phase' + (mismatches.length > 1 ? 's differ' : ' differs') + ' from Core. Update Core/Cortex to match, or confirm anyway if the difference is intentional.'
      if (btn) {
        btn.disabled = false; btn.style.opacity = '1'; btn.style.cursor = 'pointer'
        btn.textContent = 'Confirm anyway'
        btn.style.background = '#B7651A'; btn.style.borderColor = '#B7651A'
        btn.title = 'Some phases still differ from Core. Confirming marks progress current and advances the invoice step anyway.'
      }
    } else {
      if (warn) warn.style.display = 'none'
      if (btn) {
        btn.disabled = false; btn.style.opacity = '1'; btn.style.cursor = 'pointer'
        btn.textContent = 'Confirm progress current'
        btn.style.background = ''; btn.style.borderColor = ''
        btn.title = 'Marks CORE Project Progress Current? = Yes and advances this invoice step.'
      }
    }
  }

  // (Billed-from-Core is now read off each phase record in fetchCorePercents — Core's own per-phase
  // "Billed" figure — instead of re-summing invoice line items, which didn't match Core's number.)

  async function savePhasePercent(liIdx, phaseId, pctDecimal) {
    var statusEl = byId('aj-phase-status')
    if (statusEl) statusEl.textContent = 'Saving...'
    var memberId = getLoggedInMemberId()
    // Shared with the Progress Gate (services/phases.js#savePhasePercentComplete) — one write shape.
    var updated = await savePhasePercentComplete(phaseId, pctDecimal, memberId)
    if (updated && !updated.error) {
      if (statusEl) statusEl.textContent = 'Saved'
      var newPct = Math.round((updated.fields['Percent Complete Override'] || 0) * 100)
      var pctColor = newPct >= 100 ? 'var(--grn)' : newPct >= 50 ? 'var(--t1)' : 'var(--amb)'
      var currentEl = byId('pct-display-' + phaseId)
      if (currentEl) { currentEl.textContent = newPct + '%'; currentEl.style.color = pctColor; checkCoreMatch() }
      var inputEl = byId('pct-input-' + phaseId)
      if (inputEl) { inputEl.style.borderColor = 'var(--grn)'; inputEl.value = newPct }
      var dateEl = byId('last-date-' + phaseId)
      if (dateEl) dateEl.textContent = 'Today'
      var byEl = byId('last-by-' + phaseId)
      if (byEl) {
        var u = window.logged_in_user
        var uname = u && u.name ? u.name.split(' ')[0] : ''
        byEl.textContent = uname || 'You'
      }
      setTimeout(function() { if (statusEl) statusEl.textContent = '' }, 2000)
    } else {
      if (statusEl) statusEl.textContent = 'Error saving'
    }
  }

  // ── Billing signals ─────────────────────────────────────────────────────────────
  // The window itself now lives in services/billing.js (getBillingPeriod, "since last run") —
  // shared with Readiness instead of an independently-drifting local copy (the two previously
  // didn't even agree with each other: this one started the window on the 1st of last month,
  // Readiness's own copy started it a few days after the prior close).
  /**
   * The billing signals for one project, from its phases and the period's time entries. Shared by
   * the per-project "Billing signals" panel and the run-wide pre-flight, so the two can never
   * disagree about what a project owes.
   *   phases      — rows for phases that advanced past what was billed, or carry billable hours
   *   hasHours    — approved billable hours this period
   *   hasAdvanced — a phase's % complete is past what has been billed
   *   notSent     — approved entries not yet in Core
   *   waitingHours — billable hours logged but not approved yet (a project with these isn't quiet)
   */
  function phaseSignals(phases, entries) {
    var notSent = 0, waitingHours = 0
    var phaseHours = {}
    if (entries) entries.forEach(function(e) {
      var st = e.fields['Status'] || ''
      if (!e.fields['Billable']) return   // only billable hours feed the billing signal
      var hrs = e.fields['Actual Hours'] || 0
      if (st !== 'Approved' && st !== 'Posted' && st !== 'Post Failed') {   // approved work only
        if (st !== 'Rejected') waitingHours += hrs
        return
      }
      var phaseId2 = (e.fields['Phase Airtable ID'] || [])[0]
      if (phaseId2) phaseHours[phaseId2] = (phaseHours[phaseId2] || 0) + hrs
      if (st !== 'Posted') notSent++   // approved but not yet sent to Core
    })

    var sorted = (phases || []).slice().sort(function(a, b) { return (a.fields['Sort Order'] || 0) - (b.fields['Sort Order'] || 0) })
    var rows = []
    sorted.forEach(function(p) {
      var pfields = p.fields
      var current = Math.round((pfields['Percent Complete Override'] || 0) * 100)
      var billedFeeVal = pfields['Billed Fee'] || 0
      var contractedFeeVal = pfields['Contracted Fee'] || 0
      // "Billed Percent Complete" looks like the natural field to compare against, but it's actually
      // typed CURRENCY in Airtable (not percent) and sits blank on most phases — the old `|| 0`
      // fallback made every phase with ANY progress read as "advanced past what was billed," even
      // when nothing had genuinely changed (confirmed: every phase on a real project came back with
      // that field simply absent). Compare against the fee-ratio percent instead — the exact number
      // already shown in this same row's "Billed" column below, so "advanced" means what it looks
      // like it means on screen, and isn't defeated by a field that was never actually populated.
      var billed = contractedFeeVal > 0 ? Math.round((billedFeeVal / contractedFeeVal) * 100) : 0
      var hrs = phaseHours[p.id] || 0
      var advanced = current > billed
      if (advanced || hrs > 0) {
        rows.push({ name: pfields['Name'] || '', current: current, billed: billed, billedFee: billedFeeVal, contractedFee: contractedFeeVal, hours: hrs, advanced: advanced })
      }
    })
    return {
      phases: rows,
      notSent: notSent,
      waitingHours: waitingHours,
      hasAdvanced: rows.some(function (ap) { return ap.advanced }),
      hasHours: rows.some(function (ap) { return ap.hours > 0 }),
    }
  }

  // ── Pre-flight: the system's answers for every project, worked out when the run opens ──────────
  // A project still needs pre-flight answers until its "Confirm and proceed" (or skip) is done.
  function needsPreflight(li) {
    var f = li.fields
    return !f['PM Confirm Submit'] && f['Skip Invoicing?'] !== 'Yes'
  }
  function shortPhaseName(n) { return String(n || '').replace(/^\d{2}-\d{2}-/, '') }

  /**
   * Answers New billing? / Open billing? / aging for every project still in pre-flight, from the
   * same signals the per-project panel shows. Nothing is written here: the answers are defaults a
   * person accepts (one project at a time, or the quiet ones together).
   *
   * A project is QUIET only when every answer is known and all of them say there is nothing to do:
   * no approved billable hours, no phase past what's billed, no billable hours waiting for
   * approval, and Core shows no open invoice. Anything unknown (no Core project on file, Core
   * without balances) keeps it off the quiet list.
   */
  async function loadPreflight() {
    var runId = state.run && state.run.id
    state.preflight = {}; state.preflightLoaded = false; state.preflightError = false
    var items = (state.lineItems || []).filter(needsPreflight)
    if (!items.length) { state.preflightLoaded = true; render(); return }
    try {
      var projIds = items.map(function (li) { return (li.fields['Project'] || [])[0] }).filter(Boolean)
      var results = await Promise.all([B.getBillingPeriod(), B.getPhasesForProjects(projIds), B.getProjectCoreUids(projIds)])
      var period = results[0], phasesBy = results[1], uidBy = results[2]
      // Expenses: billable Core entries no invoice has picked up, and expense reports filed in
      // Cortex that haven't reached Core. Either failing leaves the answer unknown, never "none".
      var coreExp = null, pendingExp = null
      try { coreExp = await B.getUnbilledCoreExpenses() } catch (e) { coreExp = null }
      try { pendingExp = await B.getPendingExpenseReports() } catch (e) { pendingExp = null }
      var startISO = period.start.toISOString().split('T')[0]
      var endISO = period.end.toISOString().split('T')[0]
      var periodLabel = period.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        + ' – ' + period.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      var out = {}
      for (var i = 0; i < items.length; i++) {
        var li = items[i]
        var pid = (li.fields['Project'] || [])[0]
        if (!pid) continue
        var entries = await B.getProjectTimeEntriesInPeriod(pid, startISO, endISO)
        var sig = phaseSignals(phasesBy[pid] || [], entries)
        var uid = uidBy[pid] || ''
        var inv = uid ? await B.getProjectInvoiceStatus(uid) : null
        var openKnown = !!(inv && !inv.unknown)
        var openYes = openKnown && inv.openCount > 0
        var exp = expenseSignals(phasesBy[pid] || [], uid, coreExp, pendingExp && pendingExp[pid], pendingExp != null)

        var billWhy = []
        var hrs = sig.phases.reduce(function (s, p) { return s + p.hours }, 0)
        if (hrs > 0) billWhy.push(hrs.toFixed(1) + ' billable hours')
        sig.phases.filter(function (p) { return p.advanced }).forEach(function (p) {
          billWhy.push(shortPhaseName(p.name) + ' at ' + p.current + '%, billed ' + p.billed + '%')
        })
        if (exp.unbilled.length) billWhy.push(fmtUsd(exp.unbilledTotal) + ' in ' + exp.unbilled.length + ' expense' + (exp.unbilled.length > 1 ? 's' : '') + ' in Core not billed yet, oldest ' + exp.oldest)
        var openWhy = !uid ? 'no Core project on file, so open invoices can\'t be checked'
          : !openKnown ? 'Core doesn\'t show balances for this project'
          : openYes ? inv.openCount + ' open invoice' + (inv.openCount > 1 ? 's' : '') + ', ' + fmtUsd(inv.openTotal) + ', oldest ' + inv.daysOutstanding + ' days'
          : 'no open invoices in Core'

        out[li.id] = {
          newBilling: (sig.hasHours || sig.hasAdvanced || exp.unbilled.length > 0) ? 'Yes' : 'No',
          openBilling: openKnown ? (openYes ? 'Yes' : 'No') : null,
          aging: openYes ? inv.suggestion : null,
          billWhy: billWhy.length ? billWhy.join('; ') : 'no billable hours, no phase past what\'s billed, no unbilled expenses',
          openWhy: openWhy,
          waitingHours: sig.waitingHours,
          expWaiting: exp.waiting,
          expUnknown: exp.unknown,
          periodLabel: periodLabel,
          quiet: !sig.hasHours && !sig.hasAdvanced && sig.waitingHours === 0 && openKnown && !openYes
            && !exp.unknown && exp.unbilled.length === 0 && exp.waiting.length === 0,
        }
      }
      if (!state.run || state.run.id !== runId) return   // the month changed while this ran
      state.preflight = out
    } catch (e) {
      if (!state.run || state.run.id !== runId) return
      state.preflightError = true
    }
    state.preflightLoaded = true
    render()
  }

  /**
   * One project's expenses for the pre-flight. `coreExp` is the firm's billable Core expense entries
   * not yet invoiced (null = couldn't read), matched to this project by its phases' Core ids and
   * its root Core id. `pending` is its expense reports filed in Cortex but not in Core yet: these
   * won't reach this cycle's invoice until approved (pay-me-back) or posted in Bookkeeping (company
   * card), so they keep the project off the quiet list and say why.
   */
  function expenseSignals(phases, rootUid, coreExp, pending, pendingKnown) {
    var ids = {}
    function add(u) { u = String(u || '').split('?')[0].trim().toLowerCase(); if (u) ids[u] = true }
    add(rootUid)
    ;(phases || []).forEach(function (p) { add(p.fields['BQE Core Record ID']) })
    var unbilled = (coreExp || []).filter(function (e) { return ids[String(e.projectId || '').toLowerCase()] })
    var total = unbilled.reduce(function (s, e) { return s + (Number(e.chargeAmount) || 0) }, 0)
    var oldest = unbilled.map(function (e) { return String(e.date || '').slice(0, 10) }).filter(Boolean).sort()[0] || ''
    var waiting = []
    var sub = (pending || []).filter(function (x) { return x.status === 'Submitted' })
    var appr = (pending || []).filter(function (x) { return x.status === 'Approved' })
    var card = appr.filter(function (x) { return x.payor !== 'Employee' })
    var mine = appr.filter(function (x) { return x.payor === 'Employee' })
    function sum(list) { return fmtUsd(list.reduce(function (s, x) { return s + (x.amount || 0) }, 0)) }
    function n(list, word) { return list.length + ' ' + word + (list.length > 1 ? 's' : '') }
    if (sub.length) waiting.push(n(sub, 'expense') + ' (' + sum(sub) + ') waiting for approval')
    if (card.length) waiting.push(n(card, 'company-card expense') + ' (' + sum(card) + ') approved, not posted in Bookkeeping yet')
    if (mine.length) waiting.push(n(mine, 'approved expense') + ' (' + sum(mine) + ') not in Core yet')
    return { unbilled: unbilled, unbilledTotal: total, oldest: oldest, waiting: waiting, unknown: coreExp == null || !pendingKnown }
  }

  function quietItems() {
    if (!state.preflightLoaded) return []
    return (state.lineItems || []).filter(function (li) {
      var p = state.preflight[li.id]
      return p && p.quiet && needsPreflight(li)
    })
  }

  // The strip above the project list: what the system worked out, and the one click for the quiet ones.
  function preflightBannerHtml() {
    var open = (state.lineItems || []).filter(needsPreflight)
    if (!open.length) return ''
    var h = '<div style="background:var(--wh);border:0.5px solid var(--b1);border-radius:10px;padding:0.75rem 1rem;margin-bottom:8px">'
    h += '<div class="aj-eyebrow" style="margin-bottom:4px">Pre-flight answers</div>'
    if (!state.preflightLoaded) {
      h += '<div style="font-size:11px;color:var(--t3);font-style:italic">Working out new billing, open invoices and aging for ' + open.length + ' project' + (open.length > 1 ? 's' : '') + '…</div></div>'
      return h
    }
    if (state.preflightError) {
      h += '<div style="font-size:11px;color:var(--amb)">Couldn\'t work out the answers (Core or Airtable didn\'t respond). Each project still shows its own signals when you open it. <span data-action="retry-preflight" style="color:var(--blu);cursor:pointer;text-decoration:underline">Try again</span></div></div>'
      return h
    }
    var answered = open.filter(function (li) { return state.preflight[li.id] })
    var toBill = answered.filter(function (li) { return state.preflight[li.id].newBilling === 'Yes' }).length
    var withOpen = answered.filter(function (li) { return state.preflight[li.id].openBilling === 'Yes' }).length
    var quiet = quietItems()
    h += '<div style="font-size:12px;color:var(--t2);line-height:1.6">'
      + answered.length + ' project' + (answered.length !== 1 ? 's' : '') + ' answered: '
      + '<b style="color:var(--t1)">' + toBill + '</b> to bill, '
      + '<b style="color:var(--t1)">' + withOpen + '</b> with open invoices, '
      + '<b style="color:var(--t1)">' + quiet.length + '</b> with nothing to do.</div>'
    if (quiet.length) {
      h += '<button class="aj-btn aj-btn-p aj-btn-sm" style="margin-top:8px" data-action="open-quiet-modal">Close the ' + quiet.length + ' quiet project' + (quiet.length > 1 ? 's' : '') + '…</button>'
    }
    h += '</div>'
    return h
  }

  // The small suggestion line on a list row, while the project is still in pre-flight.
  function preflightTagHtml(li) {
    if (!state.preflightLoaded || !needsPreflight(li)) return ''
    var p = state.preflight[li.id]
    if (!p) return ''
    var txt
    if (p.quiet) txt = 'Nothing to do'
    else {
      var bits = []
      if (p.newBilling === 'Yes') bits.push('Bill')
      if (p.openBilling === 'Yes') bits.push('Open ' + (AGING_LABEL[p.aging] || ''))
      else if (p.openBilling == null) bits.push('Open invoices unknown')
      if (p.waitingHours > 0 && p.newBilling === 'No') bits.push(p.waitingHours.toFixed(1) + 'h awaiting approval')
      if (p.expWaiting && p.expWaiting.length) bits.push('Expenses waiting')
      else if (p.expUnknown && p.newBilling === 'No') bits.push('Expenses unknown')
      txt = bits.join(' · ') || 'No new billing'
    }
    return '<span title="The system\'s pre-flight answer" style="flex-shrink:0;font-size:9px;color:var(--t3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' + escHtml(txt) + '</span>'
  }

  // The "use these answers" box on the New billing step.
  function preflightAcceptHtml(idx) {
    var li = state.lineItems[idx]
    var p = li && state.preflightLoaded ? state.preflight[li.id] : null
    if (!p || li.fields['New Billing?'] || li.fields['Open Billing?']) return ''
    var h = '<div style="background:var(--blu-bg);border-radius:8px;padding:8px 10px;margin-bottom:8px;font-size:11px;color:var(--t2);line-height:1.6">'
    h += '<div style="font-weight:500;color:var(--blu);margin-bottom:2px">The system\'s answers (' + escHtml(p.periodLabel) + ')</div>'
    h += '<div>New billing: <b>' + p.newBilling + '</b>, ' + escHtml(p.billWhy) + '</div>'
    if (p.waitingHours > 0) h += '<div style="color:var(--amb)">' + p.waitingHours.toFixed(1) + ' billable hours logged but not approved yet</div>'
    ;(p.expWaiting || []).forEach(function (w) { h += '<div style="color:var(--amb)">' + escHtml(w) + ', so it won\'t be on this invoice until it reaches Core</div>' })
    if (p.expUnknown) h += '<div style="color:var(--amb)">Expenses couldn\'t be checked (Core or Airtable didn\'t respond); check them in Core\'s T+E review</div>'
    h += '<div>Open invoices: <b>' + (p.openBilling || 'unknown') + '</b>, ' + escHtml(p.openWhy) + '</div>'
    if (p.aging) h += '<div>Aging: <b>' + escHtml(AGING_LABEL[p.aging] || p.aging) + '</b></div>'
    if (p.openBilling) {
      h += '<button class="aj-btn aj-btn-p aj-btn-sm" style="margin-top:6px" data-action="accept-preflight" data-idx="' + idx + '">Use these answers</button>'
    } else {
      h += '<div style="margin-top:4px;color:var(--amb)">Answer open invoices yourself below; the system couldn\'t tell.</div>'
    }
    h += '</div>'
    return h
  }

  function acceptPreflight(idx) {
    var li = state.lineItems[idx]
    var p = li && state.preflight[li.id]
    if (!p || !p.openBilling) return
    var fields = { 'New Billing?': p.newBilling, 'Open Billing?': p.openBilling }
    if (p.openBilling === 'Yes' && p.aging) fields['Past Due Billing Aging'] = p.aging
    saveFields(idx, fields)
  }

  function openQuietModal() {
    var quiet = quietItems()
    if (!quiet.length) return
    var existing = byId('aj-quiet-modal'); if (existing) existing.parentNode.removeChild(existing)
    var el = document.createElement('div'); el.id = 'aj-quiet-modal'; el.className = 'aj-modal-overlay'
    var period = (state.preflight[quiet[0].id] || {}).periodLabel || ''
    var rows = quiet.map(function (li) {
      var f = li.fields
      return '<label style="display:flex;align-items:flex-start;gap:8px;padding:7px 0;border-bottom:0.5px solid var(--b1);cursor:pointer">'
        + '<input type="checkbox" data-quiet-id="' + li.id + '" checked style="margin-top:2px">'
        + '<div style="flex:1;min-width:0"><div style="font-size:12px;color:var(--t1)"><span class="aj-mono" style="font-size:10px;color:var(--t4);margin-right:6px">' + escHtml((f['Project Number'] || [''])[0]) + '</span>' + escHtml((f['Project Name (from Project)'] || [''])[0]) + '</div></div></label>'
    }).join('')
    el.innerHTML = '<div class="aj-modal" style="width:560px">'
      + '<div class="aj-modal-head"><div class="aj-modal-title">Close the quiet projects</div><button class="aj-modal-close" data-action="close-quiet-modal">x</button></div>'
      + '<div style="padding:0.875rem 1.1rem;overflow-y:auto;flex:1">'
      + '<div style="font-size:11px;color:var(--t2);line-height:1.6;margin-bottom:8px">Between ' + escHtml(period) + ' these projects have no approved billable hours, no billable hours waiting for approval, no phase past what\'s been billed, and no open invoices in Core. Untick any you want to work through by hand.</div>'
      + rows
      + '<div style="font-size:11px;color:var(--t3);line-height:1.6;margin-top:10px">For each project this records: phase progress current, time and expense checked, no new billing, no open invoices, skipped for this run. Undo is per project, from its own checklist.</div>'
      + '</div>'
      + '<div class="aj-modal-footer"><span class="aj-modal-status" id="aj-quiet-status"></span><button class="aj-btn" data-action="close-quiet-modal">Cancel</button><button class="aj-btn aj-btn-p" id="aj-quiet-go" data-action="confirm-quiet">Close ' + quiet.length + ' project' + (quiet.length > 1 ? 's' : '') + '</button></div>'
      + '</div>'
    root.appendChild(el); document.body.style.overflow = 'hidden'
  }
  function closeQuietModal() {
    var el = byId('aj-quiet-modal'); if (el) el.parentNode.removeChild(el)
    document.body.style.overflow = ''
  }
  function refreshQuietButton() {
    var boxes = root.querySelectorAll('#aj-quiet-modal input[data-quiet-id]')
    var n = 0; for (var i = 0; i < boxes.length; i++) if (boxes[i].checked) n++
    var go = byId('aj-quiet-go')
    if (go) { go.textContent = 'Close ' + n + ' project' + (n !== 1 ? 's' : ''); go.disabled = n === 0 }
  }

  async function closeQuietProjects() {
    var boxes = root.querySelectorAll('#aj-quiet-modal input[data-quiet-id]')
    var ids = []
    for (var i = 0; i < boxes.length; i++) if (boxes[i].checked) ids.push(boxes[i].getAttribute('data-quiet-id'))
    if (!ids.length) return
    var go = byId('aj-quiet-go'); if (go) go.disabled = true
    var status = byId('aj-quiet-status')
    var mid = getLoggedInMemberId()
    var failed = []
    for (var k = 0; k < ids.length; k++) {
      if (status) status.textContent = 'Closing ' + (k + 1) + ' of ' + ids.length + '…'
      var idx = -1
      for (var j = 0; j < state.lineItems.length; j++) if (state.lineItems[j].id === ids[k]) { idx = j; break }
      if (idx < 0) continue
      // The same fields the manual walk-through writes for a project with nothing to send.
      var fields = {
        'CORE Project Progress Current?': 'Yes',
        'Time and Expense Double Checked?': 'Yes',
        'New Billing?': 'No',
        'Open Billing?': 'No',
        'Skip Invoicing?': 'Yes',
        'PM Confirm Submit': true,
      }
      if (mid) fields['PM Setup Completed By'] = [mid]
      var updated = null
      try { updated = await B.updateLineItem(ids[k], fields) } catch (e) { updated = { error: (e && e.message) || 'update failed' } }
      if (updated && !updated.error) mergeUpdated(idx, updated)
      else failed.push((state.lineItems[idx].fields['Project Number'] || [''])[0] || ids[k])
    }
    closeQuietModal()
    try {
      var refreshed = await B.getRunById(state.run.id)
      if (refreshed) state.run = refreshed
    } catch (e) {}
    render()
    if (failed.length) {
      try { alertDialog({ title: 'Some projects weren\'t closed', message: 'These didn\'t save: ' + failed.join(', ') + '. Nothing was recorded for them; work them by hand or try again.' }) } catch (e) {}
    }
  }

  async function fetchNewBillingInsight(idx) {
    var item = state.lineItems[idx]
    var f = item.fields
    var projId = (f['Project'] || [])[0]
    if (!projId) return
    var period = await B.getBillingPeriod()
    var startISO = period.start.toISOString().split('T')[0]
    var endISO = period.end.toISOString().split('T')[0]

    var phases = await B.getPhasesForProject(projId)
    // Time now lives in Supabase (was the legacy Airtable staging table). Count APPROVED work and
    // flag what's approved but not yet posted to Core. "Posted" status = the entry reached Core.
    var entries = await B.getProjectTimeEntriesInPeriod(projId, startISO, endISO)

    var sig = phaseSignals(phases, entries)
    var notSent = sig.notSent
    var allPhaseData = sig.phases

    // Same range Readiness's "Billing period" header shows — this step reads time entries from
    // this exact window, so a bookkeeper who's just seen 4 people flagged behind (or not) up there
    // can tell at a glance whether this project's signals reflect the same "since last run" span.
    var periodLabel = period.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      + ' – ' + period.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    var h = '<div style="background:var(--bg);border-radius:8px;padding:10px 12px;margin-bottom:4px;font-size:11px">'
    h += '<div style="display:flex;align-items:baseline;justify-content:space-between;margin-bottom:8px">'
    h += '<span style="font-family:Montserrat,sans-serif;font-weight:500;font-size:9px;letter-spacing:0.08em;text-transform:uppercase;color:var(--t3)">Billing signals</span>'
    h += '<span style="font-family:DM Mono,monospace;font-size:9px;color:var(--t4)">' + periodLabel + '</span>'
    h += '</div>'
    if (allPhaseData.length > 0) {
      h += '<table style="width:100%;border-collapse:collapse">'
      h += '<tr style="border-bottom:0.5px solid var(--b1)">'
      h += '<th style="text-align:left;font-family:Montserrat,sans-serif;font-size:8px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t4);font-weight:500;padding:0 8px 5px 0">Phase</th>'
      h += '<th style="text-align:center;font-family:Montserrat,sans-serif;font-size:8px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t4);font-weight:500;padding:0 6px 5px;white-space:nowrap">Billed</th>'
      h += '<th style="text-align:center;font-family:Montserrat,sans-serif;font-size:8px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t4);font-weight:500;padding:0 6px 5px;white-space:nowrap">Current</th>'
      h += '<th style="text-align:right;font-family:Montserrat,sans-serif;font-size:8px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t4);font-weight:500;padding:0 0 5px 6px;white-space:nowrap">Hours</th>'
      h += '</tr>'
      allPhaseData.forEach(function(ap) {
        var shortName = ap.name.replace(/^\d{2}-\d{2}-/, '')
        var pctColor = ap.advanced ? 'var(--grn)' : 'var(--t1)'
        var hrsColor = ap.hours > 0 ? 'var(--t1)' : 'var(--t4)'
        h += '<tr style="border-bottom:0.5px solid var(--b1)">'
        h += '<td style="padding:4px 8px 4px 0;color:var(--t2);max-width:130px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + shortName + '</td>'
        h += '<td style="padding:4px 6px;text-align:center;font-family:DM Mono,monospace;color:var(--t4);font-size:10px">' + (ap.contractedFee > 0 ? ap.billed + '%' : '-') + '</td>'
        h += '<td style="padding:4px 6px;text-align:center;font-family:DM Mono,monospace;font-weight:500;color:' + pctColor + ';font-size:10px">' + ap.current + '%'
        if (ap.advanced) h += ' <span style="color:var(--grn);font-size:9px">+</span>'
        h += '</td>'
        h += '<td style="padding:4px 0 4px 6px;text-align:right;font-family:DM Mono,monospace;font-weight:500;color:' + hrsColor + ';font-size:10px">' + (ap.hours > 0 ? ap.hours.toFixed(1) + 'h' : '-') + '</td>'
        h += '</tr>'
      })
      h += '</table>'
      if (notSent > 0) {
        h += '<div style="margin-top:7px;display:flex;align-items:center;gap:5px"><div style="width:6px;height:6px;border-radius:50%;background:var(--amb);flex-shrink:0"></div>'
        h += '<span style="color:var(--amb)">' + notSent + ' entr' + (notSent > 1 ? 'ies' : 'y') + ' approved but not yet sent to Core</span></div>'
      }
      var totalH = 0
      allPhaseData.forEach(function(ap) { totalH += ap.hours })
      if (totalH > 0) {
        h += '<div style="margin-top:6px;padding-top:6px;border-top:0.5px solid var(--b1);display:flex;justify-content:space-between;align-items:center">'
        h += '<span style="color:var(--t3)">Total billable hours</span>'
        h += '<span style="font-family:DM Mono,monospace;font-weight:500;color:var(--t1)">' + totalH.toFixed(1) + 'h</span></div>'
      }
    } else {
      h += '<div style="color:var(--t3)">No phase advancement or approved time entries found for this project in the billing period.</div>'
    }
    h += '</div>'
    // Recommend "Yes" whenever any phase shows billable hours this cycle or has advanced past what
    // was last billed — the same signal the table above highlights (green "+" / non-dash Hours).
    // Tracked as two separate flags (not just one combined boolean) so the override-confirm dialog
    // can name exactly which condition fired — hours, advancement, or both — instead of a vague
    // "and/or" that makes the human re-derive what the system already knows.
    var hasAdvanced = sig.hasAdvanced
    var hasHours = sig.hasHours
    state.newBillingInsight = { idx: idx, html: h, recommendYes: hasAdvanced || hasHours, hasAdvanced: hasAdvanced, hasHours: hasHours }

    // Open-invoice / aging check from Core (saves the manual invoice-list lookup). Root Core UID
    // comes off the phases we just loaded.
    var rootCoreUid = ''
    if (phases && phases[0]) { var rc = phases[0].fields['Core Project UID (from Project)']; rootCoreUid = Array.isArray(rc) ? (rc[0] || '') : (rc || '') }
    try {
      var invStatus = await B.getProjectInvoiceStatus(rootCoreUid)
      var projInvoices = await B.getProjectInvoices(rootCoreUid)
      // undefined = never managed to ask; null = asked, no link; string = the link. Keeping the
      // first two apart is what stops step 11 telling you to generate a link that already exists.
      var stmt = {}
      try { stmt = await B.getStatementPayLink(projId, state.run && state.run.id) } catch (e2) {}
      var stmtUrl = (stmt && Object.prototype.hasOwnProperty.call(stmt, 'statementPayUrl'))
        ? (stmt.statementPayUrl || null)
        : undefined
      state.openBillingInsight = { idx: idx, status: invStatus, invoices: projInvoices, rootCoreUid: rootCoreUid, projId: projId, statementPayUrl: stmtUrl }
    } catch (e) { state.openBillingInsight = { idx: idx, status: null, invoices: [], rootCoreUid: rootCoreUid, projId: projId, statementPayUrl: undefined } }

    if (state.selectedIdx === idx) {
      var panel = byId('aj-detail-panel')
      if (panel) panel.innerHTML = renderDetail(idx)
    }
  }

  // Money + relative-date formatters for the open-invoice insight.
  function fmtUsd(n) { return '$' + (Math.round((n || 0) * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
  function fmtDate(iso) { if (!iso) return ''; var d = new Date(/[T ]/.test(iso) ? iso : iso + 'T00:00:00'); return isNaN(d) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
  var AGING_LABEL = { '30 days': 'one cycle late', '60 days': 'two cycles late', '90+ days': 'three or more cycles late' }

  // Sequence check: regular (non-late-fee, non-draft) invoices, in date order oldest→newest, should
  // have strictly increasing trailing .NN numbers. Flag any whose number isn't higher than every older
  // one (catches a finalize that landed the wrong / out-of-order number, or a duplicate).
  function computeOutOfSeq(invs) {
    function seqOf(iv) { var p = String(iv.invoiceNumber || '').split('.'); return p.length > 1 ? parseInt(p[p.length - 1], 10) : NaN }
    function isLF(iv) { return iv.lateFee || /\bLF\b|-INT/i.test(iv.invoiceNumber || '') }
    var outOfSeq = {}
    var regulars = (invs || []).filter(function (iv) { return !isLF(iv) && !iv.draft && !isNaN(seqOf(iv)) && iv.date })
      .map(function (iv) { return { id: iv.id, seq: seqOf(iv), t: new Date(iv.date).getTime() } })
      .sort(function (a, b) { return a.t - b.t })   // oldest first
    var prevSeq = -Infinity
    regulars.forEach(function (r) { if (r.seq <= prevSeq) outOfSeq[r.id] = true; if (r.seq > prevSeq) prevSeq = r.seq })
    return { outOfSeq: outOfSeq, anyOut: Object.keys(outOfSeq).length > 0 }
  }
  // True if the currently-selected project's loaded invoices are out of sequence.
  function selectedInvoicesOutOfSeq() {
    var sel = state.openBillingInsight && state.openBillingInsight.idx === state.selectedIdx ? state.openBillingInsight : null
    if (!sel || !sel.invoices) return false
    return computeOutOfSeq(sel.invoices).anyOut
  }

  // Per-project invoice list with "Preview PDF" (Cortex-rendered, no Core export). Header carries a
  // Refresh that re-pulls from Core — after approving + finalizing a draft, refresh to confirm the
  // real invoice number came in (and the draft flipped to finalized).
  function invoicePreviewHtml() {
    var sel = state.openBillingInsight && state.openBillingInsight.idx === state.selectedIdx ? state.openBillingInsight : null
    if (!sel) return ''   // insight not loaded yet for this row — stay quiet
    var invs = sel.invoices || []
    var seqCheck = computeOutOfSeq(invs)
    var outOfSeq = seqCheck.outOfSeq, anyOut = seqCheck.anyOut
    // Core Invoice Review URL for this tenant — derive the host from the project's "Open in Core" link.
    var lif = (state.lineItems[state.selectedIdx] && state.lineItems[state.selectedIdx].fields) || {}
    var coreOpen = (lif['Open in Core (from Project)'] || [''])[0] || ''
    var coreBase = (coreOpen.indexOf('/webapp/') !== -1) ? coreOpen.split('/webapp/')[0] + '/webapp' : 'https://wap1-01.bqecore.com/webapp'
    var coreInvUrl = coreBase + '/InvoiceReview/InvoiceReview'

    var h = '<div style="margin-top:6px;background:var(--bg);border-radius:8px;padding:8px 10px;font-size:11px;color:var(--t2)">'
    h += '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">'
    h += '<span style="font-family:Montserrat,sans-serif;font-weight:500;font-size:9px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t3)">Invoice PDFs</span>'
    h += '<a href="#" data-action="refresh-invoice-list" style="font-size:10px;color:var(--blu);text-decoration:none" title="Re-pull invoices from Core">&#8635; Refresh</a>'
    h += '</div>'
    if (!invs.length) { h += '<div style="color:var(--t3);font-style:italic">No invoices found for this project in Core yet. Refresh after finalizing the draft.</div></div>'; return h }
    if (anyOut) h += '<div style="display:flex;align-items:center;gap:6px;background:var(--red-bg);border:0.5px solid rgba(192,57,43,0.3);border-radius:6px;padding:6px 8px;margin-bottom:6px;color:var(--red)"><span style="font-weight:600">&#9888;</span><span>Invoice numbering looks out of sequence. Fix the number in Core, then Refresh to confirm.</span></div>'
    invs.slice(0, 12).forEach(function (iv) {
      var bad = outOfSeq[iv.id]
      h += '<div style="display:flex;align-items:center;gap:8px;padding:3px 0' + (bad ? ';background:var(--red-bg);border-radius:4px;padding-left:6px;padding-right:6px' : '') + '">'
      if (iv.invoiceNumber) h += '<a href="#" data-action="open-core-invoice" data-num="' + escHtml(iv.invoiceNumber) + '" data-url="' + escHtml(coreInvUrl) + '" title="Open Core Invoice Review and copy this number to paste into search" style="font-family:DM Mono,monospace;min-width:64px;text-decoration:none;' + (bad ? 'color:var(--red);font-weight:600' : 'color:var(--blu)') + '">#' + escHtml(iv.invoiceNumber) + '</a>'
      else h += '<span style="font-family:DM Mono,monospace;min-width:64px">#—</span>'
      if (iv.draft) h += '<span style="font-size:9px;font-weight:600;color:var(--amb);background:var(--amb-bg);border-radius:10px;padding:1px 6px">Draft</span>'
      if (bad) h += '<span style="font-size:9px;font-weight:600;color:var(--red);background:var(--red-bg);border:0.5px solid rgba(192,57,43,0.3);border-radius:10px;padding:1px 6px">&#9888; out of sequence</span>'
      h += '<span style="color:var(--t3);flex:1">' + fmtDate(iv.date) + (iv.balance > 0 ? ' · open ' + fmtUsd(iv.balance) : '') + '</span>'
      h += '<button class="aj-btn aj-btn-sm" style="font-size:10px;padding:3px 8px" data-action="preview-invoice" data-invoice-id="' + iv.id + '">Preview PDF</button>'
      // "Data" (raw invoice field dump) hidden for now — a debugging aid, not needed day-to-day.
      // Left wired up (the invoice-data action handler + B.openInvoiceData are untouched) so it's a
      // one-line flip of SHOW_INVOICE_DATA_DEBUG_BUTTON below to bring it back if needed again.
      if (SHOW_INVOICE_DATA_DEBUG_BUTTON) h += '<button class="aj-btn aj-btn-sm" style="font-size:10px;padding:3px 6px;color:var(--t3)" data-action="invoice-data" data-invoice-id="' + iv.id + '" title="View the raw invoice data (fields we can pull)">Data</button>'
      h += '</div>'
    })
    h += '</div>'
    return h
  }

  // Compact OPEN-invoice list for the "Confirm payment received" step — the same account-wide
  // picture the reminder/statement email shows (number · date · balance), instead of a single
  // aggregated dollar figure the bookkeeper has to take on faith. Reuses the already-loaded
  // state.openBillingInsight (fetched once per project select, same source invoicePreviewHtml uses)
  // so this costs nothing extra. Silently empty when nothing's loaded yet or nothing's open.
  function openInvoiceListHtml() {
    var sel = state.openBillingInsight && state.openBillingInsight.idx === state.selectedIdx ? state.openBillingInsight : null
    if (!sel) return ''
    var open = (sel.invoices || []).filter(function (iv) { return !iv.draft && (iv.balance || 0) > 0.005 })
    if (!open.length) return ''
    open = open.slice().sort(function (a, b) { return new Date(b.date) - new Date(a.date) })   // newest first
    var total = 0
    open.forEach(function (iv) { total += (iv.balance || 0) })
    var h = '<div style="margin-bottom:8px;background:var(--bg);border-radius:8px;padding:8px 10px;font-size:11px;color:var(--t2)">'
    h += '<div style="font-family:Montserrat,sans-serif;font-weight:500;font-size:9px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t3);margin-bottom:4px">Open invoices in Core (' + open.length + ')</div>'
    open.forEach(function (iv) {
      h += '<div style="display:flex;align-items:center;gap:8px;padding:2px 0">'
      h += '<span style="font-family:DM Mono,monospace;min-width:70px">#' + escHtml(iv.invoiceNumber || '—') + '</span>'
      h += '<span style="color:var(--t3);flex:1">' + fmtDate(iv.date) + '</span>'
      h += '<span style="font-family:DM Mono,monospace;font-weight:500">' + fmtUsd(iv.balance) + '</span>'
      h += '</div>'
    })
    h += '<div style="display:flex;justify-content:space-between;margin-top:4px;padding-top:4px;border-top:0.5px solid var(--b1);font-weight:600">'
    h += '<span>Total open</span><span style="font-family:DM Mono,monospace">' + fmtUsd(total) + '</span></div>'
    h += '</div>'
    return h
  }

  // Statement pay-all link — its OWN step, shown only when the workflow calls for a statement
  // (Open Billing? = Yes). It can't be generated until AFTER the draft is approved + finalized in
  // Core (a freshly-finalized invoice needs to show up as an open Core invoice first), so this
  // lives after step 8. One click builds the pay link covering every currently-open Core invoice
  // for the project — the Client Hub then shows it as a "Pay full balance" button, opening our
  // own checkout page where they pick bank transfer or card, both at the same cost to them (the
  // card surcharge was dropped 2026-09-13) — and drops the per-invoice pay links. See
  // docs/STRIPE_PAYMENTS.md.
  function statementLinkHtml() {
    var sel = state.openBillingInsight && state.openBillingInsight.idx === state.selectedIdx ? state.openBillingInsight : null
    var projId = (sel && sel.projId) || ''
    // THREE states, not two. The insight for this row loads asynchronously, and until it lands there
    // was nothing to distinguish "this project has no pay link" from "we have not looked yet" — so
    // the step sat there inviting you to generate a link the project already had (Matt, 2026-09-14).
    // Nothing breaks if you take that invitation, since generating reuses an existing link rather
    // than minting a second one, but a step that looks undone when it is done is its own problem.
    var known = !!sel && sel.statementPayUrl !== undefined
    var payUrl = (sel && sel.statementPayUrl) || ''
    var h = '<div style="margin-top:6px">'
    if (!sel) {
      h += '<div style="font-size:11px;color:var(--t2);line-height:1.6">Checking whether this project already has a pay link…</div>'
    } else if (payUrl) {
      h += '<div style="font-size:11px;color:var(--t2);line-height:1.6;margin-bottom:6px">Pay link generated — the Client Hub shows <b>Pay full balance</b> using it.</div>'
      h += '<div style="display:flex;gap:6px;align-items:center"><a href="' + escHtml(payUrl) + '" target="_blank" rel="noopener noreferrer" style="flex:1;font-size:11px;color:var(--blu);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + escHtml(payUrl) + '</a></div>'
      h += '<button class="aj-btn aj-btn-sm" style="font-size:10px;padding:4px 8px;margin-top:6px" data-action="clear-statement-link" data-proj="' + projId + '">Clear</button>'
    } else {
      h += '<div style="font-size:11px;color:var(--t2);line-height:1.6;margin-bottom:6px">Build a pay link covering every open invoice on this account, so the client settles the whole balance (interest included) in one payment — they pick bank transfer or card on the payment page, and both cost them the same.</div>'
      // Asked and could not find out. Say so rather than implying a confident "there is none" — and
      // say that acting on it is safe, because the server reuses any link already captured for this run.
      if (!known) {
        h += '<div style="font-size:11px;color:var(--amb);line-height:1.6;margin-bottom:6px">Could not confirm whether this project already has a link. Generating is safe — it reuses the existing one if there is one.</div>'
      }
      h += '<button class="aj-btn aj-btn-p aj-btn-sm" style="font-size:10px;padding:4px 10px" data-action="generate-statement-link" data-proj="' + projId + '">Generate pay link</button>'
    }
    h += '</div>'
    return h
  }

  function escHtml(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') }
  function parseEmails(s) { return String(s || '').split(/[,;\s]+/).map(function (x) { return x.trim() }).filter(function (x) { return x.indexOf('@') !== -1 }) }
  // Standing team Bcc on billing emails — accounting keeps the record; editable per-send in the modal.
  var BILLING_BCC = 'accounting@alderjames.com, matt@alderjames.com, emily@alderjames.com, billing@alderjames.com'

  // ── Recipient pill fields (Gmail-style chips for To / Cc / Bcc) ──────────────────────
  var emailRecips = { to: [], cc: [], bcc: [] }   // committed addresses per field (the send source of truth)
  var activeRecipField = 'to'                      // which field the contact picker adds to (last focused)
  function validEmail(e) { return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e) }
  function addRecip(field, email) {
    var e = String(email || '').trim(); if (!e) return
    var arr = emailRecips[field] || (emailRecips[field] = [])
    if (arr.map(function (x) { return x.toLowerCase() }).indexOf(e.toLowerCase()) === -1) arr.push(e)
  }
  function pillsHtml(field) {
    return (emailRecips[field] || []).map(function (e) {
      var bad = !validEmail(e)
      return '<span style="display:inline-flex;align-items:center;gap:4px;background:' + (bad ? 'var(--red-bg)' : 'var(--surf)') + ';color:' + (bad ? 'var(--red)' : 'var(--t1)') + ';border:0.5px solid ' + (bad ? 'rgba(192,57,43,0.3)' : 'var(--b2)') + ';border-radius:12px;padding:2px 4px 2px 8px;font-size:11px;font-family:DM Mono,monospace;margin:2px 4px 2px 0"' + (bad ? ' title="This address looks invalid"' : '') + '>' + escHtml(e) + '<button data-action="remove-recip" data-field="' + field + '" data-email="' + escHtml(e) + '" style="border:none;background:none;color:' + (bad ? 'var(--red)' : 'var(--t4)') + ';cursor:pointer;font-size:13px;line-height:1;padding:0">&times;</button></span>'
    }).join('')
  }
  function recipFieldHtml(field) {
    return '<div class="aj-recip-box" data-field="' + field + '" style="display:flex;flex-wrap:wrap;align-items:center;width:100%;border:0.5px solid var(--b3);border-radius:4px;padding:2px 6px;box-sizing:border-box;min-height:30px;cursor:text">'
      + '<span class="aj-recip-pills" data-field="' + field + '">' + pillsHtml(field) + '</span>'
      + '<input data-recip-input data-field="' + field + '" type="text" autocomplete="off" placeholder="add…" style="flex:1;min-width:80px;border:none;outline:none;font-size:12px;font-family:DM Mono,monospace;padding:3px 2px;background:transparent;color:var(--t1)">'
      + '</div>'
  }
  function rerenderRecip(field) {
    var span = document.querySelector('#aj-email-modal .aj-recip-pills[data-field="' + field + '"]')
    if (span) span.innerHTML = pillsHtml(field)
  }
  function commitRecipInput(inp) {
    if (!inp) return
    var field = inp.getAttribute('data-field')
    var toks = String(inp.value || '').split(/[,;\s]+/).map(function (s) { return s.trim() }).filter(Boolean)
    if (!toks.length) { inp.value = ''; return }
    toks.forEach(function (t) { if (t.indexOf('@') !== -1) addRecip(field, t) })
    inp.value = ''
    rerenderRecip(field)
  }
  function commitAllRecipInputs() {
    var inps = document.querySelectorAll('#aj-email-modal [data-recip-input]')
    Array.prototype.forEach.call(inps, function (inp) { commitRecipInput(inp) })
  }

  // Best-effort client-side resolution of "every invoice number this cycle's email covers," for the
  // manual "Mark issued without sending" fallback only — no send happens there, so there's no server
  // response to trust (api/billing-send-invoice.js's attachList is the fully authoritative
  // equivalent the real-send path uses instead — see confirm-send-email below). Mirrors the server's
  // filter as closely as the client-cached invoice shape allows: no isRetainer flag is exposed here,
  // so that one exclusion can't be replicated client-side — acceptable since this is the less-
  // authoritative manual fallback, not the real send path.
  function resolveTrackedInvoiceNumbers(sel, mode) {
    var invs = (sel && sel.invoices) ? sel.invoices.filter(function (iv) { return !iv.draft }) : []
    invs = invs.slice().sort(function (a, b) { return (new Date(b.date)) - (new Date(a.date)) })
    if (mode === 'invoice') {
      var newest = invs[0]
      return newest ? (newest.invoiceNumber || '') : ''
    }
    function isLF(iv) { return iv.lateFee || /\bLF\b|-INT/i.test(iv.invoiceNumber || '') }
    function finalizedNumber(n) { return /\.\d/.test(String(n || '')) }
    var open = invs.filter(function (iv) { return !isLF(iv) && finalizedNumber(iv.invoiceNumber) && (Number(iv.balance) || 0) > 0.005 })
    return open.map(function (iv) { return iv.invoiceNumber }).filter(Boolean).join(', ')
  }

  /**
   * Which invoice are we sending? Resolved in ONE place so the button, the preview modal and the
   * reload path can never disagree about it.
   *
   * This used to be inlined in issueStepHtml only, which meant the id was frozen at the moment the
   * panel rendered. The firm invoice list is cached in memory for the whole browser session
   * (services/billing.js#_invCache — no expiry), so an invoice created in Core after the Billing
   * workspace was opened is invisible until someone reloads. On 2026-09-13 that composed an email
   * for 2419.17 — an already-paid invoice — when the intended one was 2419.18, newly raised in
   * Core. The client would have received a bill for the wrong invoice with no way to pay it.
   */
  function newestInvoiceFor(idx) {
    var sel = state.openBillingInsight && state.openBillingInsight.idx === idx ? state.openBillingInsight : null
    var list = (sel && sel.invoices) ? sel.invoices.filter(function (iv) {
      if (iv.draft) return false
      // A LATE FEE is never "the invoice being issued this cycle" — Core raises it on its own
      // schedule, so it is routinely the newest record on the account and this function picked it.
      // Sunday Goods (2026-09-14) opened its statement email with invoiceId pointing at
      // "2520.03 - LF-1", which is what the modal then displayed. On a plain INVOICE send the same
      // pick would have emailed the client a late-fee notice under "Please find attached your
      // invoice", and on a statement-new send it would have put the LF number in the subject line
      // and badged that line "new" on the statement.
      if (iv.lateFee) return false
      var n = String(iv.invoiceNumber || '').toUpperCase()
      return n.indexOf('-LF') === -1 && n.indexOf('-INT') === -1
    }) : []
    list = list.slice().sort(function (a, b) { return (new Date(b.date)) - (new Date(a.date)) })
    return list[0] || null
  }

  /**
   * Actually re-pull Core. There are TWO caches, and clearing one is not a refresh.
   *
   * resetCoreCaches() drops the browser copy, but the refetch that follows asks our server without
   * a force flag — so the 5-minute shared cache in api/_bqeCache.js answers with the same list it
   * already had. Every "refresh" built on reset-alone therefore ran, took time, and changed nothing.
   * That is what left step 9 insisting no finalized invoice existed straight after the bookkeeper
   * confirmed one did (Matt, 2026-09-14), and the same gap sat behind the email modal's
   * "Reload from Core" link.
   *
   * prefetchCoreInvoices(true) is the only call that reaches past both.
   */
  async function forceCoreRepull(idx) {
    try { B.resetCoreCaches() } catch (e) {}
    try { await B.prefetchCoreInvoices(true) } catch (e) {}
    try { await fetchNewBillingInsight(idx) } catch (e) {}
  }

  /**
   * Re-pull Core, then re-resolve. Always use this before opening the email preview: refreshing the
   * data without re-resolving the selection is how the existing "Reload from Core" link still
   * handed the old invoice id straight back to the modal.
   */
  async function refreshThenNewest(idx) {
    await forceCoreRepull(idx)
    return newestInvoiceFor(idx)
  }

  // The "Issue to client" step input — composes + (once enabled) sends the action-aware email with the
  // Core PDF attached. The newest finalized invoice in the insight is the one we attach + pay.
  function issueStepHtml(f) {
    var sel = state.openBillingInsight && state.openBillingInsight.idx === state.selectedIdx ? state.openBillingInsight : null
    // Five cases:
    //   invoice             — new billing this cycle, no prior balance
    //   statement           — prior balance only (chasing), under two periods
    //   statement-new       — new billing this cycle AND a prior balance, under two periods
    //   statement-pause     — prior balance, two+ periods past due (60+ days) → respectful work pause
    //   statement-new-pause — new billing this cycle AND a past-due prior balance (pause + new invoice)
    var aging = f['Past Due Billing Aging'] || ''
    var twoPlus = aging === '60 days' || aging === '90+ days'
    var openBilling = f['Open Billing?'] === 'Yes'
    var newBilling = f['New Billing?'] === 'Yes'
    var mode = openBilling
      ? (twoPlus
          ? (newBilling ? 'statement-new-pause' : 'statement-pause')
          : (newBilling ? 'statement-new' : 'statement'))
      : 'invoice'
    var newest = newestInvoiceFor(state.selectedIdx)
    var invId = newest ? newest.id : ''
    var invNum = newest ? (newest.invoiceNumber || '') : ''
    var h = ''
    // "No finalized invoice found" is a CLAIM about Core, and it must not be made before we have
    // looked. The insight for this row loads asynchronously, so until it lands newestInvoiceFor()
    // returns nothing for the same reason it would if Core were genuinely empty — and the step told
    // you to go finalize an invoice that already existed, for as long as the fetch took (Matt,
    // 2026-09-14; the third instance of this shape today, after step 11 and the Slack post).
    //
    // The button stays disabled either way — there is no invoice id to send — so nothing is lost by
    // being honest about which of the two situations you are in.
    if (!invId) {
      h += sel
        ? '<div style="font-size:11px;color:var(--amb);margin-bottom:8px">No finalized invoice found in Core for this project yet — finalize it first.</div>'
        : '<div style="font-size:11px;color:var(--t2);margin-bottom:8px">Checking Core for a finalized invoice…</div>'
    }
    var dataAttrs = ' data-idx="' + state.selectedIdx + '" data-inv="' + invId + '" data-num="' + escHtml(invNum) + '" data-mode="' + mode + '"'
    h += '<div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center">'
    h += '<button class="aj-btn aj-btn-p aj-btn-sm" data-action="open-email-modal"' + dataAttrs + (invId ? '' : ' disabled') + '>Preview &amp; send email</button>'
    var trackedNums = resolveTrackedInvoiceNumbers(sel, mode) || invNum
    h += '<a href="#" data-action="mark-issued-manual" data-idx="' + state.selectedIdx + '" data-num="' + escHtml(trackedNums) + '" style="font-size:10px;color:var(--t4);text-decoration:underline">Mark issued without sending</a>'
    h += '</div>'
    return h
  }

  // Email preview/send modal — composes via the server (send:false), shows To/Subject/attachment +
  // rendered body, and sends (send:true) on confirm, then marks issued + posted.
  // isReminder reuses this exact compose UI (recipient chips, salutation, HTML preview, attachments,
  // test send) for the payment-followup reminder — see showFollowupReminderModal. Only the send
  // button's label/action-flag, the confirm/success copy (in confirm-send-email), and the "reload
  // from Core" affordance (not meaningful here — the invoice was independently resolved via
  // resolveFollowupInvoiceRef, not this cycle's own openBillingInsight) differ.
  async function openEmailPreviewModal(idx, invId, invNum, mode, fresh, isReminder, repull) {
    var item = state.lineItems[idx]; var f = item.fields
    var projId = (f['Project'] || [])[0]
    var sel = state.openBillingInsight && state.openBillingInsight.idx === idx ? state.openBillingInsight : null
    var payUrl = mode !== 'invoice' ? ((sel && sel.statementPayUrl) || '') : ''
    var existing = byId('aj-email-modal'); if (existing) existing.parentNode.removeChild(existing)
    var el = document.createElement('div'); el.id = 'aj-email-modal'; el.className = 'aj-modal-overlay'
    var titleBase = mode === 'invoice' ? 'Invoice' : (mode === 'statement-new' || mode === 'statement-new-pause') ? 'Invoice + statement' : 'Statement'
    var reminderAttr = isReminder ? ' data-reminder="true"' : ''
    el.innerHTML = '<div class="aj-modal" style="width:660px;max-width:95vw">'
      + '<div class="aj-modal-head"><div class="aj-modal-title">' + (isReminder ? 'Reminder — ' : '') + titleBase + ' email</div>'
      + '<button class="aj-modal-close" data-action="close-email-modal">x</button></div>'
      + '<div id="aj-email-body" style="padding:0.875rem 1rem;overflow-y:auto;flex:1"><div style="font-size:12px;color:var(--t3)">Composing…</div></div>'
      + '<div class="aj-modal-footer"><span class="aj-modal-status" id="aj-email-status"></span>'
      + '<button class="aj-btn" data-action="close-email-modal">Close</button>'
      + '<button class="aj-btn aj-btn-p" id="aj-email-send-btn" data-action="confirm-send-email" data-idx="' + idx + '" data-inv="' + invId + '" data-num="' + escHtml(invNum) + '" data-mode="' + mode + '"' + reminderAttr + ' disabled>' + (isReminder ? 'Send reminder' : 'Send &amp; mark posted') + '</button>'
      + '</div></div>'
    root.appendChild(el); document.body.style.overflow = 'hidden'

    // The Core re-pull happens HERE, inside the open modal — not in the click handler before it.
    // It was moved out on 2026-09-13 to stop a stale invoice being composed, which was right, but it
    // left the button dead for the length of a full firm-invoice pull with nothing on screen; forcing
    // past the server cache (2026-09-14) made that wait longer still. The work is the same, the
    // waiting is now visibly attached to something (Matt: "tends to hang for a long while").
    if (repull && !isReminder) {
      var b0 = byId('aj-email-body')
      if (b0) b0.innerHTML = '<div style="font-size:12px;color:var(--t3)">Re-checking Core for the latest invoice…</div>'
      var fresh0 = await refreshThenNewest(idx)
      if (fresh0) { invId = fresh0.id; invNum = fresh0.invoiceNumber || invNum }
      // The send button carries the id it will send — it must follow the re-resolved invoice.
      var sb0 = byId('aj-email-send-btn')
      if (sb0) { sb0.setAttribute('data-inv', invId); sb0.setAttribute('data-num', invNum) }
      sel = state.openBillingInsight && state.openBillingInsight.idx === idx ? state.openBillingInsight : null
      payUrl = mode !== 'invoice' ? ((sel && sel.statementPayUrl) || '') : ''
      var b1 = byId('aj-email-body')
      if (!b1) return   // closed while we were waiting
      b1.innerHTML = '<div style="font-size:12px;color:var(--t3)">Composing…</div>'
    }

    var resp = await B.sendInvoiceEmail({ projectId: projId, invoiceId: invId, mode: mode, invoiceNumber: invNum, payUrl: payUrl, isReminder: !!isReminder, send: false, fresh: !!fresh })
    var wrap = byId('aj-email-body'); if (!wrap) return
    if (!resp || resp.error) { wrap.innerHTML = '<div style="font-size:12px;color:var(--red)">Couldn\'t compose the email: ' + escHtml((resp && resp.error) || 'error') + '</div>'; return }
    var atts = resp.attachments || (resp.attachment ? [resp.attachment] : [])
    // Initialize the recipient chips: To from the resolved billing contact, Bcc with the standing team
    // copy, Cc empty. These arrays are the source of truth on send.
    emailRecips = { to: parseEmails(resp.to), cc: [], bcc: parseEmails(BILLING_BCC) }
    activeRecipField = 'to'
    var hh = ''
    var lbl = 'font-size:10px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t4);margin-bottom:2px'
    // Recipients as Gmail-style pills — committed (valid) addresses show as chips; typing + Enter/comma
    // commits; × removes. "Add from contacts" and the picker drop into the last-focused field.
    hh += '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:2px"><span style="' + lbl + '">To</span><a href="#" data-action="open-contacts" style="font-size:10px;color:var(--blu);text-decoration:none">Open contacts &#8599;</a></div>'
    hh += recipFieldHtml('to')
    hh += '<div style="position:relative;margin:6px 0 8px">'
    hh += '<input id="aj-contact-search" type="text" placeholder="&#43; Add from contacts — type a name or email" autocomplete="off" style="width:100%;font-size:12px;padding:5px 8px;border:0.5px solid var(--b3);background:var(--wh);color:var(--t1);border-radius:4px;font-family:DM Sans,sans-serif;box-sizing:border-box">'
    hh += '<div id="aj-contact-results" style="display:none;position:absolute;left:0;right:0;top:100%;z-index:6;background:var(--wh);border:0.5px solid var(--b3);border-radius:4px;max-height:170px;overflow-y:auto;box-shadow:0 6px 18px rgba(0,0,0,0.25)"></div>'
    hh += '</div>'
    hh += '<div style="display:flex;gap:8px;margin-bottom:10px">'
    hh += '<div style="flex:1"><div style="' + lbl + '">Cc</div>' + recipFieldHtml('cc') + '</div>'
    hh += '<div style="flex:1"><div style="' + lbl + '">Bcc</div>' + recipFieldHtml('bcc') + '</div>'
    hh += '</div>'
    hh += '<div style="' + lbl + '">Subject</div><div style="font-size:13px;color:var(--t1);margin-bottom:10px">' + escHtml(resp.subject || '') + '</div>'
    hh += '<div style="' + lbl + '">Salutation (greeting)</div><input id="aj-email-salutation" type="text" value="' + escHtml(resp.salutation || '') + '" placeholder="e.g. Cathey (blank = “Hello”)" style="width:100%;font-size:13px;padding:5px 8px;border:0.5px solid var(--b3);background:var(--wh);color:var(--t1);border-radius:4px;font-family:DM Sans,sans-serif;box-sizing:border-box;margin-bottom:10px">'
    // Optional one-off note for this send only — never stored, rendered as its own paragraph between
    // the lead/invoice-list context and the payment ask (see compose() in api/billing-send-invoice.js).
    hh += '<div style="' + lbl + '">Custom message (optional, this email only)</div><textarea id="aj-email-custom-msg" rows="3" placeholder="Add a note for this send only — e.g. a condolence, a payment-timeline accommodation…" style="width:100%;font-size:13px;padding:6px 8px;border:0.5px solid var(--b3);background:var(--wh);color:var(--t1);border-radius:4px;font-family:DM Sans,sans-serif;box-sizing:border-box;margin-bottom:10px;resize:vertical"></textarea>'
    // The body always reads "you can view and pay it using the link below". When no link resolved,
    // that sentence points at nothing — so say so here rather than leaving it to be spotted in the
    // preview pane. The server refuses the send outright as well; this exists so it is caught first.
    if (resp.missingPayLink) {
      hh += '<div style="border:0.5px solid var(--red);background:var(--red-bg);border-radius:4px;padding:8px 10px;margin-bottom:10px">'
        + '<div style="font-size:12px;font-weight:600;color:var(--red);margin-bottom:2px">No payment link resolved</div>'
        + '<div style="font-size:11px;color:var(--t1);line-height:1.6">This email tells the client to pay using a link it does not contain. Try Reload from Core below — sending is blocked until one resolves.</div></div>'
    }
    var attHtml = atts.length
      ? atts.map(function (a) { return '&#128206; ' + escHtml(a.filename) + (a.sizeKB ? ' <span style="color:var(--t3)">(' + a.sizeKB + ' KB)</span>' : '') }).join('<br>')
      : '<span style="color:var(--amb)">No invoice PDF to attach.</span>'
    hh += '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:2px"><span style="font-size:10px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t4)">Attachment' + (atts.length > 1 ? 's (' + atts.length + ')' : '') + '</span>'
      + (isReminder ? '' : '<a href="#" data-action="reload-email-modal" data-idx="' + idx + '" data-inv="' + invId + '" data-num="' + escHtml(invNum) + '" data-mode="' + mode + '" style="font-size:10px;color:var(--blu);text-decoration:none" title="Re-fetch invoices from Core">&#8635; Reload from Core</a>') + '</div>'
      + '<div style="font-size:12px;color:var(--t1);margin-bottom:10px;line-height:1.7">' + attHtml + '</div>'
    // Name the invoice being sent, with its date and money. Tonight's wrong-invoice send was caught
    // by eye against Core; it should be catchable without leaving this screen.
    //
    // Looked up by the id this modal was actually opened with — NOT re-derived as "the newest".
    // A reminder deliberately targets a specific overdue invoice (resolveFollowupInvoiceRef), so
    // re-deriving here would confidently display the wrong number on exactly the screen whose job
    // is to let you catch the wrong number.
    var selInv = null
    try {
      var selIns = state.openBillingInsight && state.openBillingInsight.idx === idx ? state.openBillingInsight : null
      var selList = (selIns && selIns.invoices) ? selIns.invoices : []
      for (var si = 0; si < selList.length; si++) { if (selList[si].id === invId) { selInv = selList[si]; break } }
      if (!selInv && invNum) selInv = { invoiceNumber: invNum }
    } catch (e) {}
    // A statement covers the whole account, so naming ONE invoice here was answering a question
    // nobody asked and inviting the reader to think that single invoice was the subject of the
    // send. The attachment list directly above already names every document going out, which is
    // the check this line exists to support. No derived totals: the statement PDF is the only
    // place that number should be computed, and a second computation here could disagree with it.
    var isStmtMode = mode && mode !== 'invoice'
    if (isStmtMode) {
      hh += '<div style="font-size:10px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t4);margin-bottom:4px">Sending</div>'
      hh += '<div style="font-size:12px;color:var(--t1);margin-bottom:10px;font-family:DM Mono,monospace">Statement of account <span style="color:var(--t3)">· covers the full open balance</span></div>'
    } else if (selInv) {
      var amtTxt = selInv.balance != null && selInv.balance !== undefined
        ? ('$' + Number(selInv.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' due')
        : ''
      hh += '<div style="font-size:10px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t4);margin-bottom:4px">Sending</div>'
      hh += '<div style="font-size:12px;color:var(--t1);margin-bottom:10px;font-family:DM Mono,monospace">Invoice ' + escHtml(selInv.invoiceNumber || '') 
        + (selInv.date ? ' <span style="color:var(--t3)">· ' + escHtml(String(selInv.date).slice(0, 10)) + '</span>' : '')
        + (amtTxt ? ' <span style="color:var(--t3)">· ' + escHtml(amtTxt) + '</span>' : '') + '</div>'
    }
    hh += '<div style="font-size:10px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t4);margin-bottom:4px">Message</div>'
    hh += '<iframe style="width:100%;height:300px;border:0.5px solid var(--b2);border-radius:6px;background:#fff" srcdoc="' + escHtml(resp.html || '') + '"></iframe>'
    if (!resp.enabled) hh += '<div style="margin-top:8px;font-size:11px;color:var(--amb);background:var(--amb-bg);border-radius:6px;padding:7px 9px;line-height:1.5">Email sending isn\'t switched on yet. Review the draft above. Once we verify the sending domain in Resend and set <b>BILLING_EMAIL_SEND_ENABLED</b>, "Send" will ' + (isReminder ? 'email the client and mark this reminder sent.' : 'email the client and mark this issued + posted. For now, use “Mark issued without sending” to proceed.') + '</div>'
    // Test send — emails the chosen address (defaults to you) and does NOT mark the invoice issued/posted/reminded.
    var meEmail = ''
    try { meEmail = (window.Clerk && window.Clerk.user && window.Clerk.user.primaryEmailAddress && window.Clerk.user.primaryEmailAddress.emailAddress) || '' } catch (e) {}
    hh += '<div style="margin-top:10px;border-top:0.5px solid var(--b1);padding-top:10px">'
    hh += '<div style="font-size:10px;letter-spacing:0.06em;text-transform:uppercase;color:var(--t4);margin-bottom:4px">Send a test (does not ' + (isReminder ? 'mark the reminder sent' : 'post the invoice') + ')</div>'
    hh += '<div style="display:flex;gap:6px;align-items:center">'
    hh += '<input id="aj-test-email" type="email" value="' + escHtml(meEmail) + '" placeholder="you@alderjames.com" style="flex:1;font-size:12px;padding:5px 8px;border:0.5px solid var(--b3);background:var(--wh);color:var(--t1);border-radius:4px;font-family:DM Mono,monospace">'
    hh += '<button class="aj-btn aj-btn-sm" data-action="send-test-email" data-idx="' + idx + '" data-inv="' + invId + '" data-num="' + escHtml(invNum) + '" data-mode="' + mode + '"' + reminderAttr + '>Send test</button>'
    hh += '</div></div>'
    wrap.innerHTML = hh
    var sb = byId('aj-email-send-btn'); if (sb) sb.disabled = false

    // Live contact search → results list (clicking a row appends to To via the pick-contact action).
    var csInput = byId('aj-contact-search')
    if (csInput) {
      var csTimer = null
      csInput.addEventListener('input', function () {
        var q = (csInput.value || '').trim()
        var rdiv = byId('aj-contact-results')
        if (csTimer) clearTimeout(csTimer)
        if (!rdiv) return
        if (q.length < 2) { rdiv.style.display = 'none'; rdiv.innerHTML = ''; return }
        csTimer = setTimeout(function () {
          searchContacts(q).then(function (res) {
            var rd = byId('aj-contact-results'); if (!rd) return
            var list = ((res && res.contacts) || []).filter(function (c) { return c.fields && c.fields['Email'] })
            if (!list.length) { rd.innerHTML = '<div style="padding:6px 8px;font-size:11px;color:var(--t4)">No contacts with an email matched.</div>'; rd.style.display = 'block'; return }
            rd.innerHTML = list.slice(0, 8).map(function (c) {
              var nm = ((c.fields['First Name'] || '') + ' ' + (c.fields['Last Name'] || '')).trim() || c.fields['Email']
              var em = c.fields['Email']
              return '<div data-action="pick-contact" data-email="' + escHtml(em) + '" style="padding:6px 8px;font-size:12px;cursor:pointer;border-bottom:0.5px solid var(--b1)"><span style="color:var(--t1)">' + escHtml(nm) + '</span> <span style="color:var(--t3);font-family:DM Mono,monospace">' + escHtml(em) + '</span></div>'
            }).join('')
            rd.style.display = 'block'
          }).catch(function () {})
        }, 250)
      })
    }

    // Pill-field inputs: Enter/comma/semicolon (or blur) commits; Backspace on empty removes the last
    // chip; focusing a field makes it the target for the contact picker. Clicking the box focuses input.
    var recipInputs = wrap.querySelectorAll('[data-recip-input]')
    Array.prototype.forEach.call(recipInputs, function (inp) {
      var fld = inp.getAttribute('data-field')
      inp.addEventListener('focus', function () { activeRecipField = fld })
      inp.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter' || ev.key === ',' || ev.key === ';') { ev.preventDefault(); commitRecipInput(inp) }
        else if (ev.key === 'Backspace' && !inp.value && (emailRecips[fld] || []).length) { ev.preventDefault(); emailRecips[fld].pop(); rerenderRecip(fld) }
      })
      inp.addEventListener('blur', function () { commitRecipInput(inp) })
    })
    Array.prototype.forEach.call(wrap.querySelectorAll('.aj-recip-box'), function (box) {
      box.addEventListener('mousedown', function (ev) { if (ev.target === box || ev.target.classList.contains('aj-recip-pills')) { var i = box.querySelector('[data-recip-input]'); if (i) { ev.preventDefault(); i.focus() } } })
    })

    // Salutation + custom message: editable, re-compose the message body server-side on change and
    // swap just the iframe (leaves recipients/attachments untouched). Sends use the live input values.
    var salInput = byId('aj-email-salutation')
    var customMsgInput = byId('aj-email-custom-msg')
    var refreshPreview = function () {
      B.sendInvoiceEmail({ projectId: projId, invoiceId: invId, mode: mode, invoiceNumber: invNum, payUrl: payUrl, salutation: salInput ? salInput.value : undefined, customMessage: customMsgInput ? customMsgInput.value : undefined, send: false })
        .then(function (r) { if (r && !r.error) { var ifr = wrap.querySelector('iframe'); if (ifr) ifr.setAttribute('srcdoc', r.html || '') } })
        .catch(function () {})
    }
    if (salInput) {
      salInput.addEventListener('blur', refreshPreview)
      salInput.addEventListener('keydown', function (ev) { if (ev.key === 'Enter') { ev.preventDefault(); refreshPreview() } })
    }
    if (customMsgInput) customMsgInput.addEventListener('blur', refreshPreview)
  }

  // Lock the email modal while a send is in flight — block every control (send, close, X, test,
  // recipient edits, reload) so nothing can be changed or double-sent mid-request.
  function setEmailBusy(busy, label) {
    var modal = byId('aj-email-modal'); if (!modal) return
    var panel = modal.querySelector('.aj-modal')
    if (panel) { panel.style.pointerEvents = busy ? 'none' : ''; panel.style.opacity = busy ? '0.6' : '' }
    var sendBtn = byId('aj-email-send-btn'); if (sendBtn) sendBtn.disabled = !!busy
    var st = byId('aj-email-status'); if (st && label != null) st.textContent = label
  }

  // Mark a line item issued + posted in one write (the email-send success path collapses both).
  // After a send: mark the line item ISSUED only. Posting to BQE Core's ledger happens in Core (it's
  // tied to approval there, not a flag we flip), so the run's final "Mark posted in Core" step is a
  // manual confirmation the bookkeeper sets after posting it in Core.
  async function markIssued(idx, invoiceNumber) {
    var item = state.lineItems[idx]
    showSaving(true)
    var fields = { 'Send Invoice or Statement': true }
    // A statement cycle can cover more than one open invoice — accept either a single number or an
    // array (joined into the same comma-separated shape the payment-followup steps below parse).
    if (Array.isArray(invoiceNumber)) invoiceNumber = invoiceNumber.filter(Boolean).join(', ')
    // Persist the invoice number(s) this cycle resolved — the payment-followup steps below need it
    // to look these specific invoices back up in Core later. Previously never written by this app
    // (only populated on older, pre-port records via the retired Softr tool).
    if (invoiceNumber) fields['Invoice Number'] = invoiceNumber
    var updated = null
    try { updated = await B.updateLineItem(item.id, fields) }
    catch (e) { updated = { error: (e && e.message) || 'update failed' } }
    showSaving(false)
    if (updated && !updated.error) {
      mergeUpdated(idx, updated)
      item.fields['Send Invoice or Statement'] = true
      if (invoiceNumber) item.fields['Invoice Number'] = invoiceNumber
      var p = byId('aj-detail-panel'); if (p) p.innerHTML = renderDetail(idx); updateListRow(idx)
      return { ok: true }
    }
    return { ok: false, error: (updated && updated.error) || 'unknown error' }
  }

  // ── Follow-up reminder modal (post-posting "still unpaid" required step) ──────────────────────
  // Derives `mode` the same way issueStepHtml already does for the original issue email, so a
  // follow-up on an account that was actually billed as a statement (with pause language) doesn't
  // regress to a bare single-invoice email.
  function followupModeFor(f) {
    var aging = f['Past Due Billing Aging'] || ''
    var twoPlus = aging === '60 days' || aging === '90+ days'
    var openBilling = f['Open Billing?'] === 'Yes'
    var newBilling = f['New Billing?'] === 'Yes'
    return openBilling ? (twoPlus ? (newBilling ? 'statement-new-pause' : 'statement-pause') : (newBilling ? 'statement-new' : 'statement')) : 'invoice'
  }

  // Invoice Number can track more than one open invoice for a statement cycle (comma-separated,
  // newest first by construction) — the reminder email's compose modes name ONE invoice in the
  // subject/lead text ("your new invoice #X"), so always take the first entry there, while the
  // payment-check logic elsewhere verifies the full tracked set regardless of which one gets named.
  function firstInvoiceNumber(f) {
    return String(f['Invoice Number'] || '').split(',')[0].trim()
  }

  // Which invoice a reminder attaches to. Normally the already-matched fu.id from
  // state.followupStatus — but that's only populated when the line item HAS an Invoice Number on
  // file, which is exactly NOT the case for the states that most need a reminder (`unknown`, and
  // its manual-unpaid override): Core couldn't be matched automatically in the first place. Falls
  // back to independently resolving the project's own oldest open Core invoice
  // (B.getFallbackInvoiceForProject) so the reminder still has something real to attach — cached
  // per line item so repeat opens (preview, then Send) don't re-resolve. Returns null if genuinely
  // nothing can be found (no Core UID on file, or no open invoice matches).
  function resolveFollowupInvoiceRef(item) {
    var f = item.fields
    var fu = state.followupStatus[item.id] || {}
    if (fu.id) return Promise.resolve({ id: fu.id, invoiceNumber: firstInvoiceNumber(f) })
    state.followupInvoiceFallback = state.followupInvoiceFallback || {}
    if (state.followupInvoiceFallback.hasOwnProperty(item.id)) return Promise.resolve(state.followupInvoiceFallback[item.id])
    return B.getFallbackInvoiceForProject((f['Project'] || [])[0]).then(function (resolved) {
      state.followupInvoiceFallback[item.id] = resolved
      return resolved
    }).catch(function () { return null })
  }

  // A reminder is composed and sent through the EXACT SAME rich modal as the original invoice/
  // statement email (recipient chips, salutation editing, HTML preview, attachment list, test
  // send) — see openEmailPreviewModal's `isReminder` branch — rather than a separate, thinner
  // preview. Only the invoice-resolution step differs: a reminder can't rely on
  // state.openBillingInsight (that's this CYCLE's own new/open invoices); it needs the invoice the
  // manual-unpaid override actually refers to, which is resolved via resolveFollowupInvoiceRef.
  function showFollowupReminderModal(idx) {
    var item = state.lineItems[idx]; var f = item.fields
    var mode = followupModeFor(f)
    resolveFollowupInvoiceRef(item).then(function (ref) {
      if (!ref || !ref.id) {
        try { alertDialog({ title: 'No invoice found', message: 'Couldn\'t find an open invoice in Core for this project to attach the reminder to. Check Core directly, or add the correct number to this line item\'s Invoice Number field in Airtable, then try again.' }) } catch (e) {}
        return
      }
      openEmailPreviewModal(idx, ref.id, ref.invoiceNumber || '', mode, false, true)
    }).catch(function () {
      try { alertDialog({ title: 'Couldn\'t compose the reminder', message: 'Something went wrong resolving the invoice for this reminder. Try again.' }) } catch (e) {}
    })
  }

  // The Core open-invoice insight block shown in the "Open billing?" step (+ invoice PDF previews).
  function openBillingInsightHtml() {
    var ins = state.openBillingInsight && state.openBillingInsight.idx === state.selectedIdx ? state.openBillingInsight.status : undefined
    var ar = ''
    if (ins === undefined) ar = '<div style="background:var(--bg);border-radius:8px;padding:8px 10px;margin-bottom:6px;font-size:11px;color:var(--t3);font-style:italic">Checking Core for open invoices…</div>'
    else if (!ins || ins.unknown) ar = ''
    else if (ins.openCount === 0) ar = '<div style="background:var(--grn-bg);border-radius:8px;padding:8px 10px;margin-bottom:6px;font-size:11px;color:var(--grn);display:flex;align-items:center;gap:6px"><span style="font-weight:600">✓</span><span>Core: no open invoices for this project.</span></div>'
    else {
      var interest = ins.suggestion === '60 days' || ins.suggestion === '90+ days'
      var accent = interest ? 'var(--amb)' : 'var(--blu)'
      var bg = interest ? 'var(--amb-bg)' : 'var(--blu-bg)'
      ar = '<div style="background:' + bg + ';border-radius:8px;padding:8px 10px;margin-bottom:6px;font-size:11px;color:var(--t2);line-height:1.6">'
        + '<div><b style="color:' + accent + '">Core: ' + ins.openCount + ' open invoice' + (ins.openCount > 1 ? 's' : '') + '</b> totaling <b style="font-family:DM Mono,monospace">' + fmtUsd(ins.openTotal) + '</b>.</div>'
        + (ins.oldestDate ? '<div style="margin-top:2px">Oldest: ' + (ins.oldestNumber ? '#' + ins.oldestNumber + ' ' : '') + 'issued ' + fmtDate(ins.oldestDate) + ' &mdash; <b>' + ins.daysOutstanding + ' days</b> outstanding (suggests <b style="color:' + accent + '">' + (AGING_LABEL[ins.suggestion] || '') + '</b>).</div>' : '')
        + '</div>'
    }
    return ar + invoicePreviewHtml()
  }

  async function stepBack(idx, fieldName) {
    var item = state.lineItems[idx]
    if (!fieldName) return
    // Payment-check sentinel — undoing it clears the checkbox, re-exposing the live Core status.
    if (fieldName === '__payment-check__') {
      showSaving(true)
      var updatedPc = await B.updateLineItem(item.id, { 'Payment Status Checked': null })
      showSaving(false)
      if (updatedPc && !updatedPc.error) { mergeUpdated(idx, updatedPc); item.fields['Payment Status Checked'] = false }
      var panelPc = byId('aj-detail-panel'); if (panelPc) panelPc.innerHTML = renderDetail(idx); updateListRow(idx)
      return
    }
    // Statement pay-all link isn't an Airtable field — undoing it clears the captured Core link.
    if (fieldName === '__statement-link__') {
      var slProj = (item.fields['Project'] || [])[0]
      showSaving(true)
      try { if (slProj) await B.clearStatementPayLink(slProj, state.run && state.run.id) } catch (e) {}
      showSaving(false)
      if (state.openBillingInsight && state.openBillingInsight.idx === idx) { state.openBillingInsight.statementPayUrl = null }
      var panel0 = byId('aj-detail-panel'); if (panel0) panel0.innerHTML = renderDetail(idx); updateListRow(idx)
      return
    }
    showSaving(true)
    var fields = {}
    fields[fieldName] = null
    if (fieldName === 'New Billing?' || fieldName === 'Open Billing?') fields['Skip Invoicing?'] = null
    if (fieldName === 'PM Confirm Submit') fields['PM Setup Completed By'] = []
    var updated = await B.updateLineItem(item.id, fields)
    showSaving(false)
    if (updated && !updated.error) {
      mergeUpdated(idx, updated)
      state.lineItems[idx].fields[fieldName] = null
      var panel = byId('aj-detail-panel')
      if (panel) panel.innerHTML = renderDetail(idx)
      updateListRow(idx)
    }
  }

  function showDeleteLiModal(idx) {
    var existing = byId('aj-delete-li-modal')
    if (existing) existing.parentNode.removeChild(existing)
    var item = state.lineItems[idx]
    var projName = (item.fields['Project Name (from Project)'] || ['Unknown'])[0]
    var projNum = (item.fields['Project Number'] || [''])[0]
    var el = document.createElement('div')
    el.id = 'aj-delete-li-modal'; el.className = 'aj-modal-overlay'; el.style.zIndex = '10000'
    el.innerHTML = '<div class="aj-modal" style="width:420px;max-width:94vw">'
      + '<div class="aj-modal-head" style="background:#C0392B"><div class="aj-modal-title">Remove from billing run</div></div>'
      + '<div style="padding:1.25rem 1.25rem 1rem">'
      + '<div style="font-size:13px;font-weight:500;color:var(--t1);margin-bottom:8px">' + projName + '</div>'
      + '<div style="font-size:11px;font-family:DM Mono,monospace;color:var(--t4);margin-bottom:12px">' + projNum + '</div>'
      + '<div style="font-size:12px;color:var(--t2);line-height:1.7;margin-bottom:12px">This will permanently delete this line item from the billing run. The project record will not be affected.</div>'
      + '<div style="background:var(--red-bg);border-left:3px solid var(--red);border-radius:4px;padding:8px 10px;font-size:11px;color:var(--red)">This action cannot be undone.</div>'
      + '</div>'
      + '<div class="aj-modal-footer">'
      + '<span class="aj-modal-status" id="aj-delete-li-status"></span>'
      + '<button class="aj-btn" data-action="close-delete-li-modal">Cancel</button>'
      + '<button class="aj-btn" style="border-color:var(--red);color:var(--red)" id="aj-delete-li-confirm-btn" data-action="confirm-delete-li" data-idx="' + idx + '">Remove project</button>'
      + '</div></div>'
    root.appendChild(el)
  }

  async function executeDeleteLi(idx) {
    var item = state.lineItems[idx]
    var statusEl = byId('aj-delete-li-status')
    var confirmBtn = byId('aj-delete-li-confirm-btn')
    if (statusEl) statusEl.textContent = 'Deleting...'
    if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.style.opacity = '0.4' }
    var resp = await B.deleteLineItem(item.id)
    if (resp && (resp.deleted || resp.id)) {
      var modal = byId('aj-delete-li-modal')
      if (modal) modal.parentNode.removeChild(modal)
      state.lineItems.splice(idx, 1)
      if (state.selectedIdx === idx) state.selectedIdx = -1
      else if (state.selectedIdx > idx) state.selectedIdx--
      render()
    } else {
      if (statusEl) statusEl.textContent = 'Error - try again'
      if (confirmBtn) { confirmBtn.disabled = false; confirmBtn.style.opacity = '1' }
    }
  }

  function billingProjectLabel(f) {
    var num = (f['Project Number'] || [''])[0] || ''
    var name = (f['Project Name (from Project)'] || [''])[0] || ''
    return (num ? num + ' — ' : '') + name
  }

  // Sync a newly-established (or newly-loaded) thread ts into this line item's in-memory state and
  // repaint whatever's currently showing it — shared by both the manual chat-bubble path (via the
  // global 'slack-thread-started' event, dispatched from GlobalSlackPanel.jsx once a thread starts
  // anywhere in the app) and the auto-create path below (which already has the item in hand, no
  // event round-trip needed).
  function syncThreadTs(lineItemId, ts) {
    for (var bi = 0; bi < state.lineItems.length; bi++) {
      if (state.lineItems[bi].id === lineItemId) {
        state.lineItems[bi].fields['Slack Thread Timestamp'] = ts
        updateListRow(bi)
        if (state.selectedIdx === bi) { var p = byId('aj-detail-panel'); if (p) p.innerHTML = renderDetail(bi) }
        break
      }
    }
  }

  // Most billing events (Approve) only ever REPLY into a thread a human already deliberately
  // started — see the chat-bubble in renderDetail, wired through the app's existing global Slack
  // panel (GlobalSlackPanel.jsx / NotificationBell.jsx), same mechanism Milestones/Sheets/BIM use.
  // Sending a draft BACK for corrections is the one exception (createIfMissing=true, below): that
  // note has to land somewhere or it's stuck going nowhere, so it starts a thread itself if none
  // exists yet, rather than silently vanishing into 'Draft Review Comments' with nobody notified.
  // Best-effort throughout — a Slack hiccup must never block or delay the billing action itself.
  async function postBillingSlackUpdate(item, message, createIfMissing) {
    try {
      var projectId = (item.fields['Project'] || [])[0]
      if (!projectId) return
      var token = null
      try { token = await window.__clerkGetToken() } catch (e) {}
      var authorName = (window.logged_in_user && window.logged_in_user.name) || 'Billing'
      var existingTs = item.fields['Slack Thread Timestamp']
      if (!existingTs) {
        if (!createIfMissing) return
        var res = await startDiscussion({
          entityId: item.id, entityType: 'billing_line_item',
          entityName: billingProjectLabel(item.fields), projectId: projectId,
          projectName: (item.fields['Project Name (from Project)'] || [''])[0] || '', startedBy: authorName,
        }, token)
        if (!res || !res.ts) return
        existingTs = res.ts
        syncThreadTs(item.id, existingTs)
      }
      await replyToThread({ projectId: projectId, threadTs: existingTs, message: message, authorName: authorName, entityType: 'billing_line_item' }, token)
    } catch (e) { /* non-fatal — never block the billing action */ }
  }

  function getLoggedInMemberId() {
    try { var u = window.logged_in_user; return u ? (u.airtable_record_id || '') : '' } catch (e) { return '' }
  }

  // ── Event delegation (scoped to root) ───────────────────────────────────────────
  function onClick(e) {
    var t = e.target
    if (t.getAttribute && t.getAttribute('data-quiet-id')) { refreshQuietButton(); return }
    var action = null, el = t
    while (el && el !== root) { action = el.getAttribute('data-action'); if (action) break; el = el.parentElement }
    if (!action) return
    var idx = parseInt(el.getAttribute('data-idx') || '-1', 10)

    if (action === 'open-quiet-modal') { openQuietModal(); return }
    if (action === 'close-quiet-modal') { closeQuietModal(); return }
    if (action === 'confirm-quiet') { closeQuietProjects(); return }
    if (action === 'accept-preflight') { acceptPreflight(idx); return }
    if (action === 'retry-preflight') { loadPreflight(); return }

    if (action === 'nav') { state.monthOffset += parseInt(el.getAttribute('data-dir'),10); state.selectedIdx = -1; load() }
    if (action === 'retry-line-items') { if (state.run && state.run.id) fetchLineItems(state.run.id); else load() }
    if (action === 'preview-invoice') {
      var iid = el.getAttribute('data-invoice-id'); if (!iid) return
      var prevTxt = el.textContent; el.textContent = '…'; el.disabled = true
      // Core's own invoice PDF (the exact file behind the pay page's "View Invoice") — consistent
      // with what the client sees. Falls back to our renderer if the Core fetch fails.
      B.openCoreInvoicePdf(iid)
        .catch(function () { return B.openInvoicePdf(iid) })
        .then(function () { el.textContent = prevTxt; el.disabled = false })
        .catch(function () { el.textContent = 'failed'; setTimeout(function () { el.textContent = prevTxt; el.disabled = false }, 2000) })
      return
    }
    if (action === 'preview-statement') {
      // NOTE: named rootProjId, not root — `root` is the mountRun(root, opts) container element,
      // and a `var` here previously shadowed it for this entire onClick function (var is
      // function-scoped, not block-scoped), breaking every other `root.querySelectorAll(...)` call
      // in this handler (e.g. 'refresh-core-pct') with "Cannot read properties of undefined".
      var rootProjId = el.getAttribute('data-root'); if (!rootProjId) return
      var sPrev = el.textContent; el.textContent = '…'; el.disabled = true
      B.openStatementPdf(rootProjId).then(function () { el.textContent = sPrev; el.disabled = false })
        .catch(function (err) { el.textContent = 'failed'; try { alertDialog({ title: 'Statement', message: (err && err.message) || 'Could not build the statement.' }) } catch (e) {} ; setTimeout(function () { el.textContent = sPrev; el.disabled = false }, 2000) })
      return
    }
    if (action === 'invoice-data') {
      var did = el.getAttribute('data-invoice-id'); if (!did) return
      var dPrev = el.textContent; el.textContent = '…'; el.disabled = true
      B.openInvoiceData(did).then(function () { el.textContent = dPrev; el.disabled = false })
        .catch(function () { el.textContent = 'failed'; setTimeout(function () { el.textContent = dPrev; el.disabled = false }, 2000) })
      return
    }
    if (action === 'generate-statement-link') {
      var spProj = el.getAttribute('data-proj')
      if (!spProj) return
      if (!(state.run && state.run.id)) { try { alertDialog({ title: 'Statement link', message: 'No billing run is loaded yet — wait for the page to finish loading and try again.' }) } catch (e) {} ; return }
      var spPrev = el.textContent; el.textContent = 'Generating…'; el.disabled = true
      // authedPost() never rejects on a non-2xx response (it resolves with { error }) — a real failure
      // (auth hiccup, Stripe error, DB error, etc.) used to sail through this .then() unnoticed. Must
      // check resp.ok/resp.error explicitly instead of trusting a resolved promise.
      B.generateStatementPayLink(spProj, state.run.id).then(function (resp) {
        if (!resp || resp.error || !resp.ok) {
          el.textContent = 'failed'; el.disabled = false
          try { alertDialog({ title: 'Statement link not generated', message: 'The link was NOT generated — ' + ((resp && resp.error) || 'the server did not confirm it') + '. Please try again before moving on.' }) } catch (e) {}
          setTimeout(function () { el.textContent = spPrev }, 2000)
          return
        }
        if (state.openBillingInsight) { state.openBillingInsight.statementPayUrl = resp.statementPayUrl }
        var p = byId('aj-detail-panel'); if (p) p.innerHTML = renderDetail(state.selectedIdx)
      }).catch(function () {
        el.textContent = 'failed'; el.disabled = false
        try { alertDialog({ title: 'Statement link not generated', message: 'The link was NOT generated due to a network error. Please try again before moving on.' }) } catch (e) {}
        setTimeout(function () { el.textContent = spPrev }, 2000)
      })
      return
    }
    if (action === 'clear-statement-link') {
      var spProj2 = el.getAttribute('data-proj'); if (!spProj2) return
      B.clearStatementPayLink(spProj2, state.run && state.run.id).then(function (resp) {
        if (resp && resp.error) { try { alertDialog({ title: 'Statement link', message: 'Could not clear the link: ' + resp.error }) } catch (e) {} ; return }
        if (state.openBillingInsight) { state.openBillingInsight.statementPayUrl = null }
        var p = byId('aj-detail-panel'); if (p) p.innerHTML = renderDetail(state.selectedIdx)
      })
      return
    }
    if (action === 'open-email-modal') {
      var oIdx = parseInt(el.getAttribute('data-idx'), 10)
      var oMode = el.getAttribute('data-mode')
      var oInv = el.getAttribute('data-inv'), oNum = el.getAttribute('data-num')
      // Opens immediately and re-pulls inside itself. The id on this button was resolved when the
      // panel rendered, possibly long before an invoice was raised in Core, so it is still re-resolved
      // before anything is composed — just not while the user stares at an unresponsive button.
      openEmailPreviewModal(oIdx, oInv, oNum, oMode, true, false, true)
      return
    }
    if (action === 'close-email-modal') {
      var em = byId('aj-email-modal'); if (em) em.parentNode.removeChild(em); document.body.style.overflow = ''
      return
    }
    if (action === 'open-contacts') {
      try { window.dispatchEvent(new CustomEvent('open-contacts-panel')) } catch (e) {}
      return
    }
    if (action === 'pick-contact') {
      var pcEmail = el.getAttribute('data-email')
      if (pcEmail) { addRecip(activeRecipField || 'to', pcEmail); rerenderRecip(activeRecipField || 'to') }
      var rd2 = byId('aj-contact-results'); if (rd2) { rd2.style.display = 'none'; rd2.innerHTML = '' }
      var cs2 = byId('aj-contact-search'); if (cs2) cs2.value = ''
      return
    }
    if (action === 'remove-recip') {
      var rrField = el.getAttribute('data-field'), rrEmail = (el.getAttribute('data-email') || '').toLowerCase()
      if (emailRecips[rrField]) emailRecips[rrField] = emailRecips[rrField].filter(function (e) { return e.toLowerCase() !== rrEmail })
      rerenderRecip(rrField)
      return
    }
    if (action === 'refresh-invoice-list') {
      var fiIdx = state.selectedIdx
      try { el.textContent = '↻ Refreshing…' } catch (e) {}
      ;(async function () {
        // Both caches — dropping the client copy alone leaves the server answering from its own
        // 5-minute one, which is a refresh that visibly runs and changes nothing.
        await forceCoreRepull(fiIdx)   // re-renders the detail panel on completion
      })()
      return
    }
    if (action === 'reload-email-modal') {
      var rIdx = parseInt(el.getAttribute('data-idx'), 10)
      var rInv = el.getAttribute('data-inv'), rNum = el.getAttribute('data-num'), rMode = el.getAttribute('data-mode')
      var rBody = byId('aj-email-body'); if (rBody) rBody.innerHTML = '<div style="font-size:12px;color:var(--t3)">Reloading from Core…</div>'
      // Refresh both caches: client run list (resetCoreCaches + re-fetch) AND the server firm cache
      // (fresh:true on the re-opened preview) — the latter is what holds the email's invoice list.
      // Re-resolve, do not reuse rInv: refreshing the data and then handing back the SAME invoice id
      // was the bug that made this link look like it worked while changing nothing that mattered.
      openEmailPreviewModal(rIdx, rInv, rNum, rMode, true, false, true)
      return
    }
    if (action === 'mark-issued-manual') {
      // Reuses markIssued() (added last round for the email-send path) so this — currently the more
      // commonly used path, since email sending isn't switched on yet — also captures Invoice Number
      // instead of leaving it permanently blank for every project marked issued this way.
      markIssued(parseInt(el.getAttribute('data-idx'), 10), el.getAttribute('data-num') || '')
      return
    }
    if (action === 'send-test-email') {
      var teIdx = parseInt(el.getAttribute('data-idx'), 10)
      var teInv = el.getAttribute('data-inv'), teNum = el.getAttribute('data-num'), teMode = el.getAttribute('data-mode')
      var teIsReminder = el.getAttribute('data-reminder') === 'true'
      var teInp = byId('aj-test-email'); var teTo = teInp ? (teInp.value || '').trim() : ''
      var teSt = byId('aj-email-status')
      if (!teTo) { if (teSt) teSt.textContent = 'Enter a test address.'; return }
      ;(async function () {
        var item = state.lineItems[teIdx]; var f = item.fields; var projId = (f['Project'] || [])[0]
        var sel = state.openBillingInsight && state.openBillingInsight.idx === teIdx ? state.openBillingInsight : null
        var payUrl = teMode !== 'invoice' ? ((sel && sel.statementPayUrl) || '') : ''
        setEmailBusy(true, 'Sending test…')   // lock the modal during the test send too
        // to override + send:true + test:true → goes only to the tester (no team Bcc), and NO mark
        // issued/posted/reminded. Just a deliverability/rendering check.
        var teSal = (byId('aj-email-salutation') || {}).value
        var teCm = (byId('aj-email-custom-msg') || {}).value
        var resp = await B.sendInvoiceEmail({ projectId: projId, invoiceId: teInv, mode: teMode, invoiceNumber: teNum, payUrl: payUrl, isReminder: teIsReminder, to: teTo, salutation: teSal, customMessage: teCm, send: true, test: true })
        setEmailBusy(false)
        if (resp && resp.sent) { if (teSt) teSt.textContent = 'Test sent to ' + (resp.to || teTo) }
        else if (resp && resp.skipped) { if (teSt) teSt.textContent = ''; try { alertDialog({ title: 'Not enabled yet', message: 'Email sending isn’t switched on yet. After the domain verifies in Resend, set RESEND_API_KEY and BILLING_EMAIL_SEND_ENABLED=true in Vercel, redeploy, then try the test again.' }) } catch (e) {} }
        else { if (teSt) teSt.textContent = ''; try { alertDialog({ title: 'Test failed', message: (resp && resp.error) || 'Could not send the test email.' }) } catch (e) {} }
      })()
      return
    }
    if (action === 'confirm-send-email') {
      var ceIdx = parseInt(el.getAttribute('data-idx'), 10)
      var ceInv = el.getAttribute('data-inv'), ceNum = el.getAttribute('data-num'), ceMode = el.getAttribute('data-mode')
      var ceIsReminder = el.getAttribute('data-reminder') === 'true'
      commitAllRecipInputs()   // fold any half-typed address into chips first
      var ceTo = (emailRecips.to || []).slice(), ceCc = (emailRecips.cc || []).slice(), ceBcc = (emailRecips.bcc || []).slice()
      var ceSt0 = byId('aj-email-status')
      if (!ceTo.length) { if (ceSt0) ceSt0.textContent = 'Add a recipient in “To”.'; return }
      ;(async function () {
        var item = state.lineItems[ceIdx]; var f = item.fields; var projId = (f['Project'] || [])[0]
        var sel = state.openBillingInsight && state.openBillingInsight.idx === ceIdx ? state.openBillingInsight : null
        var payUrl = ceMode !== 'invoice' ? ((sel && sel.statementPayUrl) || '') : ''
        var ok = ceIsReminder
          ? await confirmDialog({ title: 'Send reminder?', message: 'Email this follow-up reminder to ' + ceTo.join(', ') + ' with the PDF attached?', confirmLabel: 'Send reminder' })
          : await confirmDialog({ title: 'Send to client?', message: 'Email the ' + (ceMode === 'invoice' ? 'invoice' : 'statement') + ' to ' + ceTo.join(', ') + ' with the PDF attached, then mark it issued and posted?', confirmLabel: 'Send & mark posted' })
        if (!ok) return
        setEmailBusy(true, 'Sending…')   // lock the whole modal until the request resolves
        var ceSal = (byId('aj-email-salutation') || {}).value
        var ceCm = (byId('aj-email-custom-msg') || {}).value
        var resp = await B.sendInvoiceEmail({ projectId: projId, invoiceId: ceInv, mode: ceMode, invoiceNumber: ceNum, payUrl: payUrl, isReminder: ceIsReminder, to: ceTo, cc: ceCc, bcc: ceBcc, salutation: ceSal, customMessage: ceCm, send: true })
        if (resp && resp.sent) {
          var m = byId('aj-email-modal'); if (m) m.parentNode.removeChild(m); document.body.style.overflow = ''
          if (ceIsReminder) {
            // Write the same legacy fields the bookkeeper already hand-maintains for this cycle's
            // reminder, alongside the billing_reminders Supabase row api/billing-send-invoice.js
            // already logged on this successful send.
            var todayISO = new Date().toISOString().split('T')[0]
            var rUpdated = await B.updateLineItem(item.id, { 'Reminder Sent': true, 'Reminder Sent Date': todayISO })
            if (rUpdated && !rUpdated.error) mergeUpdated(ceIdx, rUpdated)
            item.fields['Reminder Sent'] = true; item.fields['Reminder Sent Date'] = todayISO
            var rp = byId('aj-detail-panel'); if (rp) rp.innerHTML = renderDetail(ceIdx); updateListRow(ceIdx)
            try { alertDialog({ title: 'Reminder sent', message: 'Reminder emailed to ' + (resp.to || 'the client') + '.' }) } catch (e) {}
            return
          }
          // The server's invoiceNumbers is the authoritative list this send actually covered (every
          // open invoice attached to a statement, not just the one named in the compose call) —
          // prefer it over the pre-send ceNum guess; fall back only if an older response shape ever
          // comes back.
          var markNums = (resp.invoiceNumbers && resp.invoiceNumbers.length) ? resp.invoiceNumbers : [ceNum]
          var mark = await markIssued(ceIdx, markNums)
          try {
            if (mark && mark.ok) alertDialog({ title: 'Sent', message: 'Email sent to ' + (resp.to || 'the client') + ' and marked issued. Last step: post the invoice in BQE Core, then mark it posted here.' })
            else alertDialog({ title: 'Sent — but not recorded', message: 'The email went out to ' + (resp.to || 'the client') + ', but updating the line item failed (' + (mark && mark.error) + '). Set it issued manually, then post in Core.' })
          } catch (e) {}
        } else if (resp && resp.skipped) {
          setEmailBusy(false, '')
          try { alertDialog({ title: 'Not enabled yet', message: ceIsReminder ? 'Email sending isn’t switched on yet. Once we verify the sending domain and enable it, this will send the reminder.' : 'Email sending isn’t switched on yet. The draft looks ready — once we verify the sending domain and enable it, this will send the email and mark the invoice posted. For now, use “Mark issued without sending.”' }) } catch (e) {}
        } else {
          setEmailBusy(false, '')
          try { alertDialog({ title: 'Send failed', message: (resp && resp.error) || 'Could not send the email. Try again.' }) } catch (e) {}
        }
      })()
      return
    }
    if (action === 'send-back-draft') {
      var sbIdx = parseInt(el.getAttribute('data-idx'), 10)
      ;(async function () {
        var note = await promptDialog({ title: 'Send back for corrections', message: 'What needs fixing? (optional note for the bookkeeper)', placeholder: 'e.g. wrong write-down on the CD phase', multiline: true })
        if (note === null) return   // cancelled
        showSaving(true)
        var item = state.lineItems[sbIdx]
        // Reset to "Prepare draft": clear approval + finalize + the prepared flag so the bookkeeper
        // re-does it in Core (delete draft, re-batch, regenerate). Cortex can't touch Core drafts.
        var fields = { 'Draft Review Disposition': 'Revise', 'Create Draft': null, 'Review Draft Preview': null, 'Draft Review Comments': note || '' }
        var updated = await B.updateLineItem(item.id, fields)
        showSaving(false)
        if (updated && !updated.error) {
          mergeUpdated(sbIdx, updated)
          item.fields['Draft Review Disposition'] = 'Revise'; item.fields['Create Draft'] = null; item.fields['Review Draft Preview'] = null; item.fields['Draft Review Comments'] = note || ''
          var p = byId('aj-detail-panel'); if (p) p.innerHTML = renderDetail(sbIdx); updateListRow(sbIdx)
          postBillingSlackUpdate(item, '🔁 Revisions requested for the *' + billingProjectLabel(item.fields) + '* invoice draft.' + (note ? '\nRemarks: ' + note : ''), true)
          // No longer Approved — drop it out of the header's approved-total.
          loadApprovedNewBillingTotal()
        }
      })()
      return
    }
    if (action === 'mark-draft-ready') {
      var mrIdx = parseInt(el.getAttribute('data-idx'), 10)
      var mrItem = state.lineItems[mrIdx]
      // Mark the draft ready for review. If this is a re-prepare after a send-back, reset the review
      // state to pending (clear the Revise disposition + the correction note) so the manager sees a
      // fresh review, not a stale "sent back".
      var mrFields = { 'Create Draft': true }
      var wasRevise = mrItem.fields['Draft Review Disposition'] === 'Revise'
      // Clear with null, not '' — Draft Review Disposition is a single-select and Airtable rejects an
      // empty-string value, which would fail the whole PATCH (so Create Draft never set → button "dead").
      if (wasRevise) { mrFields['Draft Review Disposition'] = null; mrFields['Draft Review Comments'] = null }
      ;(async function () {
        showSaving(true)
        var updated = await B.updateLineItem(mrItem.id, mrFields)
        showSaving(false)
        if (updated && !updated.error) {
          mergeUpdated(mrIdx, updated)
          mrItem.fields['Create Draft'] = true
          if (wasRevise) { mrItem.fields['Draft Review Disposition'] = null; mrItem.fields['Draft Review Comments'] = null }
          var p = byId('aj-detail-panel'); if (p) p.innerHTML = renderDetail(mrIdx); updateListRow(mrIdx)
        }
      })()
      return
    }
    if (action === 'open-core-invoice') {
      // Core's invoice search is an in-page filter (not URL-drivable), so copy the number to the
      // clipboard and open Invoice Review — then it's just paste + Enter in Core's search.
      var ocNum = el.getAttribute('data-num') || ''
      var ocUrl = el.getAttribute('data-url') || 'https://wap1-01.bqecore.com/webapp/InvoiceReview/InvoiceReview'
      try { if (ocNum && navigator.clipboard) navigator.clipboard.writeText(ocNum) } catch (e) {}
      window.open(ocUrl, '_blank', 'noopener')
      return
    }
    if (action === 'open-billing-slack') {
      // Reuses the app's existing global Slack panel host (GlobalSlackPanel.jsx, already mounted
      // once in OutlineView regardless of which workspace is active) — the same mechanism every
      // other entity's chat-bubble (Milestones/Sheets/BIM) already opens through.
      var slIdx = parseInt(el.getAttribute('data-idx'), 10)
      var slItem = state.lineItems[slIdx]
      if (!slItem) return
      var slF = slItem.fields
      var slProjectId = (slF['Project'] || [])[0]
      if (!slProjectId) return
      window.dispatchEvent(new CustomEvent('open-slack-panel', { detail: {
        entityId: slItem.id,
        entityType: 'billing_line_item',
        entityName: billingProjectLabel(slF),
        projectId: slProjectId,
        projectName: (slF['Project Name (from Project)'] || [''])[0] || '',
        threadTs: slF['Slack Thread Timestamp'] || '',
      } }))
      return
    }
    if (action === 'open-renumber-checklist') { openRenumberChecklist(); return }
    if (action === 'close-renumber') { var rm = byId('aj-renumber-modal'); if (rm) rm.parentNode.removeChild(rm); document.body.style.overflow = ''; return }
    if (action === 'resolve-renumber') {
      var rp = el.getAttribute('data-proj'); if (!rp) return
      el.textContent = '…'; el.disabled = true
      B.resolveRenumberFollowup(rp).then(function () {
        state.renumberFollowups = (state.renumberFollowups || []).filter(function (x) { return x.project_airtable_id !== rp })
        renderRenumberBody()
        var host = byId('aj-renumber-host'); if (host) host.innerHTML = renumberButtonHtml()
      }).catch(function () { el.textContent = 'Mark recalibrated'; el.disabled = false })
      return
    }
    if (action === 'select-li') selectLI(parseInt(el.getAttribute('data-idx'),10))
    if (action === 'save-check-field') {
      var val = el.getAttribute('data-val')
      var fld = el.getAttribute('data-field')
      // Gate: can't confirm an invoice finalized in Core while its open invoices are out of sequence —
      // fix the number in Core (and Refresh) first, so we never finalize a mis-numbered run.
      //
      // This is also THE moment the bookkeeper confirms the invoice just got its real number in Core
      // (the button reads "Confirm finalized in Core" — see issueStepHtml's neighbor, step8Input).
      // state.openBillingInsight.invoices was populated once, back when this project row was first
      // selected, possibly long before the draft existed — so it must be refreshed HERE, before the
      // out-of-sequence check runs, or the check (and every later step: Issue, Mark Posted) would
      // silently keep evaluating against a stale/previous-cycle invoice list.
      if (fld === 'Review Draft Preview' && val === 'true') {
        ;(async function () {
          showSaving(true)
          await forceCoreRepull(idx)
          showSaving(false)
          if (selectedInvoicesOutOfSeq()) {
            // Auto-flag this project for Core renumber recalibration (the number gets fixed in Core).
            try {
              var gf = state.lineItems[idx].fields
              var gsel = state.openBillingInsight && state.openBillingInsight.idx === idx ? state.openBillingInsight : null
              B.flagRenumberFollowup({
                projectId: (gf['Project'] || [])[0],
                label: (((gf['Project Number'] || [''])[0]) + ' ' + ((gf['Project Name (from Project)'] || [''])[0])).trim(),
                coreUid: (gsel && gsel.rootCoreUid) || '',
                coreUrl: (gf['Open in Core (from Project)'] || [''])[0] || ''
              }).then(loadRenumberFollowups).catch(function () {})
            } catch (e) {}
            try { alertDialog({ title: 'Invoice numbering is out of sequence', message: 'The open invoices for this project aren’t in sequence (see the flagged rows above). Fix the invoice number in BQE Core, click Refresh to confirm it came across, then confirm it finalized. (Flagged for Core renumber re-calibration.)' }) } catch (e) {}
            return
          }
          saveField(idx, fld, true)
        })()
        return
      }
      // Gate: "I've posted it in Core" — verify with Core that the invoice is actually posted first.
      if (fld === 'Mark Posted' && val === 'true') {
        var mpSel = state.openBillingInsight && state.openBillingInsight.idx === idx ? state.openBillingInsight : null
        var mpInvs = (mpSel && mpSel.invoices) ? mpSel.invoices.filter(function (iv) { return !iv.draft && iv.invoiceNumber }) : []
        mpInvs = mpInvs.slice().sort(function (a, b) { return (new Date(b.date)) - (new Date(a.date)) })
        var mpInv = mpInvs[0]
        ;(async function () {
          if (!mpInv || !mpInv.id) {
            var ok0 = await confirmDialog({ title: 'Couldn’t identify the invoice', message: 'Cortex couldn’t find the finalized invoice to verify against Core (try Refresh). Mark it posted anyway?', confirmLabel: 'Mark posted' })
            if (ok0) saveField(idx, fld, true)
            return
          }
          showSaving(true)
          var st = null
          try { st = await B.getCoreInvoiceStatus(mpInv.id) } catch (e) { st = { error: (e && e.message) || 'check failed' } }
          showSaving(false)
          if (st && st.posted) { saveField(idx, fld, true); return }
          if (!st || st.error) {
            var ok1 = await confirmDialog({ title: 'Couldn’t verify with Core', message: 'Core didn’t confirm the status' + (st && st.error ? ' (' + st.error + ')' : '') + '. Mark it posted anyway?', confirmLabel: 'Mark posted' })
            if (ok1) saveField(idx, fld, true)
            return
          }
          try { alertDialog({ title: 'Not posted in Core yet', message: 'Core still shows invoice #' + (st.invoiceNumber || mpInv.invoiceNumber || '') + ' as ' + (st.isDraft ? 'a draft' : 'un-posted') + '. Post it in BQE Core, then click "I\'ve posted it in Core" again.' }) } catch (e) {}
        })()
        return
      }
      // Gate: choosing the answer the billing signals do NOT recommend — confirm it's deliberate,
      // with the reasoning spelled out, rather than let a stray click silently override the finding.
      if (fld === 'New Billing?' && (val === 'Yes' || val === 'No')) {
        var nbg = state.newBillingInsight && state.newBillingInsight.idx === idx ? state.newBillingInsight : null
        if (nbg && typeof nbg.recommendYes === 'boolean' && (nbg.recommendYes ? 'Yes' : 'No') !== val) {
          ;(async function () {
            var msg
            if (nbg.recommendYes) {
              var why = (nbg.hasAdvanced && nbg.hasHours)
                ? 'at least one phase has advanced past what was last billed, and there are billable hours logged this cycle'
                : nbg.hasAdvanced
                  ? 'at least one phase has advanced past what was last billed this cycle (no new billable hours logged)'
                  : 'there are billable hours logged this cycle (no phase has advanced past what was last billed)'
              msg = 'The billing signals show ' + why + ' — that recommends "Yes, invoice this cycle." Mark no new billing anyway?'
            } else {
              msg = 'The billing signals show no billable hours and no phase advancement this cycle — that recommends "No new billing." Invoice this cycle anyway?'
            }
            var ok = await confirmDialog({ title: 'This isn\'t the recommended answer', message: msg, confirmLabel: val === 'Yes' ? 'Yes, invoice this cycle' : 'No new billing' })
            if (ok) saveField(idx, fld, val)
          })()
          return
        }
      }
      if (fld === 'Open Billing?' && (val === 'Yes' || val === 'No')) {
        var obg = state.openBillingInsight && state.openBillingInsight.idx === idx ? state.openBillingInsight.status : null
        if (obg && !obg.unknown) {
          var obgRecommendedVal = obg.openCount > 0 ? 'Yes' : 'No'
          if (obgRecommendedVal !== val) {
            ;(async function () {
              var msg = obg.openCount > 0
                ? 'Core shows an open invoice balance for this project — that recommends "Yes, open invoices." Mark no open invoices anyway?'
                : 'Core shows no open invoices for this project — that recommends "No open invoices." Mark open billing anyway?'
              var ok = await confirmDialog({ title: 'This isn\'t the recommended answer', message: msg, confirmLabel: val === 'Yes' ? 'Yes, open invoices' : 'No open invoices' })
              if (ok) saveField(idx, fld, val)
            })()
            return
          }
        }
      }
      if (fld === 'Draft Review Disposition' && val === 'Approved') {
        var apItem = state.lineItems[idx]
        ;(async function () {
          await saveField(idx, fld, val)
          postBillingSlackUpdate(apItem, '✅ Invoice draft approved for *' + billingProjectLabel(apItem.fields) + '*.')
        })()
        return
      }
      saveField(idx, fld, val === 'true' ? true : val)
    }
    if (action === 'mark-payment-checked-unpaid') {
      // The "unknown" payment-check state means Core couldn't confirm status automatically — a
      // human's own "still unpaid" call substitutes for Core's, in the unpaid direction (the
      // "Confirm — paid" button covers the paid direction via mark-payment-checked-paid below).
      saveFields(idx, { 'Payment Status Checked': true, 'Payment Manually Marked Unpaid': true })
    }
    if (action === 'mark-payment-checked-paid') {
      // The paid-direction twin of the above — a human's own "paid" call substitutes for Core's,
      // resolving to the SAME real 'paid' state a Core-confirmed payment reaches (not just
      // "checked"), so it shows as Paid in the list and doesn't block closeout.
      saveFields(idx, { 'Payment Status Checked': true, 'Payment Manually Marked Paid': true })
    }
    if (action === 'skip-followup-reminder') {
      (async function () {
        var skipItem = state.lineItems[idx]
        var skipProj = skipItem ? (skipItem.fields['Project Number'] || [''])[0] : ''
        var ok = await confirmDialog({
          title: 'Skip this reminder?',
          message: 'This resolves the payment follow-up for ' + skipProj + ' without actually sending a reminder email, so it won’t block closing out this run. It still shows up in the Readiness "Billing holds" panel for ongoing collection.',
          confirmLabel: 'Skip reminder',
        })
        if (!ok) return
        saveFields(idx, { 'Reminder Skipped': true })
      })()
    }
    if (action === 'mark-final-check-unpaid') {
      // The second, later checkpoint's "still unpaid" outcome — the paid direction reuses
      // mark-payment-checked-paid above (it already resolves to 'paid' unconditionally, ahead of
      // this stage, so no separate action is needed for that side). This is genuinely terminal:
      // the run can close, and Billing Holds keeps tracking the open balance from here on.
      saveFields(idx, { 'Final Payment Confirmed': true })
    }
    if (action === 'pm-confirm') pmConfirm(idx)
    if (action === 'open-phase-modal') openPhaseModal(idx)
    if (action === 'close-phase-modal') { var pm = byId('aj-phase-modal'); if (pm) pm.parentNode.removeChild(pm); document.body.style.overflow = '' }
    if (action === 'sync-billed-fee') {
      var phaseRecId = el.getAttribute('data-phase-record-id'), coreBilledVal = parseFloat(el.getAttribute('data-core-billed') || '0')
      if (!phaseRecId) return
      el.textContent = '...'; el.disabled = true
      B.updatePhase(phaseRecId, { 'Billed Fee': coreBilledVal }).then(function(updated) {
        if (updated && !updated.error) {
          var billedCell2 = el.parentNode
          billedCell2.innerHTML = '<span style="pointer-events:none">$' + coreBilledVal.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}) + '</span>'
          billedCell2.style.color = 'var(--grn)'; billedCell2.style.fontWeight = '400'; billedCell2.setAttribute('data-at-billed', coreBilledVal)
        } else { el.textContent = 'err' }
      }).catch(function() { el.textContent = 'err' })
      return
    }
    if (action === 'refresh-core-pct') {
      // Re-pull ONLY this project's phase percentages — direct per-phase reads from Core. Leaves the
      // Billed column and the firm-wide invoice cache untouched (no full data refresh). Also writes
      // the fresh % back into the shared phase cache so reopening the modal stays consistent.
      var lbl = el.querySelector('span'); var prev = lbl ? lbl.textContent : 'Refresh Core'
      el.disabled = true; if (lbl) lbl.textContent = 'Refreshing…'
      var rcells = root.querySelectorAll('[id^="core-pct-"]')
      var targets = []
      for (var ri = 0; ri < rcells.length; ri++) {
        if (rcells[ri].getAttribute('data-hourly') === '1') continue
        if (!rcells[ri].getAttribute('data-core-id')) continue
        targets.push(rcells[ri])
      }
      if (!targets.length) { el.disabled = false; if (lbl) lbl.textContent = prev; return }
      B.getCorePhasesCached().then(function(phaseMap) {
        var remaining = targets.length
        var done = function() { remaining--; if (remaining <= 0) { el.disabled = false; if (lbl) lbl.textContent = prev } }
        targets.forEach(function(cell) {
          var coreId = cell.getAttribute('data-core-id')
          cell.textContent = '...'; cell.style.color = 'var(--t4)'
          B.getCorePhase(coreId).then(function(phase) {
            if (phase && !phase.error && phase.percentComplete !== undefined && phase.percentComplete !== null) {
              if (phaseMap) phaseMap[coreId] = phase.percentComplete   // keep the cache fresh for reopen
              paintCoreCell(cell, phase.percentComplete)
            } else { cell.textContent = 'err'; cell.style.color = 'var(--red)' }
            checkCoreMatch()
          }).catch(function() { cell.textContent = '-'; cell.style.color = 'var(--t4)' })
            .then(done)
        })
      })
      return
    }
    if (action === 'show-te-help') showTeHelpPopover()
    if (action === 'close-te-help') { var th = byId('aj-te-help'); if (th) th.parentNode.removeChild(th) }
    if (action === 'cancel-pct-warn') { var pw = byId('aj-pct-warn'); if (pw) pw.parentNode.removeChild(pw) }
    if (action === 'confirm-pct-reduce') {
      var pw2 = byId('aj-pct-warn'); if (pw2) pw2.parentNode.removeChild(pw2)
      savePhasePercent(parseInt(el.getAttribute('data-idx'), 10), el.getAttribute('data-phase-id'), parseFloat(el.getAttribute('data-val')))
    }
    if (action === 'save-phase-pct') {
      var phaseId = el.getAttribute('data-phase-id')
      var inputEl = byId('pct-input-' + phaseId), currentEl = byId('pct-display-' + phaseId)
      if (inputEl && currentEl) {
        var pctVal = parseFloat(inputEl.value), currentPct = parseFloat(currentEl.textContent)
        if (!isNaN(pctVal)) {
          if (pctVal < currentPct) showPctWarning(idx, phaseId, pctVal, currentPct)
          else savePhasePercent(idx, phaseId, pctVal / 100)
        }
      }
    }
    if (action === 'confirm-phases-done') {
      saveField(idx, 'CORE Project Progress Current?', 'Yes')
      var pm2 = byId('aj-phase-modal'); if (pm2) pm2.parentNode.removeChild(pm2); document.body.style.overflow = ''
    }
    if (action === 'step-back') stepBack(parseInt(el.getAttribute('data-idx'), 10), el.getAttribute('data-field'))
    if (action === 'open-invoice-reviewer') {
      var pnum2 = el.getAttribute('data-projnum') || ''
      if (pnum2 && navigator.clipboard) navigator.clipboard.writeText(pnum2).then(function() {
        var conf2 = byId('aj-inv-copy-confirm'); if (conf2) { conf2.textContent = pnum2 + ' copied'; conf2.style.opacity = '1'; setTimeout(function() { conf2.style.opacity = '0' }, 2000) }
      }).catch(function() {})
      return
    }
    if (action === 'open-te-reviewer') {
      var pnum = el.getAttribute('data-projnum') || ''
      if (pnum && navigator.clipboard) navigator.clipboard.writeText(pnum).then(function() {
        var conf = byId('aj-copy-confirm'); if (conf) { conf.textContent = pnum + ' copied'; conf.style.opacity = '1'; setTimeout(function() { conf.style.opacity = '0' }, 2000) }
      }).catch(function() {})
      return
    }
    if (action === 'confirm-batch-run') confirmBatchRun()
    if (action === 'delete-li') { e.stopPropagation(); showDeleteLiModal(parseInt(el.getAttribute('data-idx'), 10)); return }
    if (action === 'close-delete-li-modal') { var dlm = byId('aj-delete-li-modal'); if (dlm) dlm.parentNode.removeChild(dlm); return }
    if (action === 'confirm-delete-li') { executeDeleteLi(parseInt(el.getAttribute('data-idx'), 10)); return }
    if (action === 'toggle-filter') { var fkey = el.getAttribute('data-filter'); state.hiddenFilters[fkey] = !state.hiddenFilters[fkey]; render(); return }
    if (action === 'search-clear') { state.searchQuery = ''; render(); var inp = byId('aj-br-search'); if (inp) inp.focus(); return }
    if (action === 'skip-no-activity') skipNoActivity(idx)
    if (action === 'skip-this-month') skipThisMonth(idx)
    if (action === 'go-readiness') { window.dispatchEvent(new CustomEvent('open-billing', { detail: { tool: 'readiness' } })); return }
    if (action === 'toggle-precheck') { toggleClosePrecheck(el.getAttribute('data-field'), el.checked); return }
    if (action === 'close-out-run') closeOutRun()
    if (action === 'reopen-run') reopenRun()
    if (action === 'open-followup-reminder-modal') showFollowupReminderModal(parseInt(el.getAttribute('data-idx'), 10))
  }

  async function toggleClosePrecheck(field, val) {
    var fields = {}; fields[field] = !!val
    var updated = await B.updateRun(state.run.id, fields)
    if (updated && !updated.error) { state.run.fields[field] = updated.fields[field]; render() }
  }

  async function closeOutRun() {
    // Re-check here too, not just in the button's disabled state — a stale render must not bypass it.
    if (!state.followupStatusLoaded || state.lineItems.some(needsFollowup)) {
      try { alertDialog({ title: 'Not ready to close out', message: 'Resolve the payment follow-ups above first, then close out this run.' }) } catch (e) {}
      return
    }
    var savingEl = byId('aj-closeout-saving')
    if (savingEl) savingEl.textContent = 'Saving...'
    var updated = await B.updateRun(state.run.id, { Status: 'Complete' })
    if (updated && !updated.error) { state.run.fields['Status'] = updated.fields['Status']; render() }
    else if (savingEl) savingEl.textContent = 'Error - try again'
  }

  async function reopenRun() {
    var updated = await B.updateRun(state.run.id, { Status: 'In Progress' })
    if (updated && !updated.error) { state.run.fields['Status'] = updated.fields['Status']; render() }
  }

  async function pmConfirm(idx) {
    var memberId = getLoggedInMemberId()
    var fields = { 'PM Confirm Submit': true }
    if (memberId) fields['PM Setup Completed By'] = [memberId]
    showSaving(true)
    var item = state.lineItems[idx]
    var updated = await B.updateLineItem(item.id, fields)
    if (updated && !updated.error) {
      state.lineItems[idx] = updated
      var refreshed = await B.getRunById(state.run.id)
      showSaving(false)
      if (refreshed) state.run = refreshed
      var panel = byId('aj-detail-panel'); if (panel) panel.innerHTML = renderDetail(idx)
      updateListRow(idx); render()
    } else { showSaving(false) }
  }

  async function confirmBatchRun() {
    var savingEl = byId('aj-batch-saving')
    if (savingEl) savingEl.textContent = 'Saving...'
    var updated = await B.updateRun(state.run.id, { 'Batch Run Complete': true })
    if (updated && !updated.error) {
      state.run.fields['Batch Run Complete'] = updated.fields['Batch Run Complete']
      state.selectedIdx = -1; render()
    } else { if (savingEl) savingEl.textContent = 'Error - try again' }
  }

  async function skipThisMonth(idx) {
    var item = state.lineItems[idx]
    if (!item) return
    var num = (item.fields['Project Number'] || [''])[0] || 'this project'
    if (!(await confirmDialog({ title: 'Skip ' + num + ' this month?', message: 'Nothing is billed on it in this run, and it counts as done for the batch.', confirmLabel: 'Skip this month' }))) return
    var fields = {
      'CORE Project Progress Current?': 'Yes',
      'Time and Expense Double Checked?': 'Yes',
      'New Billing?': 'No',
      'Open Billing?': 'No',
      'Skip Invoicing?': 'Yes',
      'PM Confirm Submit': true,
    }
    var mid = getLoggedInMemberId()
    if (mid) fields['PM Setup Completed By'] = [mid]
    showSaving(true)
    var updated = null
    try { updated = await B.updateLineItem(item.id, fields) } catch (e) { updated = { error: (e && e.message) || 'update failed' } }
    showSaving(false)
    if (updated && !updated.error) {
      state.lineItems[idx] = updated
      try { var refreshed = await B.getRunById(state.run.id); if (refreshed) state.run = refreshed } catch (e) {}
      var panel = byId('aj-detail-panel'); if (panel) panel.innerHTML = renderDetail(idx)
      updateListRow(idx); render()
    } else {
      try { alertDialog({ title: 'Could not skip ' + num, message: (updated && updated.error) || 'The save did not go through. Try again.' }) } catch (e) {}
    }
  }

  async function skipNoActivity(idx) {
    var fields2 = { 'Skip Invoicing?': 'Yes', 'PM Confirm Submit': true }
    var mid = getLoggedInMemberId()
    if (mid) fields2['PM Setup Completed By'] = [mid]
    showSaving(true)
    var item2 = state.lineItems[idx]
    var updated = await B.updateLineItem(item2.id, fields2)
    showSaving(false)
    if (updated && !updated.error) {
      state.lineItems[idx] = updated
      var panel = byId('aj-detail-panel'); if (panel) panel.innerHTML = renderDetail(idx)
      updateListRow(idx); render()
    }
  }

  function onInput(e) {
    var t = e.target
    if (t.getAttribute && t.getAttribute('data-action') === 'search-input') {
      state.searchQuery = t.value
      var panel = byId('aj-li-list')
      if (!panel) return
      var li = state.lineItems
      var filteredLi = filterLi()
      var h = ''
      if (filteredLi.length === 0) h = '<div class="aj-empty">No projects match.</div>'
      else filteredLi.forEach(function(item) { h += listRowHtml(item, li.indexOf(item)) })
      panel.innerHTML = h
      var clearBtn = root.querySelector('[data-action="search-clear"]')
      var searchWrap = t.parentNode
      if (state.searchQuery && !clearBtn) {
        var cb = document.createElement('button')
        cb.setAttribute('data-action', 'search-clear')
        cb.style.cssText = 'position:absolute;right:8px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:var(--t4);font-size:14px;line-height:1;padding:2px 4px'
        cb.innerHTML = '&#x2715;'
        searchWrap.appendChild(cb)
      } else if (!state.searchQuery && clearBtn) { clearBtn.parentNode.removeChild(clearBtn) }
    }
  }

  // ── Dot tooltip ─────────────────────────────────────────────────────────────────
  var tip = document.createElement('div')
  tip.id = 'aj-dot-tooltip'
  root.appendChild(tip)
  function onOver(e) {
    var el = e.target
    while (el && el !== root) {
      if (el.getAttribute && el.getAttribute('data-tip')) {
        tip.textContent = el.getAttribute('data-tip'); tip.style.display = 'block'
        var r = el.getBoundingClientRect()
        tip.style.left = (r.left + r.width / 2 - tip.offsetWidth / 2) + 'px'
        tip.style.top = (r.top - tip.offsetHeight - 6) + 'px'
        return
      }
      el = el.parentElement
    }
    tip.style.display = 'none'
  }
  function onOut(e) {
    var el = e.relatedTarget, found = false
    while (el && el !== root) { if (el.getAttribute && el.getAttribute('data-tip')) { found = true; break } el = el.parentElement }
    if (!found) tip.style.display = 'none'
  }

  // Bridge back from GlobalSlackPanel.jsx (React, mounted once in OutlineView) once a thread is
  // started via the chat-bubble in renderDetail's header — mirrors how SheetRow/MilestoneRow sync
  // a returned thread ts into their own state, just via a CustomEvent since runTool.js isn't React.
  function onSlackThreadStarted(e) {
    var d = e.detail || {}
    if (d.entityType !== 'billing_line_item' || !d.entityId || !d.ts) return
    syncThreadTs(d.entityId, d.ts)
  }

  root.addEventListener('click', onClick)
  root.addEventListener('input', onInput)
  root.addEventListener('mouseover', onOver)
  root.addEventListener('mouseout', onOut)
  window.addEventListener('slack-thread-started', onSlackThreadStarted)
  load()

  // Prefetch the firm's Core invoices AND phase %s ONCE now (Billing Run open) so every phase modal
  // reads Billed + Core % from memory instantly — no per-modal Core calls. A persistent top-right
  // pill shows progress, and a Refresh button re-pulls Core (it can change while the run is open).
  var coreLoad = document.createElement('div')
  coreLoad.className = 'arb-core-load'
  root.appendChild(coreLoad)
  function paintCorePill(state) {
    if (state === 'loading') {
      coreLoad.style.opacity = '1'
      coreLoad.innerHTML = '<span class="arb-spin"></span><span>Loading Core data…</span>'
    } else if (state === 'error') {
      coreLoad.style.opacity = '1'
      coreLoad.innerHTML = '<span style="color:var(--red)">Core data unavailable</span>'
        + '<button class="arb-core-refresh" title="Retry loading Core data">Retry</button>'
    } else {
      coreLoad.style.opacity = '1'
      coreLoad.innerHTML = '<span style="color:var(--grn);font-weight:600">✓</span><span>Core data loaded</span>'
        + '<button class="arb-core-refresh" title="Re-pull invoices + phase % from BQE Core">↻</button>'
    }
    var rb = coreLoad.querySelector('.arb-core-refresh')
    if (rb) rb.onclick = function () { loadCoreData(true) }
  }
  function loadCoreData(isRefresh) {
    if (isRefresh) B.resetCoreCaches()
    paintCorePill('loading')
    Promise.all([
      B.prefetchCoreInvoices(isRefresh).catch(function () { return null }),
      B.prefetchCorePhases(isRefresh).catch(function () { return null }),
    ]).then(function (r) {
      if (!B.coreInvoicesReady() && !B.corePhasesReady()) { paintCorePill('error'); return }
      paintCorePill('loaded')
      // If a phase modal is open, repaint its Billed + Core % from the fresh caches.
      if (byId('aj-phase-modal')) { try { fetchCorePercents() } catch (e) {} }
      // Also re-pull the selected project's own invoice list (state.openBillingInsight) — the firm-wide
      // prefetch above doesn't touch it, so without this the "Open invoices in Core" list on the
      // Confirm-payment step stayed stale after clicking this pill's own refresh.
      if (isRefresh && state.selectedIdx >= 0) { try { fetchNewBillingInsight(state.selectedIdx) } catch (e) {} }
      // The pre-flight answers read the same invoices — work them out again from the fresh ones.
      if (isRefresh && state.run) { try { loadPreflight() } catch (e) {} }
    })
  }
  loadCoreData(false)

  return function teardown() {
    root.removeEventListener('click', onClick)
    root.removeEventListener('input', onInput)
    root.removeEventListener('mouseover', onOver)
    root.removeEventListener('mouseout', onOut)
    window.removeEventListener('slack-thread-started', onSlackThreadStarted)
    document.body.style.overflow = ''
  }
}
